/* eslint-disable eqeqeq */
/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Phase 6 Vendor-Port W5 — COIL SELECTOR (§33.2).
 *
 * React port of components/coil/view/coil.selector.js (4,268) + its templates
 * (coil.selector + coil.rated.outputs [3,065 — the LARGEST output sheet] +
 * coil.selector.draw_popup [806] + thickness_by_tube_dia + coil.selector.print)
 * into the 6e2c component-selector chassis (§30) as the 'coil' tab body.
 * REUSES the mature W1-W4 infra heavily: useRatingDispatch, buildRatingEnvelope
 * (coil derives manufacturer from the mfg name; mfg_id 21 → hard error),
 * useDependencyGraph (mfg/type/duty cascades), getFromCmsData (via coilCms).
 * NOTE the rated-output area is NOT the shared ratingKit table: coil's legacy
 * sheet is a three-tab Coil / Performance / Notes layout whose Coil tab is the
 * `.coil-summary-table` candidate list, so it is rendered directly here.
 *
 * Opened via the COIL FORM's rating label: CoilForm's FormHeading
 * (withRatingBlock, ratingBlockDataName='coil') → openRatingStub →
 * rating_mode:activate {name:'coil', current_model:{id}, section}. The §30.4
 * fan-out key is therefore `coil_<id>:selector:<action>`. ALSO listens
 * `update_rating_dialog:<id>` (hot-swap fresh models after a syncdata coil-master
 * diff — emitted by the CoilForm).
 *
 * ★ DATA-SAFETY (§4 + the 6e2a userArmed gate — HARD REQUIREMENT):
 *   - RATE has TWO paths (BOTH userArmed — NEVER auto-fired):
 *       external_rating===false → POST /ahu/rating_database_coil_selection/
 *                                  (manufacturer:'manual', KSDM master-coil DB);
 *       else                    → POST /api/v1.0/rating/ {part:'coil',
 *                                  manufacturer: mfg.name.toLowerCase()} via the
 *                                  SHARED useRatingDispatch. (mfg_id 21 short-
 *                                  circuits to an error WITHOUT calling the API.)
 *   - SELECT is a FOUR-WAY write (userArmed): (1) section.set(eadb/eawb) →
 *     PATCH ahu/sections/<id>; (2) project.save(elevation rounded) → PATCH
 *     projects/<id>; (3) current_model.set(~55 perf fields) → form auto-save +
 *     current_model.save({mounting_angle, duty_id}); (4) rated_coil_model.set →
 *     PATCH ahu/rating_mode_coils/<id> (patchRatedCoil). + draw:coil:connection,
 *     drain-pan nudge, hide cascade.
 *   - NOTHING fires on mount/open/tab-switch/browse. SMOKE = open/render/cancel →
 *     0 mutating + 0 /rating/ + 0 rating_database_coil_selection calls.
 *   - Coil rated-state on dev3 = `capacity_total IS NOT NULL` (master_catalog_coil
 *     _id is 0 for ALL coils). We render the STORED rated state read-only.
 */

import {
  createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import type { RootState } from 'app/store';
import { ahuBoardBus } from '../../bus/ahuBoardBus';
import {
  RATING_STORAGE_KEYS,
  readRatingMap,
  findLastEntry,
  findEntryByMfg,
  writeRatingEntry,
  type IndependentRatingEntry,
  type IndependentRatingMap,
} from 'pages/dashboard/components/header/independentRatingStorage';
import { optionLabel, type OptionRow } from '../formKit';
import { formatDecimalDisplay, formatIntegerDisplay } from '../formUtils';
import {
  useGetComponentDetailQuery,
  useGetComponentCmsDocQuery,
  usePatchRatedCoilMutation,
  ahuComponentFormsApi,
} from 'app/services/ahuComponentFormsApi';
import { ahuBoardFormsApi } from 'app/services/ahuBoardFormsApi';
import { projectBoardApi } from 'app/services/projectBoardApi';
import {
  ahuBoardApi,
  useGetCmsAhuDocQuery,
  useGetAhuBoardProjectQuery,
} from 'app/services/ahuBoardApi';
import { CoilPrintSheet } from './CoilPrintSheet';
import { printSheetReady } from './printSheetReady';
import { useRatingDispatch } from './useRatingDispatch';
import { buildRatingEnvelope } from './ratingEnvelope';
import { useDependencyGraph, type LoadParamsFn } from './useDependencyGraph';
import {
  COIL_CMS_DOC_URL,
  coilRatingMfgs,
  coilExternalRating,
  coilMfgName,
  coilAirStreams,
  type CoilCmsDoc,
} from './coilCms';
import {
  COIL_DEPENDENCY_GRAPH,
  COIL_COUNT_FIELDS,
  CASING_MTL_DEFAULTS,
  coilTypeChangePatch,
  coilDutyChangePatch,
  DEFAULTS_DEPENDS_ON_DUTY,
  coilCollectionUrl,
  coilPickFallback,
  COIL_NONE_OPTION_FIELDS,
  coilMfgChangePatch,
  coilTypeDutyDefaultsPatch,
  coilOptionId,
  coilOptionLabel,
  coilOptionValue,
  coilTypeFromTypes,
  coilTypeVisibility,
  buildHeaderVendorRateInput,
  buildDbRateInput,
  buildCoilSelectPayload,
  parseTxv,
  applyCoilRequiredAwHeights,
  heatingCoilHasUpstreamCooling,
  type CoilCmsSettingsAw,
  type CoilParents,
  type ReheatScanCoil,
  type ReheatScanSection,
} from './coilSelectorData';
import {
  useGetCoilNotesQuery,
  usePostCoilDatabaseRatingMutation,
} from './coilSelectorApi';
import type { ComponentFormShim } from '../componentFormTypes';
import { guardSelect } from '../cascadeCommit';
import { pendingSelectWrites, selectWriteKey, trackSelectWrites } from './selectWriteGate';
import { disableSyncOnAhu, sectionAirFieldsChanged } from '../commitAirConditions';
import { takeSectionPendingRerate } from '../../psychrometric/pendingRerate';
import { cmsSeasonsByComponent, cmsTunnelIndexes, deriveSeasonValue } from '../cmsDoc';
import { MIN_AIR_TEMP_F } from '../SeasonGroup';
import type { ICmsAhuDoc } from 'models';
import type { SelectorTabProps } from './selectorTypes';
import { track } from 'app/analytics';

const num = (v: string): number | null => {
  if (v === '' || v === 'None' || v === 'none' || v === '--') return null;
  const n = Number(v.replace(/,/g, ''));
  return Number.isNaN(n) ? null : n;
};

function parseParams(raw: unknown): Record<string, unknown> {
  if (raw == null) return {};
  try {
    return JSON.parse(String(raw)) as Record<string, unknown>;
  } catch {
    return {};
  }
}

// Selector-parity — the full legacy nav (coil.selector.tmpl:6-27): Coil,
// Performance, Manifold (Headers/Connections), Casing, Options, Mounting,
// Pricing. 'Properties' is the React name for the legacy 'Coil' tab.
// Mounting is a real legacy tab (tmpl:22-24 / pane :1296-1362) — folding its
// three fields into the Coil tab is what made that tab 29 rows against dev's
// 19, so it is split back out.
type InputTab =
  'Properties' | 'Performance' | 'Manifold' | 'Casing' | 'Options' | 'Mounting' | 'Pricing';
// legacy nav order (tmpl:7-27) — Mounting sits between Options and Pricing.
const INPUT_TABS: readonly InputTab[] = [
  'Properties', 'Performance', 'Manifold', 'Casing', 'Options', 'Mounting', 'Pricing',
];
// the legacy pane id per tab (tmpl:45/:478/:975/:1364/:1516/:1752), prefixed at
// render with `ahu-` outside header mode. These double as the nav <li> class,
// exactly as legacy does it (tmpl:7-27).
const PANE_ID: Record<InputTab, string> = {
  Properties: 'Coil_Properties_Performance',
  Performance: 'Coil_Performance',
  Manifold: 'Coil_Headers_Connections',
  Casing: 'Coil_Casing_Options',
  Options: 'Coil_Options',
  Mounting: 'Coil_Mounting',
  Pricing: 'Coil_Pricing_Coil',
};
// the legacy pane class list per tab, verbatim from the template — the spacing
// (`mt-26` / `mt-0`) and width (`col-xs-12`, `float-left`) hooks the React port
// had dropped. `.coil_new .tab-pane.mt-26` is a real rule (common.css:13794).
const PANE_CLASS: Record<InputTab, string> = {
  Properties: 'scrollable-content mt-26 col-xs-12 float-left mt-0',
  Performance: 'scrollable-content col-xs-12 float-left mt-0',
  Manifold: 'scrollable-content col-xs-12 float-left',
  Casing: 'scrollable-content col-xs-12 float-left mt-26',
  Options: 'scrollable-content col-xs-12 float-left mt-26',
  Mounting: 'scrollable-content col-xs-12 mt-26',
  Pricing: 'mt-26 scrollable-content col-xs-12',
};
// the legacy nav label per tab (tmpl:8/:11/:14/:17/:20/:26).
const TAB_LABEL: Record<InputTab, string> = {
  Properties: 'Coil',
  Performance: 'Performance',
  Manifold: 'Manifold',
  Casing: 'Casing',
  Options: 'Options',
  Mounting: 'Mounting',
  Pricing: 'Pricing',
};

// the rated-output nav (coil.rated.outputs.tmpl:1-18). `id` is the legacy pane
// id sans prefix, `nav` the legacy <li> class sans prefix.
type OutputTab = 'coil' | 'performance' | 'notes';
const OUTPUT_TABS: ReadonlyArray<{ tab: OutputTab; label: string; id: string; nav: string }> = [
  { tab: 'coil', label: 'Coil', id: 'Coil_output', nav: 'Coil-output' },
  { tab: 'performance', label: 'Performance', id: 'Properties-Performance_output', nav: 'Properties-output' },
  { tab: 'notes', label: 'Notes', id: 'Notes-output', nav: 'Notes-output' },
];

interface CoilModel extends Record<string, unknown> {
  id?: number;
  duty_id?: number | null;
  type_id?: number | null;
  master_coil?: Record<string, unknown> | null;
  capacity_total?: number | null;
}

/**
 * ★ Phase 6.5 — the HEADER-mode seed defaults: legacy `self.coilDefaults`
 * (coil.selector.js:257-383) WITH the `options.header` override applied on top
 * (js:394-420 — `{...self.coilDefaults, fh:36, fl:48, qty_stacked:1, data_id:27,
 * …}`). Ported VERBATIM including the inert members (`id`/`coil_id` 132,
 * `model:null`, `coil_params:null`, the duplicated `nitrogen_charge`) — the
 * legacy popup on `/sizer/` writes exactly these into
 * `coil_independent_ratings.<mfg>.model_data`, so a byte-compatible writer has
 * to carry them. `buildVendorRateInput`/`buildDbRateInput` are allow-lists, so
 * the inert keys never reach the vendor.
 *
 * ★ `eadb_temp`/`eawb_temp` appear twice in legacy header mode and agree at
 * 80/67: once in the override above (into `model_data.model`) and once into
 * `model_data.section` from `defaults_depends_on_duty['1_1']` (js:525-528, key
 * `${type_id}_${duty_id || 1}` = `1_1` for the default `type_id:1`). The COIL
 * TEMPLATE renders the SECTION pair (`value="{{section.eadb_temp}}"` —
 * coil.selector.tmpl:603/:609), which is what React's `section`-flagged Num
 * rows read off the flat state.
 */
const HEADER_COIL_DEFAULTS: Record<string, unknown> = {
  aux_side_conn: false,
  casing_flange_btm_id: 3,
  casing_flange_header_side_id: 3,
  casing_flange_return_side_id: 3,
  casing_flange_top_id: 3,
  casing_height: null,
  casing_length: null,
  casing_mtl_id: 2550,
  casing_stacking_flange: false,
  casing_type_id: 3,
  circuiting_id: 1,
  coil_id: 132,
  coil_params: null,
  condensate_conn_size_id: null,
  conn_end_id: 1,
  conn_hand_id: null,
  conn_mtl_id: 4241,
  conn_pipe_end_id: 1,
  conn_protrusion_from_header: 7,
  conn_rotation_id: 1,
  depth: null,
  distributor_1_dia: null,
  distributor_2_dia: null,
  distributor_3_dia: null,
  distributor_4_dia: null,
  fastening_method_id: 1,
  feeds_id: null,
  // fh/fl: null in the base object but 36/48 after the header override
  // (coil.selector.js:396-397) — the merged value, which React was missing.
  fh: 36,
  fin_design: null,
  fin_finish_id: 1,
  fin_id: null,
  fin_mtl_id: 3583,
  fin_pattern_id: 4,
  fin_thick_id: 4,
  fl: 48,
  fl_id_to_header_id: 1.5,
  fpi_id: null,
  header_cl_to_casing_edge: 1.5,
  header_mtl_id: 1036,
  id: 132,
  inlet_conn_cl_to_btm_of_casing: 5.06,
  inlet_conn_dia_id: null,
  inlet_header_dia: null,
  mfg_id: 27,
  model: null,
  moisture_eliminator: false,
  mounting_holes: false,
  notes: null,
  operating_weight: null,
  outlet_conn_cl_to_top_of_casing: 5.06,
  outlet_conn_dia_id: null,
  outlet_header_dia: null,
  overall_length: null,
  rating_datetime: null,
  rows_id: null,
  shipping_weight: null,
  style_id: null,
  thermostatic_vent: false,
  tube_cleanable_id: null,
  tube_dia_id: 4,
  tube_hor_spacing_id: null,
  tube_interior_id: 1,
  tube_mtl_id: 4241,
  tube_vert_spacing_id: null,
  tube_wall_thick_id: 11,
  type_id: 1,
  vacuum_breaker: false,
  vent_drain_id: 1,
  label_kit: false,
  nitrogen_charge: false,
  // ---- the options.header override (coil.selector.js:394-420) ----
  qty_stacked: 1,
  data_id: 27,
  entering_fluid_temp: 45,
  face_velocity_std: 500,
  face_velocity_actual: 500,
  airflow_std: 6000,
  airflow_actual: 6000,
  airflow_actual_total: 6000,
  air_stream_id: 1,
  htfluid_id: 1,
  fpi_max_id: 27,
  evaporator_mode_id: 5,
  desired_ladb_temp: 55,
  desired_lawb_temp: 54,
  desired_leaving_fluid_temp: 65,
  h_from_floor: 0,
  pricing_method_id: 1,
  eadb_temp: 80,
  eawb_temp: 67,
  mounting_angle: 0,
};
// the override re-assigns fh/fl AFTER the defaults' nulls (js:397-398).
HEADER_COIL_DEFAULTS.fh = 36;
HEADER_COIL_DEFAULTS.fl = 48;
// ★ Phase 6.5 (builder 3) — the two fouling factors are pre-filled by the
// TEMPLATE, not by coilDefaults: coil.selector.tmpl:944/949 render
// `value="{{#if model.fouling_factor_inside}}{{…}}{{else}}0{{/if}}"`, so a
// first-ever open shows "0.00" in both boxes (confirmed live on /sizer/) where
// the port showed them EMPTY. Legacy's `collectData` then sweeps the DOM, so its
// rate envelope carries 0 too — seeding the state 0 matches BOTH the display and
// the payload. HEADER-ONLY: this object is the header seed; the board seed builds
// its own state from the saved coil.
HEADER_COIL_DEFAULTS.fouling_factor_inside = 0;
HEADER_COIL_DEFAULTS.fouling_factor_outside = 0;

/**
 * Selector-parity COIL (Reset) — the keys `HEADER_COIL_DEFAULTS` carries that
 * legacy's *board* `self.coilDefaults` does NOT.
 *
 * `HEADER_COIL_DEFAULTS` above is the MERGE of legacy's base `coilDefaults`
 * (coil.selector.js:369-393) with the `if (options.header)` override
 * (js:394-420) plus the two template-supplied fouling factors — that merged
 * shape is what header mode seeds from, so the port never needed the two halves
 * apart. `reset` does: it spreads `self.coilDefaults`, which in BOARD mode is
 * the base object only. Resetting a board coil must therefore leave airflow /
 * face velocity / qty stacked / HT fluid / evaporator mode / pricing method /
 * mounting angle / air stream / height-from-floor alone — legacy does, because
 * none of them exist on the base object.
 *
 * `fh`/`fl`/`entering_fluid_temp`/`desired_ladb_temp`/`desired_lawb_temp`/
 * `desired_leaving_fluid_temp`/`eadb_temp`/`eawb_temp` are listed here too, but
 * only for honesty about which half they came from: `reset` re-assigns fh/fl
 * itself (js:4613-4614) and the rest arrive from
 * `defaults_depends_on_duty['1_1']` (js:4616), so excluding them changes
 * nothing.
 */
const HEADER_ONLY_COIL_DEFAULT_KEYS = new Set([
  'fh', 'fl', 'qty_stacked', 'data_id', 'entering_fluid_temp',
  'face_velocity_std', 'face_velocity_actual', 'airflow_std', 'airflow_actual',
  'airflow_actual_total', 'air_stream_id', 'htfluid_id', 'fpi_max_id',
  'evaporator_mode_id', 'desired_ladb_temp', 'desired_lawb_temp',
  'desired_leaving_fluid_temp', 'h_from_floor', 'pricing_method_id',
  'eadb_temp', 'eawb_temp', 'mounting_angle',
  'fouling_factor_inside', 'fouling_factor_outside',
]);
/** legacy's base `self.coilDefaults` (coil.selector.js:369-393) — the object
 * `reset` spreads in BOARD mode. */
const BOARD_COIL_DEFAULTS: Record<string, unknown> = Object.fromEntries(
  Object.entries(HEADER_COIL_DEFAULTS).filter(
    ([k]) => !HEADER_ONLY_COIL_DEFAULT_KEYS.has(k)
  )
);

// Selector-parity COIL — the Performance "Per Coil | All Coils" pairs kept in
// sync through qty_stacked (coil.selector.js changeCoilField :2481-2560).
//   per → all: total = qty_stacked × per   (calculateTotalParameter, js:3350-3355)
//   all → per: per   = total ÷ qty_stacked (calculatePerCoil,        js:3345-3348)
// capacity_sensible's total key is `capacity_total` (special, js:2486-2488 /
// :2504-2506); the other three are `<per>_total` / `<all sans _total>`.
// ★ airflow_actual belongs here too and was excluded. Its per-coil box carries
// `per_coil_field` (tmpl:520-522) and its All-Coils box `all_coils_field`
// (tmpl:526-528), so BOTH directions run in dev. Leaving it out meant an edit
// to either airflow box left the other stale — and since the vendor reads only
// the TOTAL (coilSelectorData.ts:1254), a per-coil airflow edit never reached
// it while a total edit left the displayed per-coil value wrong.
const COIL_PER_TO_ALL: Record<string, string> = {
  airflow_actual: 'airflow_actual_total',
  capacity_sensible: 'capacity_total',
  desired_capacity_total: 'desired_capacity_total_total',
  fluid_flow: 'fluid_flow_total',
};
const COIL_ALL_TO_PER: Record<string, string> = {
  airflow_actual_total: 'airflow_actual',
  capacity_total: 'capacity_sensible',
  desired_capacity_total_total: 'desired_capacity_total',
  fluid_flow_total: 'fluid_flow',
};
const hasLen = (v: unknown) => v != null && String(v).length > 0;
const numv = (v: unknown) => Number(String(v).replace(/,/g, ''));
/** Returns the twin/total keys to patch onto state for a per/all/qty edit
 *  (coil.selector.js:2481-2560). Empty patch when `name` isn't a pair member. */
function coilPerAllRecompute(
  name: string,
  value: unknown,
  st: Record<string, unknown>
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const qty = st.qty_stacked;
  if (name in COIL_PER_TO_ALL) {
    // per edited → total = qty × per (js:2481-2498). Empty source ⇒ '' total.
    out[COIL_PER_TO_ALL[name]] =
      hasLen(value) && hasLen(qty) ? numv(qty) * numv(value) : '';
  } else if (name in COIL_ALL_TO_PER) {
    // all edited → per = total ÷ qty (js:2499-2516). Empty source ⇒ '' per.
    out[COIL_ALL_TO_PER[name]] =
      hasLen(value) && hasLen(qty) ? numv(value) / numv(qty) : '';
  } else if (name === 'qty_stacked') {
    // qty changed → legacy walks the rendered `.per_coil_field` boxes
    // (js:2532-2560) recomputing each total from its per-coil value…
    for (const [perKey, allKey] of Object.entries(COIL_PER_TO_ALL)) {
      if (perKey === 'airflow_actual') {
        // …except airflow, which is inverted (js:2541-2547): the ALL-COILS
        // total is what the user asked the AHU to move, so it is held fixed
        // and the per-coil box is re-derived as total ÷ qty. The empty branch
        // still clears `<per>_total` (js:2557), i.e. airflow_actual_total.
        if (hasLen(st[perKey]) && hasLen(value)) {
          out[perKey] = numv(st[allKey]) / numv(value);
        } else {
          out[allKey] = '';
        }
        continue;
      }
      const per = st[perKey];
      out[allKey] = hasLen(per) && hasLen(value) ? numv(value) * numv(per) : '';
    }
  }
  return out;
}

/** legacy `calcAirflowAndFaceVelocity` (coil.selector.js:3418-3520). It has two
 *  halves and BOTH were missing here:
 *
 *   • the ELEM half, fired from changeCoilField for airflow_actual /
 *     face_velocity_actual / qty_stacked (js:2563-2565) and from changeParams
 *     for fh / fl (js:2697-2699);
 *   • the NO-ELEM half, fired at the end of EVERY render (js:1862), which
 *     re-derives face_velocity_actual from the PER-COIL airflow and fh × fl
 *     unconditionally.
 *
 *  Because the render half always runs last, dev's steady state is simply
 *
 *      face_velocity_actual = round(airflow_actual ÷ (fh × fl ÷ 144))
 *
 *  — a derived display — and a typed face velocity back-solves the airflow
 *  pair on its way there (js:3457-3466). React had face_velocity_actual as a
 *  read-only box holding a stale 500, so neither direction ran.
 */
function coilFaceVelocityFromAirflow(
  st: Record<string, unknown>
): Record<string, unknown> {
  const fh = numv(st.fh);
  const fl = numv(st.fl);
  if (!hasLen(st.airflow_actual)) return { face_velocity_actual: '' };
  // dev would divide by NaN here and stamp "NaN" into the box; hold the
  // previous value instead — fh/fl are never legitimately unset in practice.
  if (!fh || !fl) return {};
  return {
    face_velocity_actual: Math.round(numv(st.airflow_actual) / ((fh * fl) / 144)),
  };
}
/** the face-velocity EDIT direction (js:3457-3466): airflow = fv × fh × fl ÷ 144,
 *  and the All-Coils total follows through calculateTotalParameter. */
function coilAirflowFromFaceVelocity(
  value: unknown,
  st: Record<string, unknown>
): Record<string, unknown> {
  const fh = numv(st.fh);
  const fl = numv(st.fl);
  if (!hasLen(value) || !fh || !fl) return {};
  const airflow = (numv(value) * fh * fl) / 144;
  return {
    airflow_actual: airflow,
    airflow_actual_total: hasLen(st.qty_stacked)
      ? numv(st.qty_stacked) * airflow
      : airflow,
  };
}

/** legacy `getAHUAirflow` (coil.selector.js:4573-4599) — picking an Air Stream
 *  pulls that stream's AHU design volume into the airflow pair. BOARD ONLY
 *  (`if (self.header) return`, js:4575); an AHU volume of 0/null leaves the
 *  current total in place. */
const COIL_AIR_STREAM_VOLUME_KEY: Record<string, string> = {
  '2': 'ra_volume_max',
  '3': 'oa_volume_max',
  '4': 'ea_volume_max',
};
function coilAirStreamAirflowPatch(
  streamId: unknown,
  st: Record<string, unknown>,
  ahuDoc: Record<string, unknown> | null | undefined
): Record<string, unknown> {
  const current = numv(st.airflow_actual_total);
  const key = COIL_AIR_STREAM_VOLUME_KEY[String(streamId)] ?? 'sa_volume_max';
  const volume = ahuDoc ? numv(ahuDoc[key]) : NaN;
  const next = volume ? volume : current;
  if (!next || next === current) return {};
  const qty = hasLen(st.qty_stacked) ? numv(st.qty_stacked) : 1;
  const per = next / (qty || 1);
  return {
    airflow_actual_total: next,
    airflow_actual: per,
    ...coilFaceVelocityFromAirflow({ ...st, airflow_actual: per }),
  };
}

/** legacy `validateNumberField` (coil.selector.js:4254-4297) — every
 *  `data-type="number"` input is clamped to its `data-min`/`data-max` and
 *  snapped to `data-step` ON COMMIT, and the MODEL then holds a NUMBER, not the
 *  comma-formatted string the box shows. React kept the raw string, so
 *  (a) nothing was clamped and (b) a value the user edited around an existing
 *  separator ("6,0000") went to the vendor with the comma still in it.
 *  Applied to the Performance pairs named in the report and their pair
 *  partners; `bounds` undefined ⇒ commit unchanged (every other field keeps its
 *  current behaviour). */
export interface CoilNumBounds { min?: number; max?: number; step?: number }
/** tmpl:693-707 / :666-679 — the four capacity boxes. */
const CAPACITY_BOUNDS: CoilNumBounds = { min: 0, step: 1, max: 10000000 };
/** tmpl:646/:653/:930 — the fluid temperatures (EFT/LFT). */
const TEMP_BOUNDS: CoilNumBounds = { min: 0, step: 0.01, max: 240 };
/** The leaving AIR temperatures, which go down to -40 °F like every other
 * air-temperature input (see SeasonGroup's MIN_AIR_TEMP_F and ahu migration
 * 0152) — a preheat coil in -40 °F outside air has to be enterable. The
 * fluid pair above keeps its own 0 °F floor. */
const AIR_TEMP_BOUNDS: CoilNumBounds = { min: MIN_AIR_TEMP_F, step: 0.01, max: 240 };
function coilValidateNumber(raw: string, b: CoilNumBounds | undefined): string {
  if (raw === '') return raw;
  const stripped = raw.replace(/,/g, '');
  let value = Number(stripped);
  if (!Number.isFinite(value)) return stripped;
  if (!b) return stripped;
  const min = b.min ?? -Infinity;
  const max = b.max ?? Infinity;
  if (value < min) value = min;
  if (value > max) value = max;
  if (b.step === 0.01) {
    value = Number(value.toFixed(2));
  } else {
    const step = b.step ?? 1;
    if (step > 1 && value % step > 0) value = Math.min(Math.ceil(value / step) * step, max);
    value = Math.round(value);
  }
  return String(value);
}
/** the PER-COIL airflow box alone gets a derived max — `max_airflow /
 *  qty_stacked` (js:4258-4263, keyed on `name === 'airflow_actual'`), falling
 *  back to the template's 400000. The All-Coils box keeps the flat data-max
 *  (tmpl:527). */
function coilAirflowBounds(st: Record<string, unknown>, perCoil: boolean): CoilNumBounds {
  if (!perCoil) return { min: 0, step: 1, max: 400000 };
  const max = numv(st.max_airflow) || 400000;
  const qty = hasLen(st.qty_stacked) ? numv(st.qty_stacked) || 1 : 1;
  return { min: 0, step: 1, max: max / qty };
}

// ---------------------------------------------------------------------------
// the dialog's field controls
// ---------------------------------------------------------------------------
/**
 * ★ THESE SIT AT MODULE SCOPE ON PURPOSE.
 *
 * They used to be declared INSIDE `CoilSelector`, which hands React a brand-new
 * component type on every render — and React answers a changed type by
 * UNMOUNTING the old subtree and mounting a fresh one instead of updating it.
 * Committing a field re-renders the dialog, so every commit threw away the very
 * `<input>` the browser had just tabbed out of: measured on the box, one commit
 * on the Performance pane replaced 22 of its 27 boxes. The browser was left with
 * nothing to focus and dropped it on `<body>`, so the user's Tab looked like it
 * had done nothing and only the NEXT Tab moved on (it resumes from where the
 * removed node had been) — the "after editing a field I have to press Tab twice"
 * report, 2026-10-13. A stable component type keeps the DOM nodes, and the
 * focus, alive across a commit.
 *
 * Everything volatile they used to close over now arrives through
 * `CoilFieldCtx`, whose value is rebuilt on every `CoilSelector` render, so the
 * controls still render from the current state and commit through the current
 * handlers.
 */
interface CoilFieldCtxValue {
  /** the live selector state (`state.current`). */
  s: Record<string, any>;
  masterLists: Record<string, any[]>;
  /** the saved coil model — only read for a select's saved-option label. */
  detail: unknown;
  coilType: string;
  /** `resolveDutyValue()` for this render — TempPairRow's wet-bulb gate. */
  dutyValue: string;
  onModelField: (name: string, raw: string | null) => void;
  onSearchField: (name: string, raw: string) => void;
  onSectionField: (name: string, raw: string) => void;
  onCheckboxField: (name: string, checked: boolean) => void;
}

const CoilFieldCtx = createContext<CoilFieldCtxValue | null>(null);

function useCoilFields(): CoilFieldCtxValue {
  const ctx = useContext(CoilFieldCtx);
  if (!ctx) throw new Error('a coil field control was rendered outside CoilSelector');
  return ctx;
}

// ----- master-list dependent select -----
// `listKey` reads options from a DIFFERENT masterLists key (the shared lists:
// connection_diameters feeds the 4 header/conn dia selects; casing_flange_widths
// the 4 flange selects; fpi_id the Fin-Spacing-Max select). `withNone` renders
// the legacy `<option value="None">- -</option>`; `disabled` (Manifold reset_field
// — the enable_inlet/enable_outlet gate) forces the None value + only the dash.
// the bare <select> — split out of CoilSelect so a PairRow can host two of
// them side by side (legacy's Circuiting | Feeds row, tmpl:251-295).
const CoilSelectControl = ({
  name, cls = 'search_field', listKey, onChange, dataName,
  withNone, hideNone, disabled, noneValue = 'None',
}: {
  name: string; cls?: string; listKey?: string; dataName?: string;
  onChange?: (raw: string) => void;
  withNone?: boolean; hideNone?: boolean; disabled?: boolean; noneValue?: string;
}) => {
  const { s, masterLists, detail, onSearchField, onModelField } = useCoilFields();
  const value = s[name];
  const valStr = value == null || value === '' ? '' : String(value);
  const rows = disabled ? [] : (masterLists[listKey ?? name] ?? []);
  const hasRow = rows.some((r: any) => String(coilOptionId(r, name)) === valStr);
  const savedName = (detail as any)?.[`${name.replace(/_id$/, '')}_name`];
  const isSearch = cls.split(/\s+/).includes('search_field');
  const handler = onChange ?? ((raw: string) => (isSearch ? onSearchField(name, raw) : onModelField(name, raw)));
  // withNone/disabled selects show the `- -` (value="None") option when unset,
  // so the controlled value resolves to it rather than an unmatched empty value.
  const effectiveVal = disabled || ((withNone) && valStr === '') ? noneValue : valStr;
  return (
    <select name={name} data-name={dataName ?? name}
      className={`${cls}${disabled ? ' reset_field non-clickable' : ''}`}
      value={effectiveVal}
      onChange={(e) => handler(e.target.value)}>
      {(withNone || disabled) && (
        <option value={noneValue} className={hideNone ? 'hide' : undefined}>- -</option>
      )}
      {!withNone && !disabled && valStr === '' && <option value="" />}
      {rows.map((r: any) => (
        <option key={String(coilOptionId(r, name))} value={String(coilOptionId(r, name))}>
          {coilOptionLabel(r, name)}
        </option>
      ))}
      {!disabled && valStr !== '' && !hasRow && (
        <option value={valStr}>{savedName != null ? String(savedName) : valStr}</option>
      )}
    </select>
  );
};

type CoilSelectProps = React.ComponentProps<typeof CoilSelectControl> &
  { label: string; unit?: string; width?: string; required?: boolean };
const CoilSelect = ({ label, unit, width, required, ...rest }: CoilSelectProps) => (
  <SelectRow label={label} dataName={rest.name} unit={unit} width={width} required={required}>
    <CoilSelectControl {...rest} />
  </SelectRow>
);

// Selector-parity — the Manifold/Casing/Options checkboxes (mounting_holes,
// casing_stacking_flange, vacuum_breaker, thermostatic_vent, moisture_eliminator,
// label_kit, nitrogen_charge, aux_side_conn, txv, txv_instl). Booleans, via
// onCheckboxField.
// legacy wraps the box in `col-xs-N checkbox-vert-distance` directly inside
// `.input-part > .row` — col-xs-3 on Options, col-xs-6 on Manifold/Casing.
const Checkbox = ({ name, label, disabled, width = 'col-xs-3' }: {
  name: string; label: string; disabled?: boolean; width?: string;
}) => {
  const { s, onCheckboxField } = useCoilFields();
  return (
    <SelectRow label={label} dataName={name} width={`${width} checkbox-vert-distance`}>
      <input type="checkbox" name={name} data-name={name}
        className="checkbox-input params_coil search_field"
        checked={parseTxv(s[name])} disabled={disabled}
        onChange={(e) => onCheckboxField(name, e.target.checked)} />
    </SelectRow>
  );
};

/**
 * ONE number box, shared by NumControl and the Per Coil / All Coils pairs.
 *
 * Legacy formats a numeric input on COMMIT only: formatter.js
 * NumericInputFormatter binds onchange ("Don't doing anything while editing" —
 * oninput is deliberately commented out) and encode() returns the raw value
 * while the input is focused; the coil field handlers bind 'change
 * .coil_field/.section_field/…' (coil.selector.js:161-176), which fires on
 * blur/Enter. A plain `value={display}` re-formats on every keystroke ("154"
 * typed char-by-char became "1.00"), which is why these boxes were uncontrolled.
 *
 * ★ `draft` is what lets them be CONTROLLED without that: it is set by TYPING
 * and by nothing else, so the user's own text wins while they are editing, and
 * blur drops it so the freshly formatted model value comes back. Controlled
 * they must be, now that a commit no longer remounts them — `defaultValue` is
 * written to the DOM once, so an uncontrolled box would keep showing exactly
 * what was typed and would never pick up a DERIVED change (face velocity
 * following an airflow edit, an All-Coils total following its per-coil box, a
 * cascade re-pick). Same shape as `SelectorNumberInput` in the accessory dialog.
 *
 * ★ A FOCUSED BOX THE USER HAS NOT TYPED INTO STILL FOLLOWS THE MODEL, and a
 * focus/blur pass over it commits NOTHING. Both halves matter, and both are
 * about writes that land while the box has focus — the manufacturer/type
 * cascade merging `coilTypeDutyDefaultsPatch` (EFT, LFT, EADB/EAWB, the desired
 * temps) through `setStateValue`, and header mode's `pendingRestore` timeout:
 *  · `draft` stays null until a keystroke, so the late value is DISPLAYED,
 *    which is what the old remount-on-every-commit did by accident.
 *  · the blur test is against `atFocus` — the text as it stood when the box was
 *    focused — and not against the current `display`, which by then may be the
 *    newly arrived value. Testing against `display` meant a user who clicked
 *    into EFT while the cascade was in flight and clicked straight back out
 *    SAVED THE OLD NUMBER over the new default, marked the coil changed and
 *    cleared the rated rows (review bot, PR #2062). `SelectorNumberInput`
 *    :130/:162 keeps the same ref for the same reason.
 *
 * So an UNCHANGED value must not commit — a plain focus/blur pass may not fire
 * markChanged / invalidate rated rows.
 */
const CoilNumberBox = ({
  name, dataName, display, className, formatter, bounds, readOnly, tabSkip, style, onCommit,
}: {
  name: string; dataName?: string; display: string; className: string;
  formatter: 'integer' | 'decimal'; bounds?: CoilNumBounds;
  readOnly?: boolean; tabSkip?: boolean; style?: React.CSSProperties;
  onCommit: (value: string) => void;
}) => {
  /** the user's own text — non-null only once they have TYPED into this box. */
  const [draft, setDraft] = useState<string | null>(null);
  /** the text as it stood when the box was focused: what "changed" is measured
   *  against on blur (`SelectorNumberInput` :130). */
  const atFocus = useRef<string>('');
  return (
    <input type="text" data-type="number" name={name} data-name={dataName ?? name} style={style}
      data-formatter_type={formatter} autoComplete="off"
      data-min={bounds?.min} data-max={bounds?.max} data-step={bounds?.step}
      className={className}
      readOnly={readOnly} tabIndex={readOnly || tabSkip ? -1 : undefined}
      value={draft ?? display}
      onFocus={(e) => { atFocus.current = e.target.value; }}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        const typed = draft;
        setDraft(null);
        // `typed === null` ⇒ never typed into, so there is nothing to save even
        // if the displayed value moved under the caret meanwhile.
        if (typed !== null && typed !== atFocus.current) {
          onCommit(coilValidateNumber(typed, bounds));
        }
      }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
  );
};

// bare numeric input — split out so PairRow can host two (legacy FH x FL).
const NumControl = ({ name, formatter = 'decimal', model = true, readOnly, section, fallback, style, bounds }: {
  name: string; formatter?: 'integer' | 'decimal';
  model?: boolean; readOnly?: boolean; section?: boolean;
  /** legacy's `data-min`/`data-max`/`data-step`, applied on commit by
   *  validateNumberField. Omitted ⇒ the raw value is committed as before. */
  bounds?: CoilNumBounds;
  /** legacy's inline `value="{{#if model.X}}{{model.X}}{{else}}D{{/if}}"`
   * display default. Handlebars `#if` is FALSY-based, so 0 also falls
   * through to the default — reproduced verbatim. Display only: the model
   * value stays unset until the user commits, exactly as legacy. */
  fallback?: number;
  style?: React.CSSProperties;
}) => {
  const { s, onModelField, onSearchField, onSectionField } = useCoilFields();
  const raw = s[name];
  const v = (raw || raw === 0) && raw !== '' ? raw : undefined;
  const eff = fallback != null && !v ? fallback : raw;
  const display = formatter === 'integer' ? formatIntegerDisplay(eff) : formatDecimalDisplay(eff);
  const commit: (n: string, raw: string) => void =
    section ? onSectionField : model ? onModelField : onSearchField;
  return (
    <CoilNumberBox name={name} display={display} formatter={formatter} bounds={bounds} style={style}
      readOnly={readOnly}
      className={`${section ? 'section_field' : model ? 'model_field' : 'search_field'}${readOnly ? ' non-clickable' : ''}`}
      onCommit={(value) => commit(name, value)} />
  );
};

type NumProps = React.ComponentProps<typeof NumControl> &
  { label: string; unit?: string; required?: boolean; width?: string };
const Num = ({ label, unit, required, width, ...rest }: NumProps) => (
  // A read-only row greys its LABEL as well as its input on dev — the label
  // span carries `non-clickable` in the template (Altitude,
  // coil.selector.tmpl:743). The port put the class on the input only, so
  // dev3's Altitude read as an ordinary editable field.
  <SelectRow label={label} dataName={rest.name} unit={unit} required={required} width={width}
    labelClass={rest.readOnly ? 'non-clickable' : undefined}>
    <NumControl {...rest} />
  </SelectRow>
);

// Selector-parity — the Performance "Per Coil | All Coils" input pairs
// (coil.selector.tmpl: airflow/capacity/fluid-flow rows). The per-coil box keeps
// its existing commit type (model_field — no rated invalidation); the all-coils
// box is a `search_field` (invalidates on edit) and is the value legacy actually
// sends to the vendor (airflow_actual_total etc.).
// `locked` is legacy's `non-clickable` + `tabindex="-1"` pair: the class is
// only styling plus a `keypress` preventDefault (`notAllowType`, js:2925), so
// the box stays in the DOM and keeps its value — readOnly is the React
// equivalent. `tabSkip` is the template's bare `tabindex="-1"` where legacy
// sets it WITHOUT the class (the Capacity Total pair, tmpl:698/:707).
// `blank` is the template rendering an EMPTY `value` while keeping the model
// value (the fluid-flow pair under a set LFT, tmpl:945/:948) — that is what
// takes those boxes out of the rate sweep.
const PerAllRow = ({ label, unit, per, all, formatter = 'decimal', perModel = true, showPer = true, required,
                    locked = false, tabSkip = false, blank = false, perBounds, allBounds }: {
  label: string; unit?: string; per: string; all: string;
  formatter?: 'integer' | 'decimal'; perModel?: boolean; showPer?: boolean; required?: boolean;
  locked?: boolean; tabSkip?: boolean; blank?: boolean;
  perBounds?: CoilNumBounds; allBounds?: CoilNumBounds;
}) => {
  const { s, onModelField, onSearchField } = useCoilFields();
  const fmt = formatter === 'integer' ? formatIntegerDisplay : formatDecimalDisplay;
  const mkInput = (nm: string, model: boolean, bounds?: CoilNumBounds) => {
    const disp = blank ? '' : fmt(s[nm]);
    const commit = model ? onModelField : onSearchField;
    return (
      <CoilNumberBox name={nm} display={disp} formatter={formatter} bounds={bounds}
        readOnly={locked} tabSkip={tabSkip}
        className={`lg-input coil_field ${model ? 'model_field' : 'all_coils_field search_field'}${locked ? ' non-clickable' : ''}`}
        onCommit={(value) => commit(nm, value)} />
    );
  };
  // legacy grid: `col-xs-3 col-xs-offset-6` + `col-xs-3` so the two values sit
  // under the Per Coil / All Coils captions (tmpl:517-530).
  return (
    <div className="row">
      <div className="col-xs-5 label-part">
        <span data-name={per} className={required ? 'required' : undefined}>{label}</span>
        {unit && <UnitSpan unit={unit} />}
      </div>
      <div className="col-xs-7 input-part">
        <div className="row">
          <div className="col-xs-3 col-xs-offset-6 text-right">
            {showPer && mkInput(per, perModel, perBounds)}
          </div>
          <div className="col-xs-3 text-right">{mkInput(all, false, allBounds)}</div>
        </div>
      </div>
    </div>
  );
};

// legacy's EADB / EAWB and LADB / LAWB rows (tmpl:606-631, :632-657): ONE row,
// the wet-bulb half shown only for a non-steam COOLING coil — legacy gates on
// `coil_type != 'steam' && duty == 'cooling'`, not on "not heating", so a
// reheat/preheat duty hides it. Widths follow legacy: col-xs-3 + slash +
// col-xs-3 when paired, col-xs-6 alone otherwise.
const TempPairRow = ({ label, rightLabel, dataName, required, left, right }: {
  label: string; rightLabel: string; dataName: string; required?: boolean;
  left: React.ReactNode; right: React.ReactNode;
}) => {
  const { coilType, dutyValue } = useCoilFields();
  const paired = coilType !== 'steam' && dutyValue === 'cooling';
  return (
    <div className="row">
      <div className="col-xs-5 label-part">
        <span data-name={dataName} className={required ? 'required' : undefined}>{label}</span>
        {paired && <> / <span className={required ? 'required' : undefined}>{rightLabel}</span></>}
        <span className="percent right unit temp">F</span>
      </div>
      <div className="col-xs-7 input-part">
        <div className="row">
          <div className={paired ? 'col-xs-3' : 'col-xs-6'}>{left}</div>
          {paired && (
            <>
              <span className="slash-icon">/</span>
              <div className="col-xs-3">{right}</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export const CoilSelector = ({
  modelId,
  ahu,
  activate,
  setSelectEnabled,
  showNotes,
  headerMode = false,
  eventScope,
  bus: busProp,
  setPrintEnabled,
}: SelectorTabProps) => {
  const eventName = (activate.name as string) || 'coil';
  const dispatch = useAppDispatch();
  // §30.4 fan-out namespace: `coil_<id>` on the board, `coil_header` in the
  // header popup (component_selector.js:280 `(self.current_model) ? … :
  // 'header'`; the coil selector resolves the SAME fallback at
  // coil.selector.js:610 `self.model ? self.model.get('coil_id') : 'header'`).
  const scope = eventScope ?? String(modelId);
  // Isolated bus in header mode so the UN-namespaced emits below
  // (rating_mode:mounting:changed, cancel:dialog:changes …) can never reach a
  // board rating dialog open on the same page — discovery §7.3 trap 1.
  const bus = busProp ?? ahuBoardBus;
  // Own-subtree root for every DOM read. Legacy reaches `#select`/`#print`/
  // `#reset` with DOCUMENT-WIDE jQuery selectors (coil.selector.js:1460, 1468,
  // 1497, 2329 `$('#component_rating_mode .control-buttons button[id="select"]')`
  // and the bare `$('#reset')` at :1494) — on a page hosting BOTH a header popup
  // and a board dialog those cross-contaminate. Discovery §7.3 trap 1.
  const rootRef = useRef<HTMLDivElement | null>(null);
  // The loading placeholder's root. `rootRef` is only attached once the form
  // itself renders, so during the open-time preloader `chassisEl()` returned
  // null and every chassis-scoped gate below (notably the `#rate` toggle) was a
  // no-op — which is exactly the window the button has to be greyed in. This
  // second ref keeps the chassis reachable through that phase; it is
  // deliberately NOT `rootRef`, whose useLayoutEffect stamps the legacy
  // `height: 43% !important` / `min-height: 310px !important` on whatever it
  // points at and would resize the preloader.
  const loadingRef = useRef<HTMLDivElement | null>(null);
  const chassisEl = useCallback(
    (): HTMLElement | null =>
      (rootRef.current ?? loadingRef.current)?.closest('.ahu-rate-content') ?? null,
    []
  );
  // ★ The input root's size is INLINE and `!important` in legacy
  // (coil.selector.tmpl:2 — `style="height: 43% !important; min-height: 310px
  // !important"`), and the `!important` is load-bearing. Once the coil is rated
  // the root also carries `.rating`, and common.css:14998
  // `.coil-rating-mode.rating { height: 340px !important }` beats a PLAIN
  // inline height. React's `style` prop cannot express `!important`, so the
  // rated input pane rendered 340px where dev renders 310px — 30px that came
  // straight off the bottom of the output panel below it, cutting off the
  // `Rated on` line. Measured against the real sheets at a 715px content box:
  // dev 310 + 346 = 656 (fits), React 340 + 346 = 686 (overflows by 25px).
  // Set imperatively so the priority survives; no `style` prop on the element.
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    el.style.setProperty('height', '43%', 'important');
    el.style.setProperty('min-height', '310px', 'important');
  });
  // coil.selector.js:1409 (+ the selector tmpl) `prefix: self.header ? '' :
  // 'ahu-'` — the tab-pane ids drop the prefix in header mode, which is what
  // keeps the header popup's panes from colliding with the board dialog's ids.
  const idPrefix = headerMode ? '' : 'ahu-';

  // ---- read-only loads (fire on mount; never mutate) ----
  // ★ HEADER MODE: there is no current_model (component_selector.js:103-113
  // never assigns one), so legacy issues NO component-detail read at all —
  // `self.current_model` is undefined and the whole `if (self.current_model)`
  // seed branch (coil.selector.js:469-494) is skipped. `modelId` is 0 there and
  // `skip` mirrors the absent fetch exactly.
  const {
    data: detail,
    isLoading: detailLoading,
    isError: detailError,
    fulfilledTimeStamp: detailFulfilledAt,
  } = useGetComponentDetailQuery(
    { plural: 'coils', id: modelId },
    { refetchOnMountOrArgChange: true, skip: !modelId }
  );
  // the rated_coil_model (rating_mode_coils/<id> — the master rated row; the
  // CoilForm already fetched it for G31, this is the dialog's copy).
  // Header mode: `self.model = options.rated_coil_model` is undefined
  // (component_selector.js:157 only sets it for the BOARD chassis) ⇒ no fetch.
  const {
    data: ratedCoilModel,
    isError: ratedError,
    fulfilledTimeStamp: ratedFulfilledAt,
    isLoading: ratedCoilLoading
  } = useGetComponentDetailQuery(
    { plural: 'rating_mode_coils', id: modelId },
    { refetchOnMountOrArgChange: true, skip: !modelId }
  );

  // ---- ★ seed only from a row that POSTDATES this open (§ the reopen gate) --
  // `getComponentDetail` carries no tags and keeps an unsubscribed entry for RTK
  // Query's default 60 s, and the chassis mounts a fresh selector on every open
  // (`key={dialog.openSeq}`). So a reopen inside that minute is served the
  // CACHED row synchronously: `refetchOnMountOrArgChange` does re-pull, but
  // `isLoading` is false the whole time (it is true only on the very first ever
  // load) and the seed below latches `seeded.current = modelId`, which blocks
  // any re-seed when the fresh row lands. Reopening right after a Select
  // therefore painted the PRE-select row — and since "rated" is
  // `capacity_total != null`, a coil rated for the first time came back up with
  // an EMPTY output pane. The second reopen was right because the first
  // reopen's refetch had by then landed in the cache. That is the reported bug.
  //
  // ⚠ `isFetching` is not a usable gate: RTK Query starts the refetch from an
  // effect inside the hook, so the first render after mount still reports
  // `isFetching: false` next to the stale `data` — the seed effect runs in that
  // very commit. `fulfilledTimeStamp` is stamped when a request FULFILS, so a
  // row cached before this dialog opened can never satisfy `>= openedAt`.
  // (Same gate, same reasoning as EvapPackSelector.tsx:176-212, which was
  // measured on dev3.)
  const openedAt = useRef(Date.now());
  // …and a GET issued while the previous Select is still saving would fulfil
  // after `openedAt` while STILL returning the pre-select row. So when this open
  // follows a Select whose chain is in flight, hold the gate open (`Infinity`)
  // until those writes settle, then re-read both rows and gate on THAT.
  const pendingWrites = useRef(pendingSelectWrites(selectWriteKey('coils', modelId)));
  const [gateAt, setGateAt] = useState<number>(() =>
    pendingWrites.current ? Number.POSITIVE_INFINITY : openedAt.current
  );
  useEffect(() => {
    const writes = pendingWrites.current;
    if (!writes) return;
    let alive = true;
    void writes.then(() => {
      if (!alive) return;
      // stamped BEFORE the refetches are dispatched, so only their (later)
      // fulfilment can satisfy the gate.
      const at = Date.now();
      void dispatch(
        ahuComponentFormsApi.endpoints.getComponentDetail.initiate(
          { plural: 'coils', id: modelId },
          { forceRefetch: true, subscribe: false }
        )
      );
      void dispatch(
        ahuComponentFormsApi.endpoints.getComponentDetail.initiate(
          { plural: 'rating_mode_coils', id: modelId },
          { forceRefetch: true, subscribe: false }
        )
      );
      setGateAt(at);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId]);
  // `isError` is the escape hatch: a failed read shows whatever is cached
  // rather than pinning the preloader on for ever. An unrated coil's
  // rating_mode_coils row may legitimately 404, which is the seed's `null` case.
  const detailFresh = detailError || (detailFulfilledAt != null && detailFulfilledAt >= gateAt);
  const ratedFresh = ratedError || (ratedFulfilledAt != null && ratedFulfilledAt >= gateAt);
  // the coil CMS doc (rating_mfg / htfluid / evaporator_mode / air_stream labels).
  const { data: coilDoc, isLoading: coilDocLoading } =
    useGetComponentCmsDocQuery(COIL_CMS_DOC_URL);

  // ---- the Manufacturer option list, keyed by `_id` (NOT `id`) ----
  // `coil.rating_mfg` can carry TWO rows for the SAME `id`: the vendor itself,
  // plus a PRIVATE-LABEL twin the backend appends when this AHU manufacturer has
  // a `generic_mfg` configured — same catalog `id`, the OWN company's `name`,
  // `generic_mfg: true` and `_id: "<id>_1"`
  // (api/rest_cms/coils_viewset.py:159-170). On dev3 that is Modine (79) plus
  // "Thermal Corporation" (id 79, `_id: "79_1"`).
  //
  // Legacy therefore keys the OPTION on `_id`: the template selects with
  // `{{#ifCompare _id '==' ../params_coil.data_id}}` (coil.selector.tmpl:60-62)
  // and the change handler stamps `params_coil.data_id = option.dataset.id`
  // (coil.selector.js:2790-2794), while `params_coil.mfg_id` stays the plain
  // catalog id for BOTH rows. React cannot reproduce that with `value={id}` —
  // two options would share a value, and both `updateOptions` (every commit)
  // and `restoreControlledState` (after every change event) select the FIRST
  // match, so the generic twin was literally unselectable: picking it snapped
  // straight back to the vendor and `generic_mfg` never reached the save.
  //
  // So the option value is the unique `_id`, and `mfg_id` is resolved from the
  // picked row (see onMfgSelect) — every mfg-keyed read downstream is unchanged.
  const mfgOptions = useMemo(
    () =>
      coilRatingMfgs(coilDoc as CoilCmsDoc).map((m) => ({
        ...m,
        /** legacy `_id` — `"79"` for a catalog row, `"79_1"` for its generic twin. */
        dataId: String((m as any)._id ?? m.id),
        /** legacy `data-generic` — `{{#if generic_mfg}}{{generic_mfg}}{{else}}none{{/if}}`. */
        genericAttr: (m as any).generic_mfg ? String((m as any).generic_mfg) : 'none',
        /** legacy `self.generic_mfg` (js:2795-2799: `data-generic != 'none'`). */
        generic: Boolean((m as any).generic_mfg),
      })),
    [coilDoc]
  );
  const mfgByDataId = useMemo(
    () => new Map(mfgOptions.map((m) => [m.dataId, m])),
    [mfgOptions]
  );
  /** read from callbacks that run outside render (doSelect / onSearchField). */
  const mfgByDataIdRef = useRef(mfgByDataId);
  mfgByDataIdRef.current = mfgByDataId;
  /**
   * The rating_mfg row `state.data_id` points at, or undefined.
   *
   * `data_id` only counts when it AGREES with `mfg_id`. It has to: state can
   * arrive from localStorage written by a build that never updated `data_id`
   * (it sat on the default 27 while the user picked another manufacturer), and
   * trusting it blindly there would move the select to the wrong vendor. When
   * they disagree the caller falls back to `mfg_id`, i.e. the pre-fix behaviour.
   */
  const resolveMfgRow = (st: Record<string, any>) => {
    const row = st.data_id == null ? undefined : mfgByDataIdRef.current.get(String(st.data_id));
    if (!row) return undefined;
    if (st.mfg_id != null && Number(row.id) !== Number(st.mfg_id)) return undefined;
    return row;
  };

  // the board AHU CMS doc — air.air_stream (the coil CMS doc has no `air` key;
  // legacy reads air streams off the tunnel/AHU CMS). mfg/construction_class are
  // on the ahu prop; this query is already cached by the board (no extra fetch).
  // ★ HEADER MODE keeps this read: legacy has no tunnel to read the air streams
  // off (`self.airstream = options.ahu?.tunnels ? … : null`,
  // coil.selector.js:393) and therefore falls back to the shared default AHU CMS
  // model (`if (!self.airstream) { await AHUCms.fetch(); … }`, js:421-424). The
  // `?? 1` fallbacks below resolve to mfg 1 / construction-class 1, i.e. the
  // same default document.
  const ahuMfgId = Number((ahu as any)?.mfg_id ?? (ahu as any)?.manufacturer_id ?? 1);
  const ahuCcId = Number(
    (ahu as any)?.construction_class_id ?? (ahu as any)?.constructionClassId ?? 1
  );
  const { data: ahuDoc } = useGetCmsAhuDocQuery(
    { mfgId: ahuMfgId, constructionClassId: ahuCcId },
    { skip: !ahuMfgId }
  );

  // Sync K (dev 9ab54f1b01) — is this section's would-be heating coil in
  // REHEAT position (a cooling-duty coil earlier on the same air stream)?
  // Dev's _heatingCoilHasUpstreamCooling walks stateStorage's
  // component.coil.model rows + their live section relations; the React
  // equivalent walks the board RTKQ caches the bootstrap already holds for
  // every section/coil (getTunnelSectionList per tunnel, getAhuCoilDetail per
  // coil, getAhuTunnelList for the tunnel-index order key) — synchronous
  // cache reads only, no new requests. Gate reads the OPEN-TIME section
  // snapshot's air stream: the dialog's own Air Stream select is a coil/model
  // field and never wrote model_data.section in legacy either. Header mode
  // has no section (legacy model_data.section = {}) → always false.
  const boardAhuId = Number(ahu?.id) || null;
  const heatingReheat = useAppSelector(
    useMemo(() => {
      const sec = (activate.section ?? null) as ReheatScanSection | null;
      const tunnelIndexes = cmsTunnelIndexes(ahuDoc as ICmsAhuDoc | undefined);
      return (state: RootState): boolean => {
        if (headerMode || !sec || boardAhuId == null) return false;
        const tunnels =
          ahuBoardApi.endpoints.getAhuTunnelList.select(boardAhuId)(state).data ?? [];
        const queries = (state[ahuBoardApi.reducerPath] as {
          queries: Record<string, { endpointName?: string; data?: unknown } | undefined>;
        }).queries;
        const sections: ReheatScanSection[] = [];
        const coils: ReheatScanCoil[] = [];
        Object.values(queries).forEach((q) => {
          if (!q) return;
          if (q.endpointName === 'getTunnelSectionList' && Array.isArray(q.data)) {
            sections.push(...(q.data as ReheatScanSection[]));
          } else if (q.endpointName === 'getAhuCoilDetail' && q.data) {
            coils.push(q.data as ReheatScanCoil);
          }
        });
        // Unlike legacy's page-scoped stateStorage, the RTKQ cache can hold
        // another unit's rows for keepUnusedDataFor after an SPA board
        // switch — scope sections to THIS unit's tunnels (coils then scope
        // themselves through the section join).
        const tunnelIds = new Set(tunnels.map((t) => t.id));
        const unitSections = tunnelIds.size
          ? sections.filter((r) => tunnelIds.has(Number(r.tunnel_id)))
          : sections;
        return heatingCoilHasUpstreamCooling({
          section: sec,
          coils,
          sections: unitSections,
          tunnels,
          tunnelIndexes,
        });
      };
    }, [activate.section, ahuDoc, boardAhuId, headerMode])
  );
  // Ref mirror so setSeasonForDuty (a stable callback) reads the live value.
  const heatingReheatRef = useRef(false);
  heatingReheatRef.current = heatingReheat;

  // ★ The duty list is the MASTER catalog in BOTH modes —
  // `/master/v1.0.0/ahu/coils/duties/mfgs/<mfg>/types/<type>/`
  // (collection/duties.js:8-12), which the dependency cascade already loads
  // into `masterLists.duty_id` (COIL_DEPENDENCY_GRAPH type_id → duty_id;
  // coilSelectorData.ts builds that exact URL).
  //
  // The board mode used to render `/api/v1.0/ahu/coils/<id>/duties/` instead.
  // That route is `MasterAHUCoilDuty.objects.filter(ahucoil__id=pk)`
  // (api/rest_ahu/duty_viewset.py:23) — the duties STORED on that coil row
  // (`ksdm_ahu_component_coil_duty`), not the manufacturer's catalog. It cannot
  // move when the user picks a different Manufacturer, and most coils carry one
  // row or none, which is why Modine offered 1 duty on dev3 and 2 on dev.

  // notes (per-mfg) — URL carries ?component_mfg_id=.
  const mfgForNotes = useRef<number | null>(null);

  // ---- state model (legacy model_data.model + params_coil merged) ----
  const state = useRef<Record<string, unknown>>({});
  const init = useRef<Record<string, unknown>>({});
  const seeded = useRef<number | null>(null);
  /** Bumped by `update_rating_dialog:<id>` to force the seed effect to re-run —
   * see the note at that listener. */
  const [reseedNonce, setReseedNonce] = useState(0);
  // Dev-sync J6 — the STAGED section season (legacy model_data.section
  // ['season_id']): seeded from the section row, overwritten by
  // setSeasonForDuty, persisted ONLY by Select's sectionSave (js:3463).
  const seasonIdRef = useRef<number | null>(null);
  // first header duty auto-pick ≡ legacy's init getCollections (no season
  // re-lock on it — js:1048/:1058 `if (!init)`).
  const dutyAutoPickInit = useRef(true);
  const [, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((v) => v + 1), []);
  /**
   * ★ Bumped by BOTH seed effects (header + board) on the commit that latches
   * `seeded.current = modelId`. Every post-seed effect that must fire exactly
   * ONCE, on that commit, keys on this — never on `detail`/`ratedCoilModel`
   * alone.
   *
   * Why: `getComponentDetail` carries no tags and RTK Query keeps the
   * unsubscribed entry for its default 60 s, so a close-and-reopen inside that
   * minute is served the CACHED row synchronously and the seed is held back by
   * the `gateAt` freshness gate below. When the mount refetch lands it comes
   * back deep-equal to what is already cached, and structuralSharing (on by
   * default) hands back the *same object reference* — so a `[modelId, detail,
   * ratedCoilModel]` dep list never changes after mount. Those effects ran once,
   * at mount, bailed on `seeded.current` (still null, the refetch in flight),
   * and never ran again. The dependency cascade therefore never fired on the
   * second open: every mfg/type-dependent option list stayed empty, so the
   * selects rendered their RAW IDS (DocPathSelect's last-resort option) or, for
   * the ones whose list is the whole option set, went BLANK — the reported
   * "some select boxes are empty after closing and reopening the Rating
   * Dialog". Legacy cannot have this bug: `getCollections(null, null, true)`
   * re-runs the whole init cascade on every dialog construction.
   *
   * `seedTick` changes on exactly the commit the seed lands on, whichever of
   * `detail` / `ratedCoilModel` / `detailFresh` / `ratedFresh` unblocked it.
   * Same defect and same shape of fix as EvapPackSelector.tsx:800-824.
   */
  const [seedTick, setSeedTick] = useState(0);
  const markSeeded = useCallback(() => setSeedTick((t) => t + 1), []);

  // userArmed gate (arms ONLY on a real #rate/#select click).
  const userArmed = useRef(false);
  const { dispatchRating, abortRating, isRating } = useRatingDispatch(
    headerMode ? 'header' : 'board'
  );
  const [postDbRating, { isLoading: isDbRating }] = usePostCoilDatabaseRatingMutation();
  const dbAbort = useRef<{ abort: () => void } | null>(null);
  // in flight on EITHER route — `isRating` is the vendor mutation only, and a
  // DB-rated coil (manufacturer:'manual') never touches it.
  const rateInFlight = isRating || isDbRating;
  // the rated_coil_model write (PATCH ahu/rating_mode_coils/<id>). userArmed only.
  const [patchRatedCoil] = usePatchRatedCoilMutation();

  const [activeTab, setActiveTab] = useState<InputTab>('Properties');

  // rated rows (from a fresh Rate; or the single stored row read-only).
  const [ratedRows, setRatedRows] = useState<Array<Record<string, unknown>> | null>(null);
  const [ratedIndex, setRatedIndex] = useState(0);
  // Render-time mirror of the chosen row so the header-mode `saveInStorage`
  // (legacy reads the synchronous `self.rated_coil_index`) has a default source.
  // Call sites that mutate it in the SAME tick as a save pass an explicit
  // override — see saveInStorage(over). NOTE coil needs NO rated-rows mirror:
  // unlike ewheel/phx its `rate` member is `self.is_clicked_rate` (js:1483), a
  // "the last render was a rated-output render" flag, not `!!self.rated_*`.
  const ratedIndexRef = useRef(0);
  ratedIndexRef.current = ratedIndex;
  const [rateErrors, setRateErrors] = useState<string[]>([]);
  const [rateWarnings, setRateWarnings] = useState<string[]>([]);
  const [apiNotes, setApiNotes] = useState<string[]>([]);
  // doSelect reads the notes out of a ref, not the state: it is a useCallback
  // that deliberately does NOT list the rate results in its deps (same reason
  // ratedRows/ratedIndex are mirrored above), so the state value it closes over
  // can be a rate behind. `self.NNotes` on dev is a plain instance field read
  // at Select time — a ref is the faithful equivalent.
  const apiNotesRef = useRef<string[]>([]);
  apiNotesRef.current = apiNotes;
  const ratedDatetime = useRef<string | null>(null);
  // `#print` — the legacy print SHEET is mounted (into `.print-content` on
  // <body>) while this is true; see doPrint.
  const [printing, setPrinting] = useState(false);
  const printTitle = useRef<string | null>(null);
  // The print sheet's report envelope: the user's report colour and the
  // project's report_co / report_logo / number / elevation (print() reads
  // `user.get('report_color')` and `self.project` — coil.selector.js:4168-4170).
  // The board bootstrap already fetched this project, so the cache is warm.
  const reportColor = useAppSelector((st) => (st.auth.user as { report_color?: string } | null)?.report_color);
  const { data: boardProject } = useGetAhuBoardProjectQuery(
    (ahu as { project_id?: number } | null | undefined)?.project_id ?? skipToken
  );
  // `/api/v1.0/projects/<id>` returns the whole row (rest_project/serializers
  // .py:228-250) — IAhuBoardProject narrows it but keeps an index signature.
  const proj = (boardProject ?? {}) as Record<string, unknown>;
  const projectName = (proj.name as string | undefined) ?? undefined;
  const projectNumber = (proj.number as string | undefined) ?? undefined;
  const projectElevation = proj.elevation;
  const reportCo = proj.report_co as string | undefined;
  const reportLogo = proj.report_logo as string | undefined;
  const ahuTag = (ahu as { tag?: string } | null | undefined)?.tag;
  // which rate path produced the current rows (false = DB route, true = vendor).
  const externalRating = useRef<boolean | undefined>(undefined);
  // output tab (coil.rated.outputs.tmpl:1-18 — Coil / Performance / Notes,
  // Coil active by default). NOTE the legacy `.coil-counts` div is rendered
  // EMPTY and nothing ever fills it (tmpl:33, no `rated_coils_more` in the
  // render_outputs context) — the coil pick is the summary row click, not a
  // numeric pager, so there is no pager state here.
  const [outputTab, setOutputTab] = useState<OutputTab>('coil');

  // draw popup.

  // ---- 5e-sync (KA-5259) — coil AW support state ----
  // the coil CMS mfg-settings row for the current duty (min_clear_top /
  // coil_to_coil_vertical_clear — the client-side min_aw_height recompute
  // inputs; legacy CmsSettings collection, coil.selector.js:879 →
  // /cms/1/v1.0.0/ahu/coils/mfg/settings/<duty>s/). Read-only GET.
  const [coilCmsAw, setCoilCmsAw] = useState<CoilCmsSettingsAw | null>(null);
  const coilCmsAwDuty = useRef<string | null>(null);
  // the LIVE section AW pair for the "Available Airway W x H" inputs (legacy
  // sectionSizesChanged re-fetches the section — the backend aw_width/aw_height
  // properties account for liners the client approximation doesn't). Warnings
  // deliberately keep the OPEN-TIME snapshot (legacy model_data.section).
  const [sectionAw, setSectionAw] = useState<{ aw_width?: unknown; aw_height?: unknown } | null>(null);

  // duty machine value ('cooling'/'heating'/… — the duty <option> data-value;
  // falls back 'cooling' like coil.selector.js:877).
  // ★ Resolved off the MASTER duty list the cascade loaded, in both modes —
  // legacy resolves the machine value off exactly that collection
  // (`self.collections.duties.find(…).item.value`, coil.selector.js:1260/:1926).
  // Off the per-coil stored list this returned the 'cooling' fallback whenever
  // the coil had no stored duty row, which then picked the wrong
  // `/coils/mfg/settings/<duty>s/` document and the wrong vendor `duty` key.
  // ★ The row must be matched on `item.id`, NOT on the row's own `id`. The duty
  // endpoint returns the join rows `{id: 5, item: {id: 1, …}}` — the outer `id`
  // is the join-table pk, the duty id lives on `item`. `d.id ?? d.item?.id`
  // therefore compared 5/6 against the coil's duty_id 1/2, never matched, and
  // every caller silently took the 'cooling' fallback — on a HEATING coil too.
  // Legacy has no such ambiguity: getDuty reads `duties[i].item.id`
  // (coil.selector.js:2118) and `duties[i].item.value` (:2119). Use the same
  // COIL_OPTION_KEYS-driven unwrap the duty <select> itself already uses, so the
  // two can never disagree again.
  /**
   * ★ The bare lookup, WITHOUT the 'cooling' fallback below.
   *
   * Legacy's getDuty (coil.selector.js:2113-2123) simply RETURNS when no row
   * matches, leaving `self.duty` undefined — so the Season default it feeds
   * (:1695-1697) resolves to null and the select shows the `- -` placeholder.
   * The 'cooling' fallback is a React-side invention for the callers that need
   * a duty string for a URL or a vendor payload; letting it reach the Season
   * default would render "Summer" on a coil whose duty resolves to nothing,
   * where dev renders `- -`.
   */
  const resolveDutyValueOrNull = useCallback((): string | null => {
    const rows = (masterListsRef.current.duty_id ?? []) as Array<Record<string, any>>;
    const row = rows.find(
      (d: any) => String(coilOptionId(d, 'duty_id')) === String(state.current.duty_id)
    );
    const v = row ? coilOptionValue(row, 'duty_id') : undefined;
    return v != null ? String(v) : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resolveDutyValue = useCallback(
    (): string => resolveDutyValueOrNull() ?? 'cooling',
    [resolveDutyValueOrNull]
  );

  // fetch the per-duty CMS settings once per duty (re-runs each render; the
  // duty ref gates the network call).
  useEffect(() => {
    if (seeded.current !== modelId) return;
    const duty = resolveDutyValue();
    if (coilCmsAwDuty.current === duty) return;
    coilCmsAwDuty.current = duty;
    void fetch(`/api/v1.0/cms/1/v1.0.0/ahu/coils/mfg/settings/${duty}s/`, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => {
        if (!body) return;
        // 5e-sync verifier fix (2026-07-19): the endpoint returns the settings
        // row as a BARE OBJECT (no array, no results wrapper) — legacy's
        // Backbone.Collection.fetch wraps a dict body into one model, so
        // `toJSON()[0]` IS the settings dict (coil.selector.js:1037). The
        // array/results-only parse dropped it → coilCmsAw stayed null → the
        // updateRequiredAwHeights recompute never ran (observed live on dev3:
        // REQUIRED AW H frozen across h_from_floor/mounting_angle changes).
        const rows = Array.isArray(body)
          ? body
          : ((body as any)?.results ?? (typeof body === 'object' ? [body] : []));
        setCoilCmsAw((rows[0] as CoilCmsSettingsAw) ?? null);
      })
      .catch(() => undefined);
  });

  // seed + live-refetch the section AW (coil.selector.js:655-688 — listens
  // changed:tunnel_<id>:sizes + changed:secrion_<id>:casing_insulation_id
  // [typo load-bearing] → section.fetch() → update the aw inputs).
  useEffect(() => {
    const sec = activate.section as
      | { id?: number; tunnel_id?: number; aw_width?: unknown; aw_height?: unknown }
      | undefined;
    if (!sec) return undefined;
    setSectionAw({ aw_width: sec.aw_width, aw_height: sec.aw_height });
    const refetch = () => {
      if (!sec.id) return;
      void fetch(`/api/v1.0/ahu/sections/${sec.id}`, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((row) => {
          if (row) setSectionAw({ aw_width: row.aw_width, aw_height: row.aw_height });
        })
        .catch(() => undefined);
    };
    const offs: Array<() => void> = [];
    // legacy registers both listeners inside the tunnel_id guard (js:655).
    if (sec.tunnel_id != null) {
      offs.push(bus.on(`changed:tunnel_${sec.tunnel_id}:sizes` as any, refetch));
      if (sec.id != null) {
        offs.push(
          bus.on(`changed:secrion_${sec.id}:casing_insulation_id` as any, refetch)
        );
      }
    }
    return () => offs.forEach((off) => off());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activate, bus]);

  // master param lists from the dependency graph (field → option rows).
  const [masterLists, setMasterLists] = useState<Record<string, OptionRow[]>>({});
  const masterListsRef = useRef<Record<string, OptionRow[]>>({});
  masterListsRef.current = masterLists;

  // ---- ★ Phase 6.5 — HEADER-MODE STANDALONE SEED (coil.selector.js:508-529) --
  // The header host has no current_model, so nothing is fetched: the seed is
  // `coilDefaults` + the `options.header` override (HEADER_COIL_DEFAULTS) merged
  // with the `last:true` entry of `localStorage.coil_independent_ratings`.
  //
  // ★ COIL SEEDS FROM `model_data`, NOT `state` — its `state.set(model.state)`
  // is COMMENTED OUT at js:518, and correspondingly its writer has NO `state`
  // member (js:1482). Legacy does `self.model_data = {...model.model_data}` and
  // `self.defaultParams = model.model_data.params_coil` (js:514-517). React's
  // flat state is the merge of legacy's `model_data.model` (perf/component
  // fields) and `model_data.params_coil` (the master/search selection), plus the
  // `model_data.section` eadb/eawb pair the template renders.
  /** legacy `self.storage_saved_data` — the parsed saved-inputs map. */
  const storageMapRef = useRef<IndependentRatingMap>({});
  /** legacy `self.mfg_id` — NOT the select value: it is the selected Manufacturer
   * option's `data-id` attribute (js:2790) and it is what KEYS the saved entry
   * (js:1481 `self.mfg_id || self.model_data.params_coil.mfg_id`). `data-id` is
   * the rating_mfg row's `_id`, which equals the catalog id for a normal vendor
   * but is `"<id>_1"` for a private-label GENERIC twin — so the two get their
   * own storage slots, exactly as on dev. Tracked as `state.data_id`. */
  const coilMfgIdRef = useRef<unknown>(null);
  /** legacy `self.rated_coil_index` / `self.saved_rated_coil_index` (js:521, 2942). */
  const savedRatedIndex = useRef<number | null>(null);
  /** legacy `self.needToRate` (js:520) + the `findFromStoraga` restore (js:2387),
   * folded into one pending slot. `applyState` distinguishes the mfg-switch
   * restore (whose model_data must still be applied) from the construction seed
   * (already applied). */
  const pendingRestore = useRef<{
    entry: IndependentRatingEntry;
    applyState: boolean;
  } | null>(null);

  /** `{...model_data.model, ...model_data.params_coil}` + the section pair — the
   * inverse of the writer below (see saveInStorage). */
  const stateFromEntry = useCallback((entry: IndependentRatingEntry): Record<string, unknown> => {
    const md = (entry.model_data ?? {}) as Record<string, any>;
    const sec = (md.section ?? {}) as Record<string, unknown>;
    const s: Record<string, unknown> = { ...(md.model ?? {}), ...(md.params_coil ?? {}) };
    // js:527-528 — the section eadb/eawb pair only falls back to
    // defaults_depends_on_duty when the SAVED section pair is empty.
    if (sec.eadb_temp != null) s.eadb_temp = sec.eadb_temp;
    if (sec.eawb_temp != null) s.eawb_temp = sec.eawb_temp;
    return s;
  }, []);

  useEffect(() => {
    if (!headerMode) return;
    if (seeded.current === modelId) return;
    const map = readRatingMap(RATING_STORAGE_KEYS.coil);
    const entry = findLastEntry(map);
    const s: Record<string, unknown> = { ...HEADER_COIL_DEFAULTS };
    if (entry) {
      Object.assign(s, stateFromEntry(entry));
      // js:522 `self.mfg_id = model.mfg_id` (undefined when the legacy writer
      // dropped it — JSON.stringify omits an undefined member; the `||` at
      // js:1481 then falls back to params_coil.mfg_id, mirrored below).
      coilMfgIdRef.current = entry.mfg_id ?? s.data_id ?? s.mfg_id ?? null;
      savedRatedIndex.current = (entry.rated_coil_index as number | null) ?? null;
      // ★ THE AUTO-RE-FIRE. Legacy has TWO paths and they are NOT gated the same
      // way: (a) `self.needToRate = model.rate` (js:520) → `render(idx,
      // needToRate)` → `$('#rate.all-rate[data-type="coil"]').click()`
      // (js:1557); and (b) `findFromStoraga()` — reached from the INIT cascade
      // `getCollections(null, null, true)` (js:609 → :1188/:1220/:1225/:1230/
      // :1236 `if (search) self.findFromStoraga()`) — which clicks `#rate` after
      // 1 s for ANY existing entry, NOT only `rate:true` (the `if (model.rate)`
      // at js:2397 gates only the preloader + the immediate re-render, while the
      // setTimeout click at js:2402-2406 sits OUTSIDE it). Path (b) subsumes (a),
      // so the observable rule is "an entry exists ⇒ the rate re-fires".
      // Mirrored as-is; do NOT narrow it to `entry.rate`.
      pendingRestore.current = { entry, applyState: false };
    } else {
      coilMfgIdRef.current = s.data_id ?? s.mfg_id ?? null;
    }
    // js:525-528 — the eadb/eawb section defaults for the seeded type+duty. The
    // key is `${type_id}_${duty_id || 1}`; only applied when the pair is empty.
    const dutyDefaults =
      DEFAULTS_DEPENDS_ON_DUTY[`${s.type_id}_${s.duty_id || 1}`] ?? {};
    if (s.eadb_temp == null && dutyDefaults.eadb_temp != null) s.eadb_temp = dutyDefaults.eadb_temp;
    if (s.eawb_temp == null && dutyDefaults.eawb_temp != null) s.eawb_temp = dutyDefaults.eawb_temp;

    storageMapRef.current = map;
    // the no-elem half of calcAirflowAndFaceVelocity (js:1862) — it runs in
    // header mode too.
    Object.assign(s, coilFaceVelocityFromAirflow(s));
    state.current = s;
    init.current = { ...s };
    mfgForNotes.current = Number(s.mfg_id) || null;
    seeded.current = modelId;
    bump();
    markSeeded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerMode, modelId]);

  /**
   * `saveInStorage` (coil.selector.js:1477-1489). Guarded exactly like legacy:
   * `if (!self.header) return;` — note coil (unlike fan/ewheel/phx) does NOT
   * additionally require `self.storage_saved_data`.
   *
   * ★ MEMBER ORDER is byte-parity with js:1481-1487 —
   *   rate, last, model_data, rated_coil_index, mfg_id
   * with NO `state` member (js:1482 has it commented out) and WITH an explicit
   * `mfg_id` (which fan omits). JSON.stringify preserves insertion order.
   *
   * ★ `rate: self.is_clicked_rate` is TRUE only when the save happens from the
   * rated-output render (`render_outputs` sets it at js:1317 and calls
   * saveInStorage at js:1475), and FALSE after any plain `render()` (js:1494).
   * So it means "the last thing that happened was a successful rate render" —
   * the same semantics as fan's `rate: !!self.rated_fans`.
   *
   * ★ The entry KEY is `self.mfg_id || self.model_data.params_coil.mfg_id`
   * (js:1481): a field edit before any Manufacturer is resolved writes the
   * literal key `"undefined"`/`"null"`. Mirrored verbatim rather than "fixed".
   *
   * ★ ONE DELIBERATE, PROVABLY NON-OBSERVABLE BYTE DIFFERENCE: legacy's
   * `self.mfg_id` is only assigned when the user actually picks a Manufacturer
   * (js:2523) or when a saved entry carried one (js:522), so a FIRST-EVER write
   * emits `mfg_id: undefined` — which JSON.stringify DROPS, leaving
   * `{rate, last, model_data, rated_coil_index}`. We seed the ref from the header
   * default (mfg 27) instead, so the member is present as `27`. Every legacy read
   * path resolves identically because both the map key and the `||` fallback at
   * js:1481 / js:2392 land on `params_coil.mfg_id`, which IS 27.
   */
  const saveInStorage = useCallback(
    (over?: { rate?: boolean; ratedIndex?: number | null }) => {
      if (!headerMode) return;
      const flat = JSON.parse(JSON.stringify(state.current)) as Record<string, unknown>;
      storageMapRef.current = writeRatingEntry(
        RATING_STORAGE_KEYS.coil,
        coilMfgIdRef.current ?? state.current.mfg_id,
        {
          rate: over?.rate ?? false,
          last: true,
          // legacy stores the WHOLE model_data ({model, params_coil, section,
          // project} — js:449-454). The legacy READER spreads `model_data` back
          // wholesale and reads `.params_coil` / `.section` off it (js:514-528),
          // so emit those four members; React's flat state is the merge of the
          // first two (see stateFromEntry, the exact inverse).
          model_data: {
            model: flat,
            params_coil: flat,
            // header mode: `self.section` is undefined ⇒ `model_data.section`
            // starts as `{}` (js:452) and only ever carries the eadb/eawb pair
            // (js:527-528).
            section: { eadb_temp: flat.eadb_temp, eawb_temp: flat.eawb_temp },
            // `self.project ? self.project.toJSON() : {}` (js:453) ⇒ `{}`.
            project: {},
          },
          rated_coil_index:
            over && 'ratedIndex' in over ? (over.ratedIndex ?? 0) : ratedIndexRef.current,
          mfg_id: coilMfgIdRef.current ?? null,
        } as IndependentRatingEntry,
        storageMapRef.current
      );
    },
    [headerMode]
  );

  // ---- seed state once GET resolves (BOARD host) ----
  useEffect(() => {
    if (headerMode) return;
    if (!detail) return;
    if (seeded.current === modelId) return;
    // ★ the reopen gate (see `gateAt` above). The seed LATCHES, so it must not
    // run against a row that predates this open — on a reopen straight after a
    // Select that row is the pre-select one and the output pane comes up empty.
    if (!detailFresh || !ratedFresh) return;
    const m = detail as CoilModel;
    // A rated coil ALWAYS has a rating_mode_coils row carrying rated-only fields
    // (casing_mtl_id / casing_type_id / conn_* etc. that may be OUT of the saved
    // catalog list). Seeding off `detail` alone — before ratedCoilModel resolves —
    // permanently drops those fields (the guard below blocks a re-seed). So defer
    // the seed until the rated row is in hand for a rated coil.
    if (m.capacity_total != null && ratedCoilModel == null) return;
    const masterCoil = (m.master_coil ?? {}) as Record<string, unknown>;
    const mp = parseParams((masterCoil as any).coil_params ?? (m as any).coil_params);
    const rated = (ratedCoilModel ?? {}) as Record<string, unknown>;

    // params_coil = the rated master coil's params (the search-field selection).
    // legacy seeds params_coil from rated_coil_model.toJSON() ?? defaultParams.
    const s: Record<string, unknown> = { ...masterCoil, ...rated };
    // copy the model perf fields off the saved coil component row.
    for (const k of Object.keys(m)) {
      if (m[k] != null && k !== 'master_coil') s[k] = m[k];
    }
    // params JSON ids that aren't columns (tube_wall_thick_id from rated, etc.).
    for (const k of Object.keys(mp)) {
      if (mp[k] != null && s[k] == null) s[k] = mp[k];
    }
    // mfg/type/duty seed from the rated master coil + the component row.
    s.mfg_id = (rated as any).mfg_id ?? (masterCoil as any).mfg_id ?? mp.mfg_id;
    // ★ `data_id` — WHICH Manufacturer option is selected, not which catalog
    // mfg. `render` recomputes it on every pass from the SAVED `generic_mfg`
    // (coil.selector.js:1676-1682: `generic_mfg ? master_coil.mfg_id + '_1' :
    // master_coil.mfg_id`, board mode only) so reopening a private-labelled
    // coil comes back up on its generic option rather than on the vendor.
    // Recomputed here rather than trusted from `mp`/`rated` for the same
    // reason legacy recomputes it: `self.data_id` is only ever set by a user
    // pick within the session.
    s.data_id =
      s.mfg_id == null
        ? null
        : (m as any).generic_mfg
          ? `${s.mfg_id}_1`
          : String(s.mfg_id);
    s.type_id = (rated as any).type_id ?? (masterCoil as any).type_id ?? m.type_id;
    s.duty_id = m.duty_id ?? (rated as any).duty_id;
    // fh/fl + per-coil airflow (legacy calculatePerCoil seeds *_total too).
    s.fh = (masterCoil as any).fh ?? mp.fh ?? (rated as any).fh;
    s.fl = (masterCoil as any).fl ?? mp.fl ?? (rated as any).fl;
    s.qty_stacked = Number(m.qty_stacked) ? m.qty_stacked : 1;
    // ★ the DB column `airflow_actual` holds the ALL-COILS total (the Select
    // write is `airflow_actual: model.airflow_actual_total`, js:3599), so the
    // open-time split is total → box, total ÷ qty → per-coil box
    // (js:568-578 `calculatePerCoil`). Only the total half was seeded, so the
    // generic key-copy above left the per-coil box showing the TOTAL on every
    // stacked (qty > 1) coil, and the derived face velocity with it.
    if (m.airflow_actual != null) {
      s.airflow_actual_total = m.airflow_actual;
      s.airflow_actual = Number(m.airflow_actual) / (Number(s.qty_stacked) || 1);
    }
    // section eadb/eawb seed (the selector edits these → section.set).
    const sec = (activate.section ?? {}) as Record<string, unknown>;
    s.eadb_temp = sec.eadb_temp;
    s.eawb_temp = sec.eawb_temp;
    // Sync K (dev 9ab54f1b01) — a heating-duty coil is rated against Winter
    // by default, so the bare pair everything downstream reads (the EADB/EAWB
    // inputs, rate inputs, Select save) resolves from the section's _winter
    // columns here — once, at seed — rather than every read site knowing
    // about the _winter pair. Skipped when the section's Season is Summer
    // specifically (season_id 1): there the bare pair already holds the rated
    // value directly (AHUCoil._heating_season_is_summer) and must not be
    // overwritten. Only backfills a FALSY bare value — dev gates on
    // `!section.eadb_temp` since backend 31e3a510f8 mirrors bare→winter on
    // every winter-rated heating save (the pairs agree; this is the legacy-
    // row backfill). Gate keys on the SECTION row's season_id, not the ref.
    if (m.duty_id === 2 && (sec.season_id as number | null | undefined) !== 1) {
      if (sec.eadb_temp_winter != null && !s.eadb_temp) s.eadb_temp = sec.eadb_temp_winter;
      if (sec.eawb_temp_winter != null && !s.eawb_temp) s.eawb_temp = sec.eawb_temp_winter;
    }
    // J6 — the staged season seeds from the section row (legacy
    // model_data.section = the section snapshot).
    seasonIdRef.current = (sec.season_id as number | null | undefined) ?? null;

    // Dev-sync J5 — the chart's pending re-rate handoff (coil.selector.js:
    // 458-620, applied LAST so it overrides every seed above). Coil-specific
    // mapping: winter keys copy into the BARE pair (:558-563 — the coil
    // dialog's single entering pair shows whichever season the re-rate is
    // for); ladb/lawb become the DESIRED leaving inputs (:574-581) and are
    // removed from the entering set; the key clears only on a gate match.
    const pendingFields = takeSectionPendingRerate(sec.id as number | undefined);
    if (pendingFields) {
      if (pendingFields.eadb_temp_winter !== undefined) {
        pendingFields.eadb_temp = pendingFields.eadb_temp_winter;
        pendingFields.eawb_temp = pendingFields.eawb_temp_winter;
      }
      if (pendingFields.ladb_temp !== undefined) {
        // Selector-parity LADB/LAWB fix: the dialog's visible LADB/LAWB inputs
        // are now `name="desired_ladb_temp"`/`desired_lawb_temp` (matching legacy
        // coil.selector.tmpl:646/:653 which bind model.desired_ladb_temp) — the
        // exact keys all three rate builders read. Set desired_* (what the input
        // + the rate now consume); s.ladb_temp is kept too as a harmless mirror.
        s.desired_ladb_temp = pendingFields.ladb_temp;
        s.ladb_temp = pendingFields.ladb_temp;
        delete pendingFields.ladb_temp;
      }
      if (pendingFields.lawb_temp !== undefined) {
        s.desired_lawb_temp = pendingFields.lawb_temp;
        s.lawb_temp = pendingFields.lawb_temp;
        delete pendingFields.lawb_temp;
      }
      Object.assign(s, pendingFields);
      // legacy js:604-618: is_rated=false + changed=true (the stored rating
      // is stale against the fresh entering) + land on Performance.
      setActiveTab('Performance');
    }

    // the no-elem half of calcAirflowAndFaceVelocity, which legacy runs at the
    // end of EVERY render including the first (js:1862).
    Object.assign(s, coilFaceVelocityFromAirflow(s));

    state.current = s;
    init.current = { ...s };
    mfgForNotes.current = Number(s.mfg_id) || null;
    seeded.current = modelId;
    // render the STORED rated row read-only when the coil is rated
    // (capacity_total IS NOT NULL — dev3 rated-state detection per the spec).
    // J5: NOT after a pending re-rate — legacy flags is_rated=false/changed
    // so the stale outputs don't render against the fresh entering.
    if (m.capacity_total != null && !pendingFields) {
      setRatedRows([{ ...rated, ...masterCoil, ...m } as Record<string, unknown>]);
      ratedDatetime.current = ((rated as any).rating_datetime as string) ?? null;
    } else {
      // A RE-seed (`update_rating_dialog`) can land on a coil that is no longer
      // rated — the sizer's APPLY runs `update_coil`, which NULLs
      // `capacity_total` and the whole rating-mode row. `setRatedRows` is
      // otherwise write-only, so the stale output pane would survive a re-seed
      // that says the coil is unrated. Legacy reaches the same end state
      // through `self.changed = true` (js:2702). On the FIRST seed both are
      // already null, so this is a no-op there.
      setRatedRows(null);
      ratedDatetime.current = null;
    }
    bump();
    markSeeded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail, ratedCoilModel, modelId, activate, bump, headerMode, detailFresh, ratedFresh, reseedNonce]);

  // notes (per-mfg) — only after mfg known.
  const notesUrl = mfgForNotes.current
    ? `/api/v1.0/cms/1/v1.0.0/ahu/coils/notes/?component_mfg_id=${mfgForNotes.current}`
    : '/api/v1.0/cms/1/v1.0.0/ahu/coils/notes/?component_mfg_id=0';
  const { data: coilNotes } = useGetCoilNotesQuery(notesUrl, {
    skip: !mfgForNotes.current,
  });

  // ---- DEPENDENCY ENGINE (the mfg/type/duty cascade) ----
  const parentsOf = useCallback((): CoilParents => {
    const s = state.current;
    return {
      mfg_id: s.mfg_id as any,
      type_id: s.type_id as any,
      duty_id: s.duty_id as any,
      tube_dia_id: s.tube_dia_id as any,
      tube_mtl_id: s.tube_mtl_id as any,
      fin_mtl_id: s.fin_mtl_id as any,
      fin_pattern_id: s.fin_pattern_id as any,
      // Selector-parity — Manifold piping_ends filters by connection material.
      conn_mtl_id: s.conn_mtl_id as any,
    };
  }, []);

  const loadParams = useCallback<LoadParamsFn>(
    async (field) => {
      const url = coilCollectionUrl(field, parentsOf());
      if (!url) return masterListsRef.current[field] ?? [];
      try {
        const res = await fetch(url, {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!res.ok) return masterListsRef.current[field] ?? [];
        const body = await res.json();
        const rows: OptionRow[] = Array.isArray(body) ? body : ((body as any)?.results ?? []);
        setMasterLists((prev) => ({ ...prev, [field]: rows }));
        // ids for the engine: the wrapped {item:{id}} or flat id.
        return rows.map((r: any) => ({ ...r, id: coilOptionId(r, field) }));
      } catch {
        return masterListsRef.current[field] ?? [];
      }
    },
    [parentsOf]
  );

  const getValue = useCallback((field: string) => state.current[field] as any, []);

  /** legacy `self.changeMFG` (coil.selector.js:2802 / :2819) — set on a
   * Manufacturer pick, cleared by any other field change. Only the fin-pattern
   * re-pick reads it. */
  const mfgChangedRef = useRef(false);
  /** mfg-190 (SGS Refrigeration) render-time auto-default "touched" tracking —
   * legacy's changeCasingMTL / changeTubeDia / changeHTFluid(unread, see below)
   * / changeTubeWallThick (coil.selector.js:2709-2760/:2802-2817). See the
   * effect below (after the casing_mtl_defaults re-pick) for the full mapping. */
  const sgs190TouchedRef = useRef({
    casingMtl: false, tubeDia: false, htfluid: false, tubeWallThick: false,
  });
  /** legacy's `init` argument (`getCollections(null, null, true)`, js:609). The
   * dialog-open cascade must NOT run changeDefaultDependsOnTypeAndDuty /
   * setSeasonForDuty when it auto-picks a type or duty (js:916, :1111, :1120-21
   * are all `if (!init)`); a cascade started by a real user change must. */
  const cascadeInit = useRef(true);
  /** set by pickFallback when the duty list narrowed to a SINGLE row, so
   * setStateValue can take legacy's single-duty branch (js:1106-1111) rather
   * than the not-in-list fallback (js:1116-1122). */
  const dutySoleOptionRef = useRef(false);
  /** setSeasonForDuty is declared below (it needs the CMS doc); the cascade
   * re-pick above it reaches it through this ref. */
  const setSeasonForDutyRef = useRef<(duty: unknown) => void>(() => undefined);

  const setStateValue = useCallback(
    (field: string, value: any) => {
      state.current = { ...state.current, [field]: value };
      // ★ getCollections does not just re-pick the type / duty — it follows the
      // re-pick with the default-temperature block and (for a duty) the season
      // re-lock: js:916 `if (!init) changeDefaultDependsOnTypeAndDuty(true,
      // collection_options.type_id)`, js:1120-1121 the same with `false,
      // duty_id` plus `setSeasonForDuty`. Without this, switching to a
      // manufacturer that drops the current type left the temperature inputs on
      // the OLD type's defaults.
      if (!cascadeInit.current && value != null) {
        if (field === 'type_id') {
          state.current = { ...state.current, ...coilTypeDutyDefaultsPatch(true, value, state.current) };
        } else if (field === 'duty_id') {
          // ★ js:1105-1122 — the three duty branches are deliberately
          // asymmetric. When the narrowed list holds exactly ONE duty
          // (:1106-1111) legacy forces it and re-locks the season but does NOT
          // run changeDefaultDependsOnTypeAndDuty; only the "current duty is not
          // in the list" fallback (:1116-1122) does. On dev3's catalog every
          // non-Fluid (mfg, type) pair except Modine/Condenser has exactly one
          // duty, so this is the COMMON path — running the defaults here wiped
          // entering air and fluid conditions the user had typed whenever they
          // switched manufacturer on an evaporator/steam/condenser coil.
          if (!dutySoleOptionRef.current) {
            state.current = { ...state.current, ...coilTypeDutyDefaultsPatch(false, value, state.current) };
          }
          setSeasonForDutyRef.current(value);
        }
      }
      bump();
    },
    [bump]
  );

  const pickFallback = useCallback(
    (field: string, ids: Array<number | string>, current: number | string | null | undefined) => {
      // applyNewValues calls this immediately before setValue, so this is the
      // one place that still knows how long the list the re-pick came from was.
      if (field === 'duty_id') dutySoleOptionRef.current = ids.length === 1;
      return coilPickFallback(field, ids, current, {
        state: state.current,
        mfgChanged: mfgChangedRef.current,
      });
    },
    []
  );

  /**
   * ★ A select that renders a leading `- -` must be allowed to STAY on it.
   *
   * `applyNewValues` (useDependencyGraph.ts:272-284) ends with the
   * DependenciesHandler rule "an out-of-range child goes null unless the new
   * list has exactly one entry, in which case take it". `coilPickFallback`
   * returns `undefined` for every `COIL_NONE_OPTION_FIELDS` member precisely so
   * the field lands on the dash — but `undefined` means "defer to the built-in
   * rule", and the built-in's sole-option arm then fires on a NULL value and
   * pins the one row. `shouldSetDefaultIfNull` is the engine's own opt-out for
   * exactly that, and coil never passed one (it defaulted to `() => true`).
   *
   * Dev cannot do this: those selects carry an explicit
   * `<option value="None" {{#unless params_coil.<field>}}selected{{/unless}}>- -`
   * (e.g. coil.selector.tmpl:1665 for Tube Cleanable) and `getCollections`
   * re-picks none of them — grep the view for `params_coil.tube_cleanable_id =`
   * / `.circuiting_id =` / `.rows_id =` / `.vent_drain_id =` and there is no
   * assignment. A null stays null until the user picks something.
   *
   * What it cost: `tube_cleanables` has exactly ONE row (`cleanable_opp_end`)
   * for mfg 21 / 27 / 143 / 181 on a fluid coil — and 27 (RAE) is the dialog's
   * DEFAULT manufacturer — so simply opening a fluid coil pinned
   * `tube_cleanable_id: 1`. It then survived a switch to Modine (whose list
   * carries all three), and Modine's handler folds `tube_cleanable` into the
   * `Duty` enum: `'--'` → `cooling` (5), `cleanable_opp_end` →
   * `cooling_cleanable_opp_end` (3)
   * (integrated_parts/coil/modine/handle_call.py:78-83 + mapping.py:1645-1654).
   * Duty 3 is the cleanable-tube product line, so the rate came back with no
   * models. Same shape for `circuiting_id` and the header/connection diameters
   * on mfg 190 / type 2, whose lists are also single-row.
   *
   * A non-null value is untouched: the `current != null` arm still clears a
   * value the narrowed list no longer carries, which is what the legacy
   * template does anyway (an unmatched select falls back to its first option,
   * the dash).
   */
  const shouldSetDefaultIfNull = useCallback(
    (field: string) => !COIL_NONE_OPTION_FIELDS.has(field),
    []
  );

  const graph = useDependencyGraph({
    spec: COIL_DEPENDENCY_GRAPH,
    loadParams,
    getValue,
    setValue: setStateValue,
    pickFallback,
    shouldSetDefaultIfNull,
  });

  // mount cascade: once seeded, populate option lists filtered by the saved
  // mfg/type so the dependent selects render LABELS (not raw ids). applyNewValues
  // keeps the saved value (always in its own list) → never mutates the model.
  //
  // ★ `ratedCoilModel` MUST stay in the dep array. For a RATED coil the board
  // seed above defers until the rated row lands (`if (m.capacity_total != null &&
  // ratedCoilModel == null) return;`), so `seeded.current` is assigned on the
  // commit driven by `ratedCoilModel` — NOT by `detail`. With `[modelId, detail]`
  // alone this effect never re-ran after that commit, so the cascade never fired
  // and casing_mtl_id / casing_type_id / conn_mtl_id / header_mtl_id /
  // tube_interior_id / type_id / vent_drain_id rendered their RAW IDs with a
  // 1-entry option list (verified: 7 selects, board coil dialog on AHU 3832).
  // The `cascadeFired` ref keeps it single-shot regardless of dep churn.
  //
  // ★★ …and `seedTick` MUST be in it too, for the same reason one level down.
  // On a REOPEN inside RTK Query's 60 s window neither `detail` nor
  // `ratedCoilModel` changes identity at all (cached row + deep-equal refetch +
  // structuralSharing), so the commit that finally seeds is driven by
  // `detailFresh`/`ratedFresh` — invisible here. This effect stayed on the bail
  // it took at mount and the cascade never fired: 17 of the dialog's 20 selects
  // came back up with a 1-option list showing a raw id, and duty_id / htfluid_id
  // with NO options at all (measured on AHU 6614 / coil 24271: open #1
  // "Cooling"/"Water"/"5/8"/"Copper"/"Mill", open #2 blank/blank/4/4241/1).
  // That is the reported bug. See `seedTick`.
  const cascadeFired = useRef(false);
  useEffect(() => {
    if (cascadeFired.current) return;
    if (seeded.current !== modelId) return;
    if (state.current.mfg_id == null) return;
    cascadeFired.current = true;
    graph.onParentChange('mfg_id');
    if (state.current.type_id != null) graph.onParentChange('type_id');
    if (state.current.tube_dia_id != null) graph.onParentChange('tube_dia_id');
    if (state.current.duty_id != null) graph.onParentChange('duty_id');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, seedTick, detail, ratedCoilModel]);

  // ★ BUG 3-REDUX (vendor-rate codes-not-labels): eagerly load the ENUM master
  // lists for the vendor-rate enum fields the moment the dialog opens on a saved
  // coil. The dependency cascade only narrows lists lazily off a real parent
  // change; on a clean open→Rate the enum lists (tube_dia/tube_mtl/fin_mtl/
  // fin_finish/fin_pattern + the row/fpi/thick/circuiting sets) may never be
  // fetched, so collectRateState's coilOptionValue had no master row to resolve
  // the CODE and the body leaked the saved LABEL ("5/8"/"Copper"/"Waffle") →
  // float("5/8") ValueError at the RAE vendor. coil_params carries LABELS too
  // (mfg-side strings), so option (b) won't help; the codes live ONLY on the
  // master row `.value`. Fetch each list once (filtered by the saved
  // mfg/type/tube_dia/tube_mtl/fin_mtl) and commit it to masterLists so
  // coilOptionValue resolves CODES on the normal flow. Pure read; never mutates
  // the model (we only setMasterLists — the same store loadParams writes).
  //
  // ★ `seedTick` in the deps for the same reopen reason as the cascade above —
  // this one is the second half of the empty-select report: it is what puts
  // rows_id / fpi_id / tube_wall_thick_id / fin_thick_id / circuiting_id in
  // `masterLists` at all, and a Rate fired without them leaks LABELS into the
  // vendor payload (see BUG 3-REDUX). So on the old dep list a reopened dialog
  // did not merely LOOK wrong, it would have rated wrong.
  const enumListsFetched = useRef<number | null>(null);
  /** how many of those eager list GETs are still outstanding (the `#rate` gate). */
  const [enumFetchPending, setEnumFetchPending] = useState(0);
  useEffect(() => {
    if (seeded.current !== modelId) return;
    if (enumListsFetched.current === modelId) return;
    if (state.current.mfg_id == null || state.current.type_id == null) return;
    enumListsFetched.current = modelId;
    const p = parentsOf();
    // the vendor-rate enum fields collectRateState resolves to `.value` codes.
    const fields = [
      'tube_dia_id', 'tube_mtl_id', 'fin_mtl_id', 'fin_finish_id', 'fin_pattern_id',
      'rows_id', 'fpi_id', 'tube_wall_thick_id', 'fin_thick_id', 'circuiting_id',
    ];
    for (const field of fields) {
      if (masterListsRef.current[field]?.length) continue;
      const url = coilCollectionUrl(field, p);
      if (!url) continue;
      // counted so the `#rate` readiness gate below knows these are outstanding
      // — they are the lists collectRateState resolves the vendor CODES from,
      // so a Rate fired before they land is exactly the payload BUG 3-REDUX
      // describes.
      setEnumFetchPending((n) => n + 1);
      void fetch(url, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      })
        .then((res) => (res.ok ? res.json() : null))
        .then((body) => {
          if (!body) return;
          const rows: OptionRow[] = Array.isArray(body) ? body : ((body as any)?.results ?? []);
          if (!rows.length) return;
          setMasterLists((prev) => (prev[field]?.length ? prev : { ...prev, [field]: rows }));
        })
        .catch(() => undefined)
        .finally(() => setEnumFetchPending((n) => n - 1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, seedTick, detail, ratedCoilModel]);

  // casing_mtl_defaults re-pick (legacy coil.selector.js:1123-1152):
  //   if (in-list && !update_casing_mtl && !duty_id_chnaged) keep
  //   else casing_mtl_defaults[`${mfg}_${type}_${duty}`] → current → first row
  // `update_casing_mtl` is set for EVERY mfg / type / duty change (js:2835), so
  // those three changes re-pick the map default even when the current material
  // is still offered — the map is the point (79_1_1 → Steel-S-304L). The
  // dependency engine's own fallback only covers the out-of-list case, so the
  // forced case lives here, keyed on the same three fields legacy keys it on.
  const casingRepickKey = useRef<string>('');
  useEffect(() => {
    if (seeded.current !== modelId) return;
    const list = (masterLists.casing_mtl_id as any[]) ?? [];
    if (!list.length) return;
    const key = `${state.current.mfg_id}_${state.current.type_id}_${state.current.duty_id}`;
    const cur = state.current.casing_mtl_id;
    const idOf = (r: any) => String(coilOptionId(r, 'casing_mtl_id'));
    const inList = cur != null && list.some((r) => idOf(r) === String(cur));
    // `update_casing_mtl` equivalent: the mfg/type/duty triple moved since the
    // last re-pick. On the very first pass (open) it also runs, which is legacy
    // — getCollections re-picks on the init cascade too.
    const forced = casingRepickKey.current !== `${modelId}:${key}`;
    if (inList && !forced) return;
    casingRepickKey.current = `${modelId}:${key}`;
    const defaultId = CASING_MTL_DEFAULTS[key];
    const hasDefault = defaultId != null && list.some((r) => idOf(r) === String(defaultId));
    const next = hasDefault ? defaultId : inList ? cur : coilOptionId(list[0], 'casing_mtl_id');
    if (next != null && String(next) !== String(cur)) {
      state.current = { ...state.current, casing_mtl_id: next };
      bump();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterLists.casing_mtl_id, modelId,
      state.current.mfg_id, state.current.type_id, state.current.duty_id]);

  // ★ mfg 190 (SGS Refrigeration) render-time auto-defaults (coil.selector.js:
  // 1762-1842). On every render, as long as the user hasn't since touched the
  // target field, legacy force-sets casing_mtl_id / tube_dia_id / htfluid_id /
  // tube_wall_thick_id off the freshly-loaded catalog lists — layered ON TOP
  // OF the generic casing_mtl_defaults re-pick above, which has no
  // '190_2_<duty>' entry and no mfg-190 case at all (this effect is declared
  // after it so it wins for mfg 190, same order as legacy: render()'s block
  // runs after getCollections' own casing_mtl_defaults re-pick).
  //
  //   • casing_mtl_id (js:1762-1810) — THREE near-duplicate blocks (keyed off
  //     the fhs / fls / casing_mtls lists) all write this SAME field, each
  //     checking `elem.id == 274`. All three lists are actually {item:{id,
  //     ...}} rows (wrapped — see COIL_OPTION_KEYS casing_mtl_id/fh/fl), so
  //     `elem.id` is always `undefined`, the "274" branch is dead code, and
  //     every live pass falls to its `else`: the constant 2535. Ported as that
  //     constant. Gated here on casing_mtl_id's OWN touched flag rather than
  //     legacy's fh/fl/casing_mtl three-flag tangle (which, combined with the
  //     dead-code bug above, makes legacy re-force casing_mtl_id back to 2535
  //     on the NEXT unrelated render unless the user has ALSO touched fh and
  //     fl — an accidental side effect of the flag mix-up we do not reproduce).
  //   • tube_dia_id (js:1786-1797) — a REAL conditional (tube_dias IS read via
  //     `elem.item.id`): '5' if the loaded list carries item.id 5, else '6'.
  //   • htfluid_id (js:1811-1822) — a REAL conditional (fluids rows are FLAT —
  //     htfluid_id is the one unwrapped list here): '13' if the list carries
  //     id 13, else '22'. Legacy gates this block on `changeCircuiting` (the
  //     CIRCUITING flag) — `changeHTFluid` is set at js:2755 but never read
  //     anywhere, so this reads as a flag-name mix-up, not intent. Gated here
  //     on htfluid_id's own touched flag instead.
  //   • tube_wall_thick_id (js:1827-1842) — a REAL conditional (elem.item?.id):
  //     '26' if the list carries item.id 26, else '19'.
  useEffect(() => {
    if (seeded.current !== modelId) return;
    if (Number(state.current.mfg_id) !== 190) return;
    const touched = sgs190TouchedRef.current;
    const hasId = (list: any[] | undefined, field: string, id: number) =>
      (list ?? []).some((r) => Number(coilOptionId(r, field)) === id);
    const next = { ...state.current };
    let changed = false;

    const casingMtlList = masterLists.casing_mtl_id as any[] | undefined;
    if (!touched.casingMtl && casingMtlList?.length && Number(next.casing_mtl_id) !== 2535) {
      next.casing_mtl_id = 2535;
      changed = true;
    }
    const tubeDiaList = masterLists.tube_dia_id as any[] | undefined;
    if (!touched.tubeDia && tubeDiaList?.length) {
      const want = hasId(tubeDiaList, 'tube_dia_id', 5) ? 5 : 6;
      if (Number(next.tube_dia_id) !== want) { next.tube_dia_id = want; changed = true; }
    }
    const htfluidList = masterLists.htfluid_id as any[] | undefined;
    if (!touched.htfluid && htfluidList?.length) {
      const want = hasId(htfluidList, 'htfluid_id', 13) ? 13 : 22;
      if (Number(next.htfluid_id) !== want) { next.htfluid_id = want; changed = true; }
    }
    const tubeWallThickList = masterLists.tube_wall_thick_id as any[] | undefined;
    if (!touched.tubeWallThick && tubeWallThickList?.length) {
      const want = hasId(tubeWallThickList, 'tube_wall_thick_id', 26) ? 26 : 19;
      if (Number(next.tube_wall_thick_id) !== want) { next.tube_wall_thick_id = want; changed = true; }
    }
    if (changed) {
      state.current = next;
      bump();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterLists.casing_mtl_id, masterLists.tube_dia_id, masterLists.htfluid_id,
      masterLists.tube_wall_thick_id, modelId, state.current.mfg_id]);

  // ★ Phase 6.5 (builder 3) — casing_type_id first-row fallback, coil.selector.js
  // :895-897 (verbatim predicate):
  //   if (self.collections.casing_types.length &&
  //       (!self.model_data.params_coil.casing_type_id ||
  //        !self.collections.casing_types.find((el) => el.item.id == …casing_type_id)))
  //     self.model_data.params_coil.casing_type_id = self.collections.casing_types[0].item.id
  // `coilDefaults.casing_type_id` is 3 (js:320) and 3 is NOT in the mfg-21/type-1
  // list, so a first-ever open lands on the FIRST row — live: 17 "Standard",
  // where the port rendered the select BLANK.
  //
  // ★★ HEADER-ONLY. Legacy runs this in both modes, but a saved board coil's
  // casing_type_id comes from its own params blob and is in-list, so gating costs
  // no board fidelity and guarantees the board path is untouched.
  const casingTypeApplied = useRef<number | null>(null);
  useEffect(() => {
    if (!headerMode) return;
    if (seeded.current !== modelId) return;
    const list = (masterLists.casing_type_id as any[]) ?? [];
    if (!list.length) return;
    const cur = state.current.casing_type_id;
    const inList = cur != null && list.some((r) => String(coilOptionId(r, 'casing_type_id')) === String(cur));
    if (inList) { casingTypeApplied.current = modelId; return; }
    if (casingTypeApplied.current === modelId) return;
    casingTypeApplied.current = modelId;
    const next = coilOptionId(list[0], 'casing_type_id');
    if (next != null && String(next) !== String(cur)) {
      state.current = { ...state.current, casing_type_id: next };
      bump();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterLists.casing_type_id, modelId, headerMode]);

  // tube spacing id+VALUE sync (legacy coil.selector.js:1063-1078): legacy
  // keeps params_coil.tube_*_spacing_id (the BASE item.id) and
  // params_coil.tube_*_spacing (the value string) in lockstep off the loaded
  // list — KEEPING the current id when the list carries it, else (null or
  // out-of-list) falling to the FIRST row, id and value together. BOTH fields
  // flow into the select persist (coil.selector.js:3371-3374).
  useEffect(() => {
    if (seeded.current !== modelId) return;
    let changed = false;
    const next = { ...state.current };
    for (const [idKey, valKey] of [
      ['tube_vert_spacing_id', 'tube_vert_spacing'],
      ['tube_hor_spacing_id', 'tube_hor_spacing'],
    ] as const) {
      const list = (masterLists[idKey] as any[]) ?? [];
      if (!list.length) continue;
      const id = next[idKey];
      const row =
        (id == null ? undefined : list.find((r) => String(coilOptionId(r, idKey)) === String(id))) ??
        list[0];
      const rowId = coilOptionId(row, idKey);
      const value = (row as any)?.item?.value;
      if (rowId != null && String(next[idKey] ?? '') !== String(rowId)) {
        next[idKey] = rowId;
        changed = true;
      }
      if (value != null && String(next[valKey] ?? '') !== String(value)) {
        next[valKey] = value;
        changed = true;
      }
    }
    if (changed) {
      state.current = next;
      bump();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterLists.tube_vert_spacing_id, masterLists.tube_hor_spacing_id, state.current, modelId]);

  // Selector-parity — casing flange id→VALUE sync. The Casing-tab Flange Top /
  // Bottom selects carry the id (casing_flange_top_id / casing_flange_btm_id),
  // but buildVendorRateInput + applyCoilRequiredAwHeights read the WIDTH VALUE
  // (casing_flange_top / casing_flange_btm — the serializer item.value fields,
  // coil.selector.js resolves them from the selected option's data-value). Keep
  // the value in lockstep with the id off the shared casing_flange_widths list
  // (only backfills the value; never clears/repicks the id — the seed + the
  // header defaults provide valid ids). Header/Return sides need no value sync
  // (they ship by id via the static table / persist by id only).
  useEffect(() => {
    if (seeded.current !== modelId) return;
    const list = (masterLists.casing_flange_widths as any[]) ?? [];
    if (!list.length) return;
    let changed = false;
    const next = { ...state.current };
    // The four flange selects read the SHARED casing_flange_widths list, so they
    // are not graph fields and the cascade never re-picks them. Legacy does not
    // either — but its selects carry no `- -` option (coil.selector.tmpl:1408/
    // :1426/:1444/:1461), so a width the new manufacturer's list drops leaves no
    // option selected and the browser falls to the FIRST width. Mirror that,
    // otherwise the select renders the stale id.
    for (const idKey of [
      'casing_flange_header_side_id', 'casing_flange_return_side_id',
      'casing_flange_top_id', 'casing_flange_btm_id',
    ] as const) {
      const id = next[idKey];
      if (id == null || id === '') continue;
      if (list.some((r) => String(coilOptionId(r, idKey)) === String(id))) continue;
      next[idKey] = coilOptionId(list[0], idKey);
      changed = true;
    }
    for (const [idKey, valKey] of [
      ['casing_flange_top_id', 'casing_flange_top'],
      ['casing_flange_btm_id', 'casing_flange_btm'],
    ] as const) {
      const id = next[idKey];
      if (id == null || id === '') continue;
      const row = list.find((r) => String(coilOptionId(r, idKey)) === String(id));
      const value = (row as any)?.item?.value;
      if (value != null && String(next[valKey] ?? '') !== String(value)) {
        next[valKey] = value;
        changed = true;
      }
    }
    if (changed) {
      state.current = next;
      bump();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterLists.casing_flange_widths, state.current, modelId]);

  // FL keep/pick-first (legacy coil.selector.js:904-918): the fls list is
  // VALUE-keyed (option value={{item.value}} — coil.selector.tmpl:187). Legacy
  // keeps params_coil.fl when some item.value matches, else falls back to the
  // FIRST list row (legacy assigns fls[0].item.id — a value-space slip; the DOM
  // select then shows its first option, so the value that reaches rate/select is
  // the first option's item.value — mirror that EFFECTIVE value). The dialog-open
  // seed itself is master_coil.fl (coil.selector.js:588 — mirrored in the seed
  // effect above); without this re-pick a fresh coil rates with NO fl → RAE
  // range-check failure.
  useEffect(() => {
    if (seeded.current !== modelId) return;
    const list = (masterLists.fl as any[]) ?? [];
    if (!list.length) return;
    const cur = state.current.fl;
    if (cur != null && cur !== '' && list.some((r) => String((r as any)?.item?.value) === String(cur))) return;
    const first = (list[0] as any)?.item?.value;
    if (first != null && String(first) !== String(cur ?? '')) {
      state.current = { ...state.current, fl: first };
      bump();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterLists.fl, state.current, modelId]);

  // ★ Phase 6.5 — duty keep-or-pick-first (coil.selector.js:952-964). getCollections
  // keeps `model_data.model.duty_id` when the loaded duty list carries it, and
  // otherwise assigns `self.collections.duties[0].item.id` (both the single-row
  // branch at :952-956 and the fallback at :961-964). `coilDefaults` has NO
  // `duty_id` (js:257-383) and the `options.header` override does not add one
  // (js:394-420), so in HEADER mode the duty always starts null and legacy always
  // lands on the first row — without this the Duty select renders blank, the
  // htfluid list can't narrow, and the `duty` rate key falls back to the
  // hardcoded 'cooling'. HEADER-ONLY: the board seed always carries a duty_id off
  // the component row, so gating this keeps board request/UI behaviour identical.
  useEffect(() => {
    if (!headerMode) return;
    if (seeded.current !== modelId) return;
    const list = (masterLists.duty_id as any[]) ?? [];
    if (!list.length) return;
    const cur = state.current.duty_id;
    if (cur != null && list.some((r) => String(coilOptionId(r, 'duty_id')) === String(cur))) return;
    const first = coilOptionId(list[0], 'duty_id');
    if (first != null && String(first) !== String(cur ?? '')) {
      state.current = { ...state.current, duty_id: first };
      // the duty is a graph parent (htfluid_id descends from it —
      // COIL_DEPENDENCY_GRAPH.duty_id), so re-cascade like legacy's
      // getCollections does with the freshly assigned duty.
      graph.onParentChange('duty_id');
      // J6 — getCollections' own re-derive also re-locks the season, but
      // NOT on the initial call (`if (!init) self.setSeasonForDuty(...)`,
      // coil.selector.js:1048/:1058). The first post-seed auto-pick IS that
      // init call.
      if (!dutyAutoPickInit.current) setSeasonForDuty(first);
      dutyAutoPickInit.current = false;
      bump();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterLists.duty_id, state.current, modelId, headerMode]);

  // ★ js:1106-1111 — the single-duty branch of getCollections runs on EVERY
  // cascade whose duty list narrowed to one row, not only when that changes the
  // value, and it always re-locks the season. The dependency engine
  // short-circuits an already-in-list value, so a coil sitting on the sole duty
  // never re-locked: set Season = Winter on a Cooling coil, switch manufacturer,
  // and dev snaps it back to Summer while dev3 kept Winter and saved it. The
  // value-changed case is handled by the re-pick above; this covers the rest.
  // `if (!init)` (js:1111) ⇒ only after a real user change.
  useEffect(() => {
    if (seeded.current !== modelId) return;
    if (cascadeInit.current) return;
    const list = (masterLists.duty_id as any[]) ?? [];
    if (list.length !== 1) return;
    const sole = coilOptionId(list[0], 'duty_id');
    if (sole == null || String(sole) !== String(state.current.duty_id ?? '')) return;
    setSeasonForDuty(sole);
    bump();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterLists.duty_id, modelId]);

  // ---- field change handlers ----
  const markChanged = useCallback(() => {
    bus.emit('rating_mode:mounting:changed');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bus]);

  // Dev-sync J6 — setSeasonForDuty (coil.selector.js:2238-2244; sync-K shape
  // dev :2305-2320): keeps the staged section season in duty sync — Cooling
  // (duty 1) → Summer. Heating (duty 2) → Winter (preheat position), UNLESS
  // this section sits downstream of a cooling coil on the same air_stream_id
  // (9ab54f1b01 — see heatingReheat above), in which case it's a reheat coil
  // and defaults to Summer instead. Anything else → unset. Legacy stamps the
  // hardcoded season ids 1/2; here the target row resolves from the CMS
  // payload by VALUE ('summer'/'winter' — same rows, id-agnostic). The duty
  // ids 1/2 ARE pinned backend constants (COOLING_DUTY_ID/HEATING_DUTY_ID,
  // psychrometric_views.py:56-59). The controlled select re-renders off the
  // ref (legacy syncs the DOM select by hand).
  const setSeasonForDuty = useCallback(
    (dutyRaw: unknown) => {
      const dutyNum = Number(dutyRaw);
      const target =
        dutyNum === 1
          ? 'summer'
          : dutyNum === 2
            ? heatingReheatRef.current
              ? 'summer'
              : 'winter'
            : null;
      const hit = target
        ? cmsSeasonsByComponent(ahuDoc as ICmsAhuDoc | undefined, 'Coil').find(
            (r) => r.value === target
          )
        : undefined;
      seasonIdRef.current = hit ? Number(hit.id) : null;
    },
    [ahuDoc]
  );
  setSeasonForDutyRef.current = setSeasonForDuty;

  /**
   * Dev-sync J6 — the SAVE-time season fallback (coil.selector.js:3550-3560).
   *
   * When Select runs with nothing staged, legacy does NOT persist a null
   * season: it falls back to the coil's duty, Cooling (1) → season 1, Heating
   * (2) → season 2. The port dropped this as dead code on the grounds that
   * `model.duty_id === 1` strict-compares a number against a string from
   * `elem.val()` — but that only holds AFTER the user touches the Duty select.
   * On open, `model_data.model.duty_id` is a number: it comes from the duty
   * collection (`collections.duties[0].item.id`, :1109/:1118) or from the
   * `coilDefaults` seed (:4620 sets a literal `1`). So the branch is live for
   * exactly the case that needs it — a section whose season was never set —
   * and dropping it made Select persist `season_id: null` where dev persists
   * 1 or 2, which ALSO skipped the per-season entering-temp copy below (both
   * of its branches are gated on a non-null season).
   *
   * ★ Deliberately NOT the setSeasonForDuty mapping above: legacy's fallback
   * is the flat duty→season pair with no reheat/upstream-cooling nuance, so a
   * reheat heating coil saves Winter here even though the select displays
   * Summer. Reproduced as-is rather than "fixed" — dev is the spec.
   * Resolved by VALUE off the CMS rows, per this file's convention.
   */
  const seasonIdForSaveFallback = useCallback((): number | null => {
    const dutyNum = Number(state.current.duty_id);
    const target = dutyNum === 1 ? 'summer' : dutyNum === 2 ? 'winter' : null;
    if (!target) return null;
    const hit = cmsSeasonsByComponent(ahuDoc as ICmsAhuDoc | undefined, 'Coil').find(
      (r) => r.value === target
    );
    return hit ? Number(hit.id) : null;
  }, [ahuDoc]);

  const onSearchField = useCallback(
    (name: string, raw: string) => {
      const value = name.endsWith('_id') ? num(raw) : raw;
      state.current = { ...state.current, [name]: value };
      // Selector-parity COIL — an All-Coils box edit recomputes its per-coil
      // twin = total ÷ qty_stacked (coil.selector.js:2499-2516), so the value
      // the vendor rates on (desired_capacity_total / fluid_flow) and the
      // display per-coil box both follow the All-Coils edit. No-op for every
      // other search field.
      const perAllPatch = coilPerAllRecompute(name, value, state.current);
      if (Object.keys(perAllPatch).length) {
        state.current = { ...state.current, ...perAllPatch };
      }
      // ★ calcAirflowAndFaceVelocity's render half (js:1862) — the derived face
      // velocity has to follow an All-Coils airflow edit AND an fh/fl change
      // (which legacy also drives explicitly from changeParams, js:2697-2699).
      state.current = {
        ...state.current,
        ...coilFaceVelocityFromAirflow(state.current),
      };
      setRatedRows(null); // a param change invalidates prior rated outputs.
      if (name === 'mfg_id') mfgForNotes.current = Number(value) || null;
      // js:2802/:2819 — `self.changeMFG` is true only for the manufacturer
      // select and is cleared by every other field.
      mfgChangedRef.current = name === 'mfg_id';
      // mfg-190 auto-default touched-tracking (see the effect declared after
      // the casing_mtl_defaults re-pick): a manufacturer pick resets all four
      // (js:2805/:2813/:2815/:2816 reset changeCasingMTL/changeTubeDia/
      // changeTubeWallThick unconditionally, for every mfg — not only 190),
      // and each target field's own edit arms its flag.
      if (name === 'mfg_id') {
        sgs190TouchedRef.current = {
          casingMtl: false, tubeDia: false, htfluid: false, tubeWallThick: false,
        };
      } else if (name === 'casing_mtl_id') {
        sgs190TouchedRef.current = { ...sgs190TouchedRef.current, casingMtl: true };
      } else if (name === 'tube_dia_id') {
        sgs190TouchedRef.current = { ...sgs190TouchedRef.current, tubeDia: true };
      } else if (name === 'htfluid_id') {
        sgs190TouchedRef.current = { ...sgs190TouchedRef.current, htfluid: true };
      } else if (name === 'tube_wall_thick_id') {
        sgs190TouchedRef.current = { ...sgs190TouchedRef.current, tubeWallThick: true };
      }
      if (COIL_DEPENDENCY_GRAPH[name]) {
        cascadeInit.current = false;
        graph.onParentChange(name);
      }
      // ★ the Manufacturer branch of changeParams (js:2781-2820 + the shared
      // mfg/type tail :2846-2895). Legacy fires the cascade first (js:2704) and
      // then stamps these synchronously, so a pin that the new manufacturer's
      // list does not carry is still overridden by the cascade when it lands —
      // same ordering here, since onParentChange only starts the fetches.
      if (name === 'mfg_id') {
        state.current = {
          ...state.current,
          ...coilMfgChangePatch(value, state.current, (typeId) =>
            coilTypeFromTypes(masterListsRef.current.type_id as any[], typeId as any)),
        };
        // js:2786 — mfg 190 forces type 2, which carries its own defaults.
        if (Number(value) === 190) {
          state.current = { ...state.current, ...coilTypeDutyDefaultsPatch(true, 2, state.current) };
        }
      }
      // ★ the Type branch of changeParams (js:2821-2834 + the same shared tail
      // at :2846-2896). This was missing entirely: `onParentChange` reloads a
      // field's DESCENDANTS, never the field itself, so the only code that ever
      // ran coilTypeDutyDefaultsPatch(true, …) was the cascade re-picking
      // type_id after an mfg change. A user's own Type pick left the entering /
      // leaving temperatures, HT Fluid, Style, Feeds and the connection
      // diameters on the PREVIOUS type's values — and rated on them.
      if (name === 'type_id') {
        state.current = {
          ...state.current,
          ...coilTypeChangePatch(value, state.current, (typeId) =>
            coilTypeFromTypes(masterListsRef.current.type_id as any[], typeId as any)),
        };
      }
      // ★ HEADER MODE (changeParams js:2409-2600). Two legacy behaviours:
      //   • mfg_id → `getCollections(self.header && name === 'mfg_id', …)`
      //     (js:2437) runs the cascade with `search=true`, whose tail calls
      //     `findFromStoraga()` (js:1188/1220/1225/1230/1236) → RESTORE that
      //     manufacturer's saved `model_data` and re-fire the rate. It does NOT
      //     save: `if (self.header && e.target.name === 'mfg_id') {} else
      //     self.saveInStorage();` (js:2576-2577).
      //   • every other search field → saveInStorage().
      if (headerMode) {
        if (name === 'mfg_id') {
          // js:2790 `self.mfg_id = $(e.target).find(':selected').attr('data-id')`
          // — the OPTION's `_id`, which onMfgSelect has just stamped as
          // `state.data_id`. It is the storage key, so a generic twin ("79_1")
          // keeps its own saved inputs rather than sharing the vendor's.
          const dataId = state.current.data_id ?? value ?? null;
          coilMfgIdRef.current = dataId;
          const entry = findEntryByMfg(storageMapRef.current, dataId);
          if (entry) pendingRestore.current = { entry, applyState: true };
        } else {
          saveInStorage();
        }
      }
      markChanged();
      bump();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [graph, markChanged, bump, headerMode, saveInStorage]
  );

  /**
   * The Manufacturer select's own handler — `changeParams`'s `mfg_id` branch,
   * the half that reads the picked OPTION rather than the select's value
   * (coil.selector.js:2790-2794):
   *
   *   self.mfg_id                      = option.dataset.id       // the `_id`
   *   self.data_id                     = option.dataset.id
   *   params_coil.data_id              = self.data_id
   *   params_coil.coil_mfg = .mfg      = option text
   *   self.generic_mfg                 = option.dataset.generic != 'none'
   *
   * Only `coil_mfg` is mirrored, not legacy's `.mfg` alias: `params_coil.mfg` is
   * write-only in dev (nothing reads it, in the view or in either template),
   * and React's state is the FLATTENED merge of `model` + `params_coil`, where
   * `mfg` would collide with the component row's own manufacturer FK.
   *
   * `params_coil.mfg_id` stays the plain catalog `id` — identical for a vendor
   * and its private-label twin — so it is handed to the shared `onSearchField`
   * unchanged and the cascade, the rate payloads and every mfg-keyed lookup
   * behave exactly as before. Everything the twin actually changes lives in the
   * three fields stamped here.
   *
   * `generic_mfg` rides the Select save (js:3658) and is what makes the backend
   * report the coil's manufacturer as the OWN company name rather than the
   * vendor's (ahu/models_component_coil.py:1281-1287) — on the board, in the
   * reports and in CAD.
   */
  const onMfgSelect = useCallback(
    (dataId: string) => {
      const row = mfgByDataIdRef.current.get(dataId);
      // Stamped BEFORE onSearchField so its own mfg branch (and the header
      // storage key it derives) sees the new data_id, mirroring legacy's single
      // synchronous pass through changeParams.
      state.current = {
        ...state.current,
        data_id: dataId,
        ...(row ? { coil_mfg: row.name } : null),
      };
      onSearchField('mfg_id', String(row ? row.id : dataId));
    },
    [onSearchField]
  );

  const onModelField = useCallback(
    (name: string, raw: string | null) => {
      let value: string | number | null =
        name.endsWith('_id') ? (raw == null ? null : num(raw)) : raw;
      // qty_stacked clamps to >= 1 before it drives the recompute
      // (coil.selector.js:2532-2533).
      if (name === 'qty_stacked' && (!Number(value) || Number(value) < 1)) value = 1;
      state.current = { ...state.current, [name]: value };
      // Selector-parity COIL — a Per-Coil box edit recomputes its All-Coils
      // total = qty_stacked × per, and a qty_stacked change recomputes EVERY
      // total from its per (coil.selector.js:2481-2498 / :2532-2560). No-op for
      // every other model field.
      const perAllPatch = coilPerAllRecompute(name, value, state.current);
      if (Object.keys(perAllPatch).length) {
        state.current = { ...state.current, ...perAllPatch };
      }
      // ★ the Duty branch of changeCoilField (js:2518-2530). Same gap as the
      // Type pick above: coilTypeDutyDefaultsPatch(false, …) only ever ran from
      // the cascade's own duty re-pick, so picking Heating on a Cooling coil
      // left EADB/EAWB on 80/67 and the fluid temps on 45/55 instead of moving
      // to 45 and 180/140, and left the previous duty's connection and header
      // diameters selected. (setSeasonForDuty is already fired by the select.)
      if (name === 'duty_id' && value != null) {
        state.current = { ...state.current, ...coilDutyChangePatch(value, state.current) };
      }
      // ★ the face-velocity EDIT direction (calcAirflowAndFaceVelocity,
      // js:3457-3466) — typing a face velocity back-solves the airflow pair.
      if (name === 'face_velocity_actual') {
        state.current = {
          ...state.current,
          ...coilAirflowFromFaceVelocity(value, state.current),
        };
      }
      // ★ getAHUAirflow (js:4573-4599) — picking an Air Stream pulls that
      // stream's AHU design volume into the airflow pair. Board only.
      if (name === 'air_stream_id' && !headerMode) {
        state.current = {
          ...state.current,
          ...coilAirStreamAirflowPatch(
            value,
            state.current,
            (activate.ahu as Record<string, unknown> | undefined) ??
              (ahu as Record<string, unknown> | null | undefined)
          ),
        };
      }
      // ★ the no-elem half of calcAirflowAndFaceVelocity, which legacy runs at
      // the end of every render (js:1862): face velocity is DERIVED from the
      // per-coil airflow and fh × fl, so it follows an airflow edit, an
      // All-Coils edit and a qty_stacked change alike.
      state.current = {
        ...state.current,
        ...coilFaceVelocityFromAirflow(state.current),
      };
      mfgChangedRef.current = false;
      if (COIL_DEPENDENCY_GRAPH[name]) {
        cascadeInit.current = false;
        graph.onParentChange(name);
      }
      // Any Performance-tab field invalidates the on-screen rated output — same
      // rule as onSearchField's `setRatedRows(null)` — EXCEPT air_stream_id,
      // which only re-pulls the AHU design airflow (coilAirStreamAirflowPatch
      // above) and, like season_id (its own bespoke handler below, never routed
      // here), doesn't itself invalidate a rating already on screen.
      if (name !== 'air_stream_id') setRatedRows(null);
      // changeCoilField ends with saveInStorage() (js:2317).
      if (headerMode) saveInStorage();
      markChanged();
      bump();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [graph, markChanged, bump, headerMode, saveInStorage, activate.ahu, ahu]
  );

  const onSectionField = useCallback(
    (name: string, raw: string) => {
      state.current = { ...state.current, [name]: raw === '' ? null : raw };
      // Only EADB/EAWB ride this handler (season_id has its own bespoke
      // onChange below and never reaches here) — both invalidate the on-screen
      // rated output, same as every other Performance-tab field.
      setRatedRows(null);
      // the section pair rides the same `.coil_field` handler ⇒ same save
      // (js:2317; the pair is also what lands in model_data.section).
      if (headerMode) saveInStorage();
      markChanged();
      bump();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [markChanged, bump, headerMode, saveInStorage]
  );

  // Selector-parity — the Manifold/Casing/Options checkboxes. Legacy stores a
  // real boolean in model_data.params_coil / .model (the checkbox `.prop
  // ('checked')`, coil.selector.js changeParams/changeCoilField); the select
  // write (buildCoilSelectPayload STRING_OR_BOOL_KEYS) + the header rate `bool()`
  // both consume booleans, so store booleans (not 'true'/'false' strings). Some
  // (moisture_eliminator/label_kit/nitrogen_charge) also ride model_data.params
  // via buildCoilSelectPayload. A checkbox is a `.search_field` — changing it
  // invalidates the prior rated outputs, like the legacy sweep re-rate.
  const onCheckboxField = useCallback(
    (name: string, checked: boolean) => {
      state.current = { ...state.current, [name]: checked };
      setRatedRows(null);
      if (headerMode) saveInStorage();
      markChanged();
      bump();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [markChanged, bump, headerMode, saveInStorage]
  );


  // ---- coil_type + visibility ----
  const types = (masterLists.type_id as any[]) ?? [];
  const coilType = coilTypeFromTypes(types, state.current.type_id as any);
  const vis = coilTypeVisibility(coilType);
  const mfgId = Number(state.current.mfg_id);
  const isExternal = coilExternalRating(coilDoc as CoilCmsDoc, mfgId);

  // ★ "Available Coils: <n>" (coil.selector.tmpl:31-42, `{{#unless
  // external_rating}}`) + the count-based #rate gate (coil.selector.js:1844-
  // 1852: `count > 10 || count == 0` -> non-clickable). This was previously
  // left un-ported (its own dedicated TODO below the tab nav) — the count
  // endpoint (`/api/v1.0/cms/1/v1.0.0/ahu/coils/all/count/`) already exists on
  // this backend (`CMSCoilFilter`, django/cms/filter/coil.py); only the
  // frontend call was missing.
  const [coilAvailableCount, setCoilAvailableCount] = useState<number | null>(null);
  // ★ IN-FLIGHT flag for the count above. The count is debounced 300 ms and then
  // round-trips, so between a field change and the new number landing the
  // component still holds the PREVIOUS manufacturer's / selection's count — and
  // the #rate gate below was reading it as if it were current. Switching
  // manufacturer is the visible case: the old count (or `null`, which adds no
  // restriction) leaves RATE clickable for ~half a second against a selection
  // whose real candidate count has not been asked for yet. `coilCountReq` is
  // what makes the flag abort-safe: the effect's cleanup aborts the fetch but
  // its `.catch` still runs, and without the id check that late rejection would
  // clear the flag belonging to the NEXT request.
  const [coilCountPending, setCoilCountPending] = useState(false);
  const coilCountReq = useRef(0);
  useEffect(() => {
    if (isExternal) { setCoilAvailableCount(null); setCoilCountPending(false); return undefined; }
    const s = state.current;
    if (s.mfg_id == null || s.type_id == null) {
      setCoilAvailableCount(null);
      setCoilCountPending(false);
      return undefined;
    }
    const params = new URLSearchParams();
    for (const key of COIL_COUNT_FIELDS) {
      const v = s[key];
      if (v == null || v === '') continue;
      params.set(key, String(v));
    }
    const reqId = ++coilCountReq.current;
    const settle = () => { if (coilCountReq.current === reqId) setCoilCountPending(false); };
    setCoilCountPending(true);
    const controller = new AbortController();
    const t = window.setTimeout(() => {
      void fetch(`/api/v1.0/cms/1/v1.0.0/ahu/coils/all/count/?${params.toString()}`, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: controller.signal,
      })
        .then((res) => (res.ok ? res.json() : null))
        .then((body) => {
          if (coilCountReq.current !== reqId) return;
          if (body && typeof body.count === 'number') setCoilAvailableCount(body.count);
          settle();
        })
        .catch(settle);
    }, 300);
    return () => { window.clearTimeout(t); controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExternal, ...COIL_COUNT_FIELDS.map((k) => state.current[k])]);

  // a coil with a rated row (capacity_total set) starts with Select ENABLED.
  useEffect(() => {
    const m = (detail ?? {}) as CoilModel;
    setSelectEnabled(m.capacity_total != null || Boolean(ratedRows?.length));
  }, [detail, ratedRows, setSelectEnabled]);

  // #rate required-field gate (checkRequiredFields — mfg/type/rows/fpi present).
  const requiredFilled = useMemo(() => {
    const s = state.current;
    return s.mfg_id != null && s.type_id != null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.current, detail]);

  // ---- ★ #rate LOAD/RENDER gate -------------------------------------------
  // The dialog opens with `#rate` live while the coil is still loading: the
  // component-detail + CMS reads are in flight, the seed has not run, the
  // mfg/type dependency cascade has not narrowed a single list and the eager
  // enum-list GETs above have not landed. Legacy has the same window (it swaps
  // its own `$el` for a preloader at coil.selector.js:551 and never touches the
  // chassis button until `render`), and a Rate clicked inside it rates against
  // a half-built payload — `collectRateState` has no master rows to resolve the
  // vendor CODES from, which is the BUG 3-REDUX failure above.
  //
  // So the button is greyed until everything the input pane is built from has
  // arrived AND the cascade has stopped moving. `isCascading` also goes true on
  // a later user mfg/type/duty change, which is deliberate: the same
  // half-narrowed payload is possible there.
  //
  // ★ It also covers the RATE ROUND-TRIP itself — `rateInFlight` is the vendor
  // dispatch OR the DB route (a manual-manufacturer coil never touches the
  // vendor mutation), i.e. the whole time `#coil-selector-preloader` is over
  // the pane. The 250 ms settle below then carries it through the output
  // render, so the button comes back only once the rated table is on screen.
  // The cost is deliberate and is the one behaviour change here: legacy lets a
  // second Rate click supersede an in-flight one (`self.rate_request.abort()`,
  // via useRatingDispatch's cancel-on-resubmit). Cancel still aborts a running
  // rate, so nothing is trapped.
  const rateDataPending =
    detailLoading ||
    coilDocLoading ||
    ratedCoilLoading ||
    seeded.current !== modelId ||
    enumFetchPending > 0 ||
    graph.isCascading ||
    rateInFlight;
  // Settle-detected, the pattern the header restore below already uses: the
  // cascade runs layer by layer and `isCascading` dips to false between layers,
  // so a bare edge would flash the button live mid-cascade. One quiet 250 ms
  // tick is what counts as "done"; any pending work in that window clears the
  // timer and re-arms.
  const [rateReady, setRateReady] = useState(false);
  useEffect(() => {
    if (rateDataPending) {
      setRateReady(false);
      return undefined;
    }
    const t = window.setTimeout(() => setRateReady(true), 250);
    return () => window.clearTimeout(t);
  }, [rateDataPending]);

  useEffect(() => {
    // SCOPED to this selector's own chassis. Legacy's own gate is accidentally
    // scoped by class (`#rate.${self.header ? 'all' : 'coil'}-rate[data-type=
    // "coil"]`, coil.selector.js:1557/:2403) — an un-scoped
    // document.getElementById('rate') would hit the BOARD dialog's #rate first
    // (duplicate ids) whenever the header popup and a board dialog coexist.
    // Discovery §7.3 trap 1.
    const btn = chassisEl()?.querySelector<HTMLElement>('#rate');
    if (!btn) return;
    // ★ Phase 6.5 — OWN-TAB PREDICATE. The header popup never unmounts a visited
    // tab (component_selector.js:126-186 is show/hide), so all four selectors
    // stay mounted and would fight over the ONE shared `#rate`: whichever
    // rendered last won, and returning to Fan after visiting HEAT WHEEL / PLATE
    // HEAT EXCHANGER left RATE greyed out with valid inputs. Legacy is immune
    // because every gate is scoped to its own tab —
    // `$('#rate.all-rate[data-type="<own>"]')` (fan :1998/:6657, coil
    // :1656/:2677, ewheel :2469, phx :2427) — which matches NOTHING while
    // another tab is active. The chassis stamps `data-type` from the active tab
    // (component_selector.js:173/:181; IndependentRatingPopup `data-type={tab}`)
    // and each selector's `activate.name` IS that tab key, so comparing the two
    // reproduces legacy's scoping exactly.
    // HEADER-ONLY: the board hosts one dialog / one selector / one `#rate`, so
    // its behaviour stays byte-identical (and a board `data-type` that ever
    // disagreed with `eventName` could otherwise freeze the button).
    if (headerMode && btn.dataset.type !== eventName) return;
    // `rateReady` only ADDS a reason to be greyed — the legacy required-field
    // gate is unchanged, and `doRate` itself is untouched (the header-mode
    // auto-re-fire calls it directly and must stay able to).
    // js:1844-1852 — the "Available Coils" count ALSO gates #rate for a
    // non-external mfg: too many candidates (>10) or none (0) is non-clickable.
    // `coilAvailableCount == null` (not yet fetched, or an external mfg) adds
    // no restriction, matching legacy (the class is only ever toggled ON by
    // this rule from inside filterDataCount's own `.then`).
    // `coilCountPending` closes the window that `null` leaves open: while the
    // count for the CURRENT selection is still debouncing or in flight, the
    // number on screen belongs to the previous selection, so RATE stays greyed
    // rather than being armed off a stale answer.
    const countBlocks =
      !isExternal &&
      (coilCountPending ||
        (coilAvailableCount != null && (coilAvailableCount > 10 || coilAvailableCount === 0)));
    btn.classList.toggle('non-clickable', !(requiredFilled && rateReady) || countBlocks);
  });

  // ---- build the coil RATE input ----
  const collectRateState = useCallback((): Record<string, any> => {
    // resolve each enum select-id to its VENDOR CODE (the master row `.value`),
    // NOT the display label (BUG 3: the vendor rejected "5/8"/"Copper"/"Waffle";
    // it wants "0.625"/"copper"/"waffle"). Legacy coil.selector.js:2691 sends
    // option.data('value') (the master `.value`) for every search-field SELECT
    // except a short exclusion list. The visible select keeps coilOptionLabel.
    const s = { ...state.current };
    const resolve = (field: string, idKey: string, outKey: string) => {
      const rows = masterListsRef.current[field];
      const id = s[idKey];
      if (rows && id != null) {
        const row = rows.find((r: any) => String(coilOptionId(r, field)) == String(id));
        if (row) s[outKey] = coilOptionValue(row, field) ?? s[outKey];
      }
    };
    resolve('rows_id', 'rows_id', 'rows');
    resolve('fpi_id', 'fpi_id', 'fpi');
    resolve('tube_dia_id', 'tube_dia_id', 'tube_dia');
    resolve('tube_wall_thick_id', 'tube_wall_thick_id', 'tube_wall_thick');
    resolve('fin_thick_id', 'fin_thick_id', 'fin_thick');
    resolve('circuiting_id', 'circuiting_id', 'circuiting');
    // the enum material/pattern/finish fields buildVendorRateInput consumes —
    // these were NOT resolved before, so they leaked raw ids / labels to the
    // vendor. Resolve them to their master `.value` codes (BUG 3).
    resolve('tube_mtl_id', 'tube_mtl_id', 'tube_mtl');
    resolve('fin_mtl_id', 'fin_mtl_id', 'fin_mtl');
    resolve('fin_pattern_id', 'fin_pattern_id', 'fin_pattern');
    resolve('fin_finish_id', 'fin_finish_id', 'fin_finish');
    return s;
  }, []);

  // legacy `generetionTimeStamp()` (coil.selector.js:4213-4242), called from
  // every rate path (:3190/:3217/:3231/:3241/:3251). It writes
  // `params_coil.rating_datetime`, which is what Select PERSISTS (the
  // buildCoilSelectPayload `rating_datetime: s.rating_datetime` key) and what
  // the print sheet's "Rated on:" reads. The port stamped a
  // `toLocaleString()` into the ref only, so the column stayed EMPTY on every
  // React-rated coil and the header line had the wrong format. The column is a
  // varchar holding exactly `YYYY.MM.DD_HH:mm:ss`.
  const stampRatingDatetime = useCallback(() => {
    const d = new Date();
    const p = (n: number) => (n < 10 ? `0${n}` : String(n));
    const ts =
      `${d.getFullYear()}.${p(d.getMonth() + 1)}.${p(d.getDate())}_` +
      `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
    ratedDatetime.current = ts;
    state.current = { ...state.current, rating_datetime: ts };
  }, []);

  // ---- RATE (CODE-ONLY, userArmed — DUAL PATH) ----
  // `chooseIdx` mirrors legacy's `self.rated_coil_index =
  // self.saved_rated_coil_index || 0` (coil.selector.js:2942-2943), i.e. the
  // restored row index the header-mode auto-re-fire passes in.
  // Set just before a re-rate the header popup runs by itself (restoring a
  // saved entry, or a manufacturer switch): that is not a user's rating, so
  // it is kept out of component_rated.
  const autoRateRef = useRef(false);
  const doRate = useCallback(async (chooseIdx = 0) => {
    const trackRating = !autoRateRef.current;
    autoRateRef.current = false;
    if (!requiredFilled) return;
    userArmed.current = true;
    setRateErrors([]);
    setRateWarnings([]);
    setApiNotes([]);

    // mfg_id 21 hard-stub to an error WITHOUT calling the API (§32 / coil 2890).
    if (mfgId == 21) {
      setRatedRows([]);
      setRateErrors(['Got error from the API.']);
      externalRating.current = isExternal;
      stampRatingDatetime();
      // render_outputs → saveInStorage with is_clicked_rate TRUE (js:1317/:1475)
      // even on the error path (legacy calls render_outputs(0, error) at :2921).
      if (headerMode) saveInStorage({ rate: true, ratedIndex: 0 });
      bump();
      return;
    }

    const s = collectRateState();
    // tube_wall_thick default '0.020' from the select text (§33.2 / coil 2900).
    if (!s.tube_wall_thick) s.tube_wall_thick = '0.020';

    // ★ RE-RATE PARITY (coil.selector.js:3198-3200). Legacy's `rate()` — the
    // single funnel BOTH the vendor and the manual/DB routes pass through —
    // does exactly two things before it fires the request:
    //
    //   self.$el.find('.rated-item')
    //     .html('<div class="preloader dotted-preloader active asd"></div>');
    //   self.rate_request?.abort();
    //
    // i.e. it BLOWS AWAY the output pane's contents the instant Rate is
    // clicked, leaving only a spinner, and it cancels whatever rate was
    // already running. The port did neither: `ratedRows` was only ever
    // written from the RESPONSE, so a re-rate left the PREVIOUS rated table
    // (or the previous "Unable to find a coil…" error) on screen for the whole
    // round trip — 5-30 s against a vendor — with just the translucent
    // `.dotted-preloader` over it. Clicking Rate a second time therefore
    // looked like it had done nothing at all, which is the reported symptom.
    // `null` (not `[]`) is the empty state: `[]` renders the error block
    // (`ratedRows != null && !hasRated`), and legacy shows the spinner ALONE.
    setRatedRows(null);
    // legacy :3200 — cancel-on-resubmit. `dispatchRating` already does this for
    // the vendor route (useRatingDispatch.ts:51-52), but the DB route had no
    // equivalent, so two overlapping manual rates raced and the FIRST response
    // to land could overwrite the second's.
    dbAbort.current?.abort?.();
    dbAbort.current = null;

    const useExternal = isExternal === true;
    externalRating.current = useExternal ? true : false;

    let body: any;
    if (!useExternal) {
      // DB route — POST /ahu/rating_database_coil_selection/ (manufacturer:'manual').
      const input = buildDbRateInput(s);
      // also carry the catalog selection values (rows/fpi/etc).
      for (const k of ['rows', 'fpi', 'tube_dia', 'tube_wall_thick', 'circuiting']) {
        if (s[k] != null && input[k] == null) input[k] = String(s[k]);
      }
      const promise = postDbRating({ manufacturer: 'manual', part: 'coil', type: coilType, input });
      dbAbort.current = promise as unknown as { abort: () => void };
      const startedAt = Date.now();
      try {
        body = await promise.unwrap();
      } catch (err: any) {
        const name = err?.name ?? err?.error?.name;
        if (name === 'AbortError') return;
        body = err?.data ?? { ok: false, errors: [String(err?.error ?? err)] };
      } finally {
        dbAbort.current = null;
      }
      // The vendor route is counted in useRatingDispatch; this is the
      // catalog (database) route, which never reaches /rating/.
      const results = Array.isArray(body?.models) ? body.models.length : 0;
      if (trackRating) track('component_rated', {
        component: 'coil',
        manufacturer: 'database',
        success: body?.ok === true && results > 0,
        results,
        duration_ms: Date.now() - startedAt,
        source: headerMode ? 'header' : 'board',
      });
    } else {
      // VENDOR route — POST /api/v1.0/rating/ with derived manufacturer (§32).
      const manufacturer = coilMfgName(coilDoc as CoilCmsDoc, mfgId).toLowerCase().replace(' ', '_');
      // 5e-sync (KA-5259): `duty` (the duty option's machine value) feeds the
      // backend's add_min_aw_dimensions_to_models CMS-settings lookup
      // (tools.py:89-96) — legacy collects it off the duty select's
      // data-name="duty"/data-value (coil.selector.tmpl:91-93).
      // ★★ Phase 6.5 (builder 4) — HEADER MODE ships the FULL legacy sweep.
      // `buildVendorRateInput` is a board allow-list whose ~40 AHU/section/model
      // -sourced members are absent without an AHU, which made the RAE vendor
      // answer `15201 - Air face velocity (0 fpm …)` with 0 models (the coil tab
      // could not rate at all). `buildHeaderVendorRateInput` mirrors legacy's
      // `.search_field` sweep (coil.selector.js:2761-2866) off the flat state +
      // the loaded master lists; it was matched key-for-key / value-for-value
      // against a LIVE legacy capture (.runtime/v65/b4_old.json).
      // ★ NO LONGER HEADER-GATED. dev runs the SAME `.search_field` sweep in
      // both modes — `rate()` posts `self.data` for every external-rating mfg
      // (coil.selector.js:3163-3170), and a captured dev BOARD payload for RAE
      // carries all 64 keys, exactly what this builder emits. The board was
      // pinned to `buildVendorRateInput`, a 29-key allow-list, so the vendor
      // saw a third of the inputs — no mfg_id/type/htfluid, no rows/fpi/
      // circuiting/feeds, no manifold, casing, options or pricing members, and
      // `casing_mtl` (the display NAME) where dev sends `casing_mtl_gauge` (the
      // code). That is the same starvation this builder was written to fix for
      // header mode; the "A/B-proven board envelope" it was preserving was
      // proven against the earlier React board, not against dev.
      const input = buildHeaderVendorRateInput({
        s,
        masterLists: masterListsRef.current as Record<string, Array<Record<string, any>>>,
        mfgId,
        coilType,
        duty: resolveDutyValue(),
        headerMode,
      });
      const envelope = buildRatingEnvelope('coil', mfgId, input, {
        manufacturerOverride: manufacturer,
      });
      (envelope as any).type = coilType;
      body = await dispatchRating(envelope, { track: trackRating });
    }

    if (!body || body.cancelled) return;
    // ★ stamped HERE, not before the request. Legacy calls generetionTimeStamp()
    // inside the rate response handler (coil.selector.js:3217/:3231/:3241/:3251)
    // and in the mfg-21 short-circuit (:3190) — never on the way out. Stamping
    // early put the new time on screen while the old outputs were still up, and
    // left it there on a cancelled/superseded rate that legacy never stamps.
    stampRatingDatetime();
    if (body.ok && body.models && body.models.length) {
      const rows = (body.models as Array<Record<string, unknown>>).map((r, i) => ({
        ...r,
        coil_number: i + 1,
      }));
      setRatedRows(rows);
      // legacy `self.rated_coil_index = self.saved_rated_coil_index || 0` then
      // clears the saved index (js:2942-2943).
      setRatedIndex(rows[chooseIdx] ? chooseIdx : 0);
      savedRatedIndex.current = null;
      // a fresh rate re-renders the whole sheet, so the nav returns to its
      // default `Coil-output selected` state (tmpl:4).
      setOutputTab('coil');
      setRateWarnings((body.warnings ?? []).filter(Boolean));
      // ★ NO auto-opened notes popup. Legacy calls showNotes() after every
      // rate (js:3191/:3218/:3233/:3254), but showNotes only ever fills
      // `.coil_selector_notes .popup-wrapper` (js:3948-3978) and NO coil
      // template renders that element — `.coil_selector_notes` appears in
      // coil.selector.js and nowhere else in the whole legacy tree, so the
      // call is a dead no-op and dev never shows a popup on rate. The notes
      // themselves belong to the Notes OUTPUT tab, which is what setApiNotes
      // feeds.
      const notes = (body.notes ?? []) as string[];
      const clean = notes.filter(Boolean).map(String);
      setApiNotes(clean);
      setSelectEnabled(rows.length > 0);
      // ★ HEADER MODE: `render_outputs` sets `is_clicked_rate = true` (js:1317)
      // and its tail calls `saveInStorage()` (js:1475) — this is what stamps
      // `rate:true` so the next popup construction restores the rated table.
      if (headerMode) saveInStorage({ rate: true, ratedIndex: rows[chooseIdx] ? chooseIdx : 0 });
    } else {
      setRatedRows([]);
      setRateErrors(((body.errors ?? []) as string[]).filter(Boolean));
      setRateWarnings(((body.warnings ?? []) as string[]).filter(Boolean));
      // the no-models / error branches also end in render_outputs (js:2952/2964/
      // 2975) ⇒ same is_clicked_rate:true save.
      if (headerMode) saveInStorage({ rate: true, ratedIndex: 0 });
    }
    bump();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    requiredFilled, mfgId, isExternal, coilType, coilDoc, collectRateState,
    resolveDutyValue, postDbRating, dispatchRating, showNotes, setSelectEnabled, bump,
    headerMode, saveInStorage,
  ]);

  const abortAnyRate = useCallback(() => {
    abortRating();
    dbAbort.current?.abort?.();
    dbAbort.current = null;
  }, [abortRating]);

  // ---- ★ Phase 6.5 — HEADER-MODE pending restore + AUTO-RE-FIRE -------------
  // Legacy hangs both off the getCollections cascade tail (`if (search)
  // self.findFromStoraga()` — coil.selector.js:1188/1220/1225/1230/1236, reached
  // from the INIT call `getCollections(null, null, true)` at js:609 and from the
  // mfg-switch call `getCollections(self.header && name === 'mfg_id', …)` at
  // js:2437). findFromStoraga re-applies the entry's `model_data` and then clicks
  // `#rate` 1 s later (js:2402-2406). React has no cascade-completion hook, so
  // the cascade is settle-detected (isCascading false through one 250 ms tick) —
  // the pattern FanSelector established. The effect deliberately has NO dep
  // array: it re-arms on every render, which is how a still-settling cascade
  // postpones itself.
  useEffect(() => {
    if (!headerMode) return undefined;
    const pending = pendingRestore.current;
    if (!pending) return undefined;
    if (!coilDoc) return undefined;
    if (!cascadeFired.current) return undefined;
    if (graph.isCascading) return undefined;
    const t = window.setTimeout(() => {
      if (graph.isCascading) return; // still settling — a later render re-arms
      if (pending.applyState) {
        // findFromStoraga js:2393-2401 — `self.model_data = {...model.model_data}`
        // then `self.saved_rated_coil_index = model.rated_coil_index`.
        state.current = { ...state.current, ...stateFromEntry(pending.entry) };
        savedRatedIndex.current = (pending.entry.rated_coil_index as number | null) ?? null;
        // phase 2 (the rate) runs on the next pass so requiredFilled/coilType
        // have re-derived from the freshly applied state.
        pendingRestore.current = { entry: pending.entry, applyState: false };
        bump();
        return;
      }
      pendingRestore.current = null;
      // the 1 s `#rate` click (js:2402-2406) — deliberately NOT gated on
      // `entry.rate`; see the seed effect's note.
      autoRateRef.current = true;
      void doRate(savedRatedIndex.current ?? 0);
    }, 250);
    return () => window.clearTimeout(t);
  });

  // ---- SELECT (selectCoil — CODE-ONLY, userArmed — the FOUR-WAY write) ----
  // ★ UNREACHABLE IN HEADER MODE: `#select` is never rendered
  // (component_selector.tmpl:131-136) and the `:selector:select` subscription is
  // omitted below, so none of the writes here (section PATCH, project PATCH, the
  // __FORM_SHIM__ model.set, patchRatedCoil, draw:coil:connection, the drain-pan
  // nudge, rating_mode:hide:<id>) can ever run there.
  const getFormShim = (): ComponentFormShim | undefined => window.__FORM_SHIM__?.[modelId];

  // All three reads are scoped to THIS selector's own subtree via rootRef.
  // They used to go through chassisEl() + `#coil-selector …`, which was only
  // safe because that id was React-only: the root now carries the legacy
  // `coil_new` id (see the root element), which UvcSelector and
  // AccessorySelectorBody also use, and `.ahu-rate-content` is shared by every
  // mounted tab — so an id-based read there could match another selector.
  // Querying our own root is unambiguous. Discovery §7.3 trap 1.
  //
  // ★ `readGenericMfg` is the exception — it resolves the SELECTED ROW from
  // `state.data_id` rather than the DOM. Legacy's Select save reads the plain
  // field `self.generic_mfg` (js:3658), stamped in the change handler; the
  // option list is the same source of truth and the state read cannot be
  // desynced by render timing (the dialog is unmounted the moment Select is
  // clicked on the `fix/rd-select-close-immediately` branch). The DOM read is
  // kept as a fallback for the pre-seed case where `data_id` is not resolvable.
  const readGenericMfg = (): boolean => {
    const row = resolveMfgRow(state.current);
    if (row) return row.generic;
    const el = rootRef.current?.querySelector<HTMLSelectElement>('select[name="mfg_id"]') ?? null;
    const opt = el?.selectedOptions?.[0];
    const g = opt?.dataset?.generic;
    return g != null && g !== 'none' && g !== 'false';
  };
  const readMountingAngle = (): string | number | null => {
    const el =
      rootRef.current?.querySelector<HTMLInputElement>('input[name="mounting_angle"]') ?? null;
    return el?.value ?? (state.current.mounting_angle as any) ?? 0;
  };
  const readTxvRaw = (): unknown => {
    const el = rootRef.current?.querySelector<HTMLInputElement>('input[name="txv"]') ?? null;
    if (el) return el.checked;
    return state.current.txv;
  };

  const finishSelect = useCallback(() => {
    bus.emit('minRequiredLengthChange');
    bus.emit('rating_mode:hide');
    bus.emit(`rating_mode:hide:${modelId}`, { selected: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, bus]);

  const doSelect = useCallback(() => {
    userArmed.current = true;
    const rows = ratedRows ?? [];
    const rated = (rows[ratedIndex] ?? rows[0] ?? {}) as Record<string, any>;
    if (!rated || !rows.length) {
      finishSelect();
      return;
    }
    const shim = getFormShim();
    const s = state.current as Record<string, any>;
    const section = (activate.section ?? {}) as Record<string, any>;
    const project = { elevation: (s.elevation ?? section.elevation) } as Record<string, any>;
    const coilParams = parseParams((s as any).coil_params) || {};
    const hasCircuitings = ((masterListsRef.current.circuiting_id ?? []).length ?? 0) > 0;
    const hasFeeds = ((masterListsRef.current.feeds_id ?? []).length ?? 0) > 0;
    // js:3550-3560 — `Number(section.season_id) || null`, then the duty
    // fallback when that leaves nothing staged.
    const effectiveSeasonId = (Number(seasonIdRef.current) || null) ?? seasonIdForSaveFallback();

    // Sync K parity (dev 7cadbbe992): the WHOLE four-way payload (incl.
    // duty_id) is built synchronously here, before any await — legacy read
    // current_model.get('duty_id') after awaits, where a re-render could have
    // swapped the duty mid-chain; dev's fix caches save-data + duty up front,
    // which this pre-built payload already is. Do not restructure.
    const payload = buildCoilSelectPayload({
      s: { ...s, eadb_temp: s.eadb_temp, eawb_temp: s.eawb_temp },
      rated,
      section: { eadb_temp: s.eadb_temp, eawb_temp: s.eawb_temp },
      project,
      mountingAngle: readMountingAngle(),
      externalRating: externalRating.current,
      genericMfg: readGenericMfg(),
      txvRaw: readTxvRaw(),
      coilParams,
      hasCircuitings,
      hasFeeds,
      // Selector-parity — the connection_diameters list resolves the Manifold
      // inlet/outlet conn-dia ids from the rated result on Select (js:3713/:3735).
      connectionDiameters: masterListsRef.current.connection_diameters as any,
      // …and the rows / fpi lists resolve the rated result's ROWS and FIN
      // SPACING back to their ids the same way (js:3717/:3757). Both are the
      // same wrapped {item:{id,value}} shape getParamIdByNumberValue expects
      // (coilSelectorData.ts:710-711).
      rowsList: masterListsRef.current.rows_id as any,
      fpisList: masterListsRef.current.fpi_id as any,
      // the rate response's top-level notes (`self.NNotes`, js:3226/:3250 —
      // set on BOTH the vendor and the DB route, which share one handler).
      apiNotes: apiNotesRef.current,
      // J6 — the staged season rides the section save (js:3463-3485); the
      // per-season entering copy keys on the CMS-resolved value. With nothing
      // staged it falls back to the coil's duty, exactly as dev does at
      // :3554-3560 (see seasonIdForSaveFallback).
      seasonId: effectiveSeasonId,
      seasonValue: deriveSeasonValue(ahuDoc as ICmsAhuDoc | undefined, effectiveSeasonId),
    });

    // Close the rating dialog HERE — the last DOM read the chain needs
    // (readMountingAngle/readGenericMfg/readTxvRaw, all inside the payload
    // build above) has happened, so nothing below depends on this component
    // still being mounted. Parity with dev: `Dispatcher.trigger('rating_mode:
    // hide') … self.remove()` sit at exactly this point in selectCoil,
    // immediately after the `#mounting_angle_save_data` read and BEFORE
    // disableSyncIfSectionChanged (coil.selector.js:3863-3870) — "don't make
    // the user wait on the sync-disable PATCH, the section save, or the
    // cascade POST inside guardSelect; those all continue in the background
    // after the dialog is gone."
    finishSelect();

    // Dev-sync J4 — disableSyncIfSectionChanged (coil.selector.js:2340-2379,
    // sole call site :3793, AWAITED): if the entering pair about to be
    // written differs from the section's current values while sync is on,
    // PATCH synchronize_air_conditions=false FIRST — the section save that
    // follows relies on the flag being off to skip the cascade. Compare
    // semantics ported exactly (null/undefined prev, String compare).
    const syncDisabled: Promise<void> =
      !!(ahu as Record<string, unknown> | undefined)?.synchronize_air_conditions &&
      sectionAirFieldsChanged(payload.sectionSave, section)
        ? disableSyncOnAhu()
        : Promise.resolve();

    // (1) section.set → PATCH ahu/sections/<id> — FIRST, and the REST of the
    // select chain WAITS for it to settle (dev-sync H — coil.selector.js
    // :3562-3596 on merged dev): AHUCoil.save() backend-side only fills in
    // default entering-air temps when the section doesn't have them yet, so a
    // coil save racing ahead of this section PATCH can read stale/unset values
    // and stomp them back to defaults. Legacy gates on the section save XHR's
    // `.always` — success AND failure both release the chain.
    const secId = Number(section.id);
    const tunnelId = Number(section.tunnel_id);
    const sectionSaveSettled: Promise<unknown> = syncDisabled.then(() => {
      if (!secId) return undefined;
      return dispatch(
        ahuBoardFormsApi.endpoints.patchAhuSection.initiate({
          id: secId,
          tunnelId,
          data: payload.sectionSave,
        })
      )
        .unwrap()
        .then((saved) => {
          // J6 — legacy's section.set() runs the section model's autosave,
          // whose success fires `changed:section:<id>:eadb_temp:eawb_temp`
          // when the _summer entering pair is in the change-set
          // (section.js:231-234) — the live psychrometric-chart refresh.
          // (Its checkChangesAndValidate call no-ops on air keys — the
          // validated-field list has none of them — so it is not dispatched
          // here.)
          if (
            payload.sectionSave.eadb_temp_summer !== undefined ||
            payload.sectionSave.eawb_temp_summer !== undefined
          ) {
            bus.emit(`changed:section:${secId}:eadb_temp:eawb_temp`);
          }
          return saved;
        })
        .catch(() => undefined);
    });

    // Every request the commit chain fires, collected so the select chain
    // registered below settles only once the row on the server IS the row a
    // reopened dialog must seed from. Observation only — nothing is awaited
    // here, so the writes still fire in exactly the order (and with exactly the
    // fire-and-forget detachment) legacy has.
    const commitWrites: Array<Promise<unknown>> = [];

    const finishSelectCoil = () => {
      // (2) project.save(elevation rounded) → PATCH projects/<id> (custom_job/
      //     quote/report_logo explicitly undefined ⇒ omitted from the body).
      //     Dev-sync H made the legacy call an explicit {patch:true}
      //     (coil.selector.js:3580) — this port already PATCHed elevation only.
      const projectId = Number((ahu as any)?.project_id);
      if (projectId && payload.projectSave.elevation != null) {
        void dispatch(
          projectBoardApi.endpoints.patchProject.initiate({
            id: projectId,
            body: { elevation: payload.projectSave.elevation } as any,
          })
        );
      }
      // (3) current_model.save × 2 — commitSaveChain's two explicit coil PATCHes
      //     (coil.selector.js:3888-3895), in legacy order so the second's
      //     `mounting_angle: s.mounting_angle` still wins over the first's DOM
      //     read, exactly as it does on dev.
      //
      // Backbone `save(attrs, {patch:true})` PUTs `attrs` VERBATIM. This port
      // had replaced BOTH calls with `model.set()`, i.e. routed them through the
      // §49.2 debounced auto-save — which is not the same request at all:
      //   · it is hard-dropped while the model is unarmed (autoSaveModel.ts:283),
      //     and the arming only happens in the `rating_mode:hide:<id>` handler
      //     (useComponentForm.ts:283) fired by finishSelect() — so on the first
      //     Select of a page session NOTHING was sent (same defect
      //     FanSelector.tsx:2814 already documents); and
      //   · even once armed it strips every key still equal to `savedState`,
      //     which drops `duty_id` — normally unchanged, since the user picked
      //     Cooling before opening the dialog.
      // `duty_id` is exactly the key the drain pan hangs off: coil_viewset.py
      // :215-220 only sets `coil.select = True` (⇒ AHUCoil.save() creates the
      // section's drain pan, models_component_coil.py:1611-1630) when the body
      // carries a cooling `duty_id`. No PATCH, or a PATCH without duty_id, and
      // there is no drain pan and no validation request — the reported bug.
      // Send both bodies verbatim, as legacy does.
      commitWrites.push(
        dispatch(
          ahuBoardFormsApi.endpoints.patchConcreteRow.initiate({
            plural: 'coils',
            id: modelId,
            data: payload.currentModelPatch,
          })
        )
          .unwrap()
          .catch(() => undefined)
          .then(() =>
            dispatch(
              ahuBoardFormsApi.endpoints.patchConcreteRow.initiate({
                plural: 'coils',
                id: modelId,
                data: payload.currentModelSet,
              })
            )
              .unwrap()
              .catch(() => undefined)
          )
      );
      // …and mirror both onto the form model, which is what legacy's save() does
      // on the way past: the form re-renders with the rated values, and the
      // §49.2 pipeline runs checkChangesAndValidate (autoSaveModel.ts:403) —
      // the "validating…" request. Arm it first, for the reason above.
      shim?.model.markUserAction();
      shim?.model.set(payload.currentModelSet);
      shim?.model.set(payload.currentModelPatch);
      // (4) rated_coil_model.set → PATCH ahu/rating_mode_coils/<id>.
      commitWrites.push(
        patchRatedCoil({ coilId: modelId, data: payload.ratedCoilSave })
          .unwrap()
          .catch(() => undefined)
      );

      // draw:coil:connection(modelJSON, section.id, qty_stacked, duty_id, h_from_floor, mounting_angle).
      // Legacy emits the FULL rated_coil model — model.set(rated_coil_save_data)
      // then model.toJSON() (coil.selector.js:3473-3475) — which carries GET-only
      // fields like `settings` {clear_ups/downs_in_section,
      // in_section_clear_selection_id} (coil_serializers.py:242-262) that
      // drawCoilConnections reads. Emitting only the PATCH payload crashed the
      // canvas (coil.settings undefined at canvas3dGeometry drawCoilConnections)
      // and aborted the select. Mirror Backbone: fetched row merged with the set.
      bus.emit(
        'draw:coil:connection',
        { ...((ratedCoilModel ?? {}) as Record<string, unknown>), ...payload.ratedCoilSave },
        secId,
        Number(s.qty_stacked) || 0,
        Number(s.duty_id) || 0,
        (s.h_from_floor ?? null) as string | number | null,
        (payload.currentModelPatch.mounting_angle ?? null) as string | number | null
      );
      shim?.render();
      // drain-pan nudge: duty 1 + no drain-pan accessory row → activate the note.
      if (Number(s.duty_id) == 1) {
        const hasDrainPan = document.querySelector(
          `#forms_accessories_${secId} > ul > li.accessory.accessory_drain_pan`
        );
        if (!hasDrainPan) {
          document.querySelectorAll('.added-drain-pan-note').forEach((n) => n.classList.add('active'));
        }
      }
      // NB no finishSelect() here — the dialog closed on the click itself (see
      // above). This whole chain runs after the unmount, off the shim/board.
    };

    // Dev-sync J4 — the Checkpoint-4 switch (coil.selector.js:3820-3840):
    // once the section PATCH settles (success AND failure both release —
    // legacy `section_save_xhr.always(runGuardedSelect)`), POST
    // coils/<id>/select with `$.extend({}, current_model_save_data,
    // {mounting_angle: <DOM read>, duty_id})` ≡ {...currentModelSet,
    // ...currentModelPatch} (the extend overwrites both keys exactly like
    // legacy :3820-3823). onCommit = commitSaveChain (:3798-3814) — the
    // coil/project/rated-coil writes above, which therefore now run AFTER
    // the select POST, not merely after the section PATCH. No autoReload
    // flag — a non-empty cascade hard-reloads 150ms later (legacy default;
    // the commit chain's PATCHes race that reload in legacy too). No
    // onError — legacy passes none: a failed /select silently skips the
    // whole commit chain.
    const runGuardedSelect = () =>
      guardSelect({
        url: `/api/v1.0/ahu/coils/${modelId}/select`,
        analyticsComponent: 'coil',
        payload: { ...payload.currentModelSet, ...payload.currentModelPatch },
        onCommit: finishSelectCoil,
      });
    // `onCommit` runs BEFORE guardSelect resolves (cascadeCommit.ts:81-96), so
    // `commitWrites` is fully populated by the time it is read here.
    const selectChain = sectionSaveSettled
      .then(runGuardedSelect)
      .then(() => Promise.all(commitWrites));
    // …and a dialog reopened before that chain settles waits for it rather than
    // seeding from the pre-select row (selectWriteGate.ts).
    trackSelectWrites(selectWriteKey('coils', modelId), selectChain);
    void selectChain;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ratedRows, ratedIndex, modelId, activate, ahu, dispatch, patchRatedCoil, finishSelect, ratedCoilModel, seasonIdForSaveFallback]);

  // ---- show_notes / draw ----
  const onShowNotes = useCallback(() => {
    const list: Array<{ note: string }> = [];
    for (const n of apiNotes) list.push({ note: n });
    for (const n of (coilNotes ?? [])) {
      const t = (n as any).note ?? (n as any).text ?? (n as any).name;
      if (t) list.push({ note: String(t) });
    }
    showNotes(list);
  }, [apiNotes, coilNotes, showNotes]);

  // ★ The Draw popup is unreachable and therefore unmounted. Its only
  // trigger was a React-invented Draw button (no legacy coil template
  // renders one) and legacy's own `Drawing-output` pane has no nav entry.
  // `CoilDrawPopup` is left in the tree as a file so the pane can be
  // restored with its builder from git history if it is ever wired up.

  // ---- summary-row pick (click .coil-summary-row → changeCoil, js:3283-3289) ----
  const changeCoil = useCallback(
    (i: number) => {
      setRatedIndex(i);
      // ★ HEADER MODE: the row pick persists `rated_coil_index` (legacy
      // changeCoil → self.rated_coil_index = idx, js:3020, followed by the
      // render_outputs tail save at js:1475) so a reopen restores the SAME row.
      if (headerMode) saveInStorage({ rate: true, ratedIndex: i });
    },
    [headerMode, saveInStorage]
  );
  // ---- print (coil.selector.js:3710 — the coil.selector.print.tmpl sheet) ----
  // ★ Print is LIVE for coil in BOTH hosts: `#print` is rendered
  // (component_selector.tmpl:61 — one template for the board and the header
  // popup) and the chassis only adds `hide-print` for energy_wheel /
  // plate_heat_exchanger (component_selector.js:175/:183), so coil keeps a
  // clickable printer icon (discovery §7.1, observed `opacity .5→1`).
  //
  // ★ The print SHEET is what gets printed — NOT the dialog. `common.css`'s
  // print block hides the whole app (`body > *:not([class*=print]) { display:
  // none !important }`, :22138), so printing the live page yields BLANK paper.
  // Legacy renders `coil.selector.print.tmpl` into a `div.print-content`
  // appended to <body> and prints that; <CoilPrintSheet> is its port, mounted
  // through a portal while `printing` is true (see the render tail).
  const doPrint = useCallback(() => {
    // Legacy sets the document title before printing:
    //   document.title = `${self.project?.get('name')}_${data.ahu.tag}_` +
    //     `${self.section?.get('tag') ? `${self.section?.get('tag')}_` : ''}coil_selection`
    //   (coil.selector.js:3845)
    // ★ HEADER MODE — MIRRORED LEGACY BUG (the coil twin of the fan tab's
    // `undefined__fan_selection`, orchestrator decision 1). There
    // `self.project`/`self.section` are undefined and `data.ahu = self.ahu ?
    // self.ahu.toJSON() : {}` (js:3795) is `{}`, so `data.ahu.tag` is
    // `undefined` — giving the literal title `undefined_undefined_coil_
    // selection`. NOTE the coil string differs from fan's
    // `undefined__fan_selection`: fan reads `data.ahu_tag`, which is `''` not
    // `undefined` (fan.selector.js:7042). The single expression below produces
    // BOTH: the board has project/ahu/section, the header host has none.
    // ★ It is the `else` branch at :3844-3852 that runs, NOT the `!self.notes`
    // branch at :3826-3838 — that one WOULD throw on `self.section.get('tag')`
    // (no optional chaining there), but `self.notes` is always assigned by
    // getCollections (js:752, reached from the init call at js:609), so the
    // throwing branch is unreachable. Reproduced verbatim; do NOT "fix" either.
    printTitle.current = document.title;
    const project = (activate.project as { name?: string } | undefined)?.name ?? projectName;
    const tag = (activate.ahu as { tag?: string } | undefined)?.tag ?? ahuTag;
    const sectionTag = (activate.section as { tag?: string } | undefined)?.tag;
    document.title =
      `${project}_${tag}_${sectionTag ? `${sectionTag}_` : ''}coil_selection`;
    // mount the sheet; the effect below fires window.print() once it is on the
    // page, matching legacy's 200 ms defer (:4187-4193 / :4202-4208).
    setPrinting(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activate, projectName, ahuTag]);

  // legacy's print tail: `setTimeout(() => { window.print(); document.title =
  // title; $('.print-content').remove() }, 200)`. Unmounting the portal is the
  // `.remove()`.
  useEffect(() => {
    if (!printing) return undefined;
    // ★ Same logo race the fan sheet hit — see printSheetReady.ts. The 200 ms
    // is still there; it is now `max(200 ms, the sheet's images)`.
    let cancelled = false;
    void printSheetReady().then(() => {
      if (cancelled) return;
      window.print();
      if (printTitle.current != null) document.title = printTitle.current;
      printTitle.current = null;
      setPrinting(false);
    });
    return () => {
      cancelled = true;
    };
  }, [printing]);

  // `#print` availability. Legacy toggles it from TWO places, both of which run
  // on every open/re-render of the dialog and agree on the outcome:
  //   render_outputs tail (coil.selector.js:1609-1630) — off unless there is a
  //     rated coil, the row list is non-empty, no error and `is_rated`;
  //   checkButtonsAvailability (js:2259-2293, called at the end of render,
  //     js:1884) — off unless `rated_coil.model` is set and not `'None'`.
  // The rated row is seeded from the SAVED coil on open (`capacity_total != null`
  // → setRatedRows above, legacy's `setOutputs`/js:2006), which is why a coil
  // that was already selected has Print enabled BEFORE any re-rate — the board
  // chassis just never applied this gate. The same gate drives `#select`, which
  // is why legacy toggles the two together.
  useEffect(() => {
    const row = (ratedRows?.[ratedIndex] ?? ratedRows?.[0]) as
      | Record<string, unknown>
      | undefined;
    const model = row?.model;
    setPrintEnabled?.(
      Boolean(ratedRows?.length) &&
        !rateErrors.length &&
        model != null &&
        String(model) !== 'None'
    );
  }, [ratedRows, ratedIndex, rateErrors, setPrintEnabled]);

  // ---- RESET (`<name>_<id>:selector:reset`) — coil.selector.js:4600-4634 ----
  // The circular-arrow button in the rating-dialog header. The chassis
  // (ComponentSelectorDialog) renders it and emits the event; legacy's handler is
  // `reset:` and it does five things:
  //   1. remember the CURRENT manufacturer (js:4602-4603 reads the select's value
  //      + its `data-generic`, and js:4621-4631 re-selects that same option
  //      afterwards) — a reset returns the coil to defaults, it does not change
  //      the vendor;
  //   2. remember the active INPUT tab (js:4604-4607) and restore it at the end
  //      (js:4633);
  //   3. rewrite `model_data.params_coil` to `self.coilDefaults` + the five
  //      explicit pins (type 1 / duty 1 / fh 36 / fl 48 / connection_per_header 1)
  //      + `defaults_depends_on_duty['1_1']` (js:4609-4617), and mirror the
  //      entering-air pair onto `model_data.section` and the duty onto
  //      `model_data.model` (js:4618-4620) — all three land in the same flat
  //      `state.current` here (see the state-model note at the top of the file);
  //   4. `$select.trigger('change')` (js:4632) — the workhorse. It runs the whole
  //      `changeParams` manufacturer branch: `getCollections` re-fetches every
  //      mfg/type-dependent option list and re-picks any value the new lists no
  //      longer carry (js:869-1240), plus the synchronous mfg side effects
  //      (js:2781-2820) and the shared mfg/type tail (js:2846-2896). React's
  //      equivalent is `graph.onParentChange('mfg_id')` (the cascade — it walks
  //      the transitive descendants of mfg_id, i.e. type_id and everything under
  //      it) together with `coilMfgChangePatch`, exactly as `onSearchField`'s own
  //      `mfg_id` branch does at :1502-1512;
  //   5. because that change handler runs, `self.is_rated` goes false and
  //      `self.changed` true (js:2680 / :2702) — so `render` stops calling
  //      `render_outputs` and the previously rated row disappears. React models
  //      that with `setRatedRows(null)`, the same thing every other search-field
  //      change does at :1488.
  //
  // Deliberately NOT ported: legacy's `#select` enable/disable. Both branches of
  // `checkButtonsAvailability` that would have toggled it are commented out
  // (js:2273-2275 / :2281-2283 / :2290-2292), so legacy leaves Select exactly as
  // it was; here the effect at :1633 owns that flag and recomputes it off
  // `ratedRows`/`detail`. Also not ported: `saveInStorage`. In header mode legacy
  // explicitly skips the save on the mfg branch (`if (self.header && e.target.name
  // === 'mfg_id') {}`, js:2576-2577), and the header host does not render a Reset
  // button at all, so this stays defensive-only — nothing is written anywhere.
  //
  // `init.current` is intentionally left alone: unlike FanSelector, coil never
  // READS it (it is written at :831 and :997 and nowhere else), and re-syncing it
  // would encode "a reset is the new pristine state", the opposite of legacy's
  // `self.changed = true`.
  //
  // Read through a ref so the (deliberately un-dep'd) subscription below can hold
  // a stale `doReset` without holding a stale tab — see the effect's dep note.
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;

  const doReset = useCallback(() => {
    // (1) + (2)
    const keptMfg = state.current.mfg_id;
    // js:4602-4603 remembers the value AND the `data-generic` of the selected
    // option, and js:4621-4631 re-selects the option matching BOTH — i.e. a
    // reset on a private-label (generic) manufacturer comes back on the generic
    // option, not on its vendor twin. `data_id` identifies that pair exactly.
    const keptDataId = state.current.data_id;
    const keptTab = activeTabRef.current;

    // (3) js:4609-4620. Board mode spreads the BASE coilDefaults; header mode
    // spreads the header-merged one, which is what `self.coilDefaults` IS when
    // `options.header` is set (js:394-420).
    state.current = {
      ...state.current,
      ...(headerMode ? HEADER_COIL_DEFAULTS : BOARD_COIL_DEFAULTS),
      // coilDefaults pins `mfg_id: 27` (js:414), but js:4621-4631 immediately
      // re-selects the option that was selected before, so the manufacturer
      // survives the reset. Applied after the spread rather than filtered out of
      // it, to keep the object identical to legacy's.
      mfg_id: keptMfg,
      data_id: keptDataId,
      type_id: 1,
      duty_id: 1,
      fh: 36,
      fl: 48,
      connection_per_header: 1,
      // js:4616 + :4618-4619 — the '1_1' row carries eadb_temp/eawb_temp (the
      // SECTION pair) alongside the four model temperatures; the flat state holds
      // both, so one spread covers legacy's two writes.
      ...(DEFAULTS_DEPENDS_ON_DUTY['1_1'] ?? {}),
    };

    // (5) the rated output goes stale — `self.changed = true` (js:2702) is what
    // hides it, and the `#print` gate at :2254 follows `ratedRows`.
    setRatedRows(null);
    setRatedIndex(0);
    setRateErrors([]);
    setRateWarnings([]);
    setApiNotes([]);
    // `rating_datetime: null` is part of coilDefaults (js:377); the ref is the
    // print sheet's mirror of it.
    ratedDatetime.current = null;

    // (4) the `$select.trigger('change')` fan-out, in `onSearchField`'s order:
    // start the cascade first, then stamp the synchronous mfg side effects, so a
    // pin the new lists do not carry is still overridden when the cascade lands
    // (same note as :1497-1501).
    mfgChangedRef.current = true;            // js:2802 `self.changeMFG = true`
    cascadeInit.current = false;             // `getCollections(…, init=false)`
    graph.onParentChange('mfg_id');
    state.current = {
      ...state.current,
      ...coilMfgChangePatch(keptMfg as number | string | null, state.current, (typeId) =>
        coilTypeFromTypes(masterListsRef.current.type_id as any[], typeId as any)),
    };

    markChanged();
    // (2) restore. A no-op in practice — React has no equivalent of legacy's
    // `changeParams` stamping `self.activeTabId` off the changed element's own
    // tab (js:2694-2696), which is the only reason the legacy dance exists — but
    // kept so the guarantee is explicit rather than incidental.
    setActiveTab(keptTab);
    bump();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graph, markChanged, bump, headerMode]);

  // ---- chassis fan-out subscriptions (§30.4) ----
  useEffect(() => {
    const offs = [
      bus.on(`${eventName}_${scope}:selector:rate` as any, () => void doRate()),
      // ★ HEADER MODE omits `:selector:select` entirely. `.control-buttons`
      // (#select + #cancel) is wrapped in `{{#ifCompare show_tab '!=' 'all'}}`
      // (component_selector.tmpl:131-136), so the header chassis can never
      // dispatch it — legacy subscribes anyway (coil.selector.js:616-620) but the
      // handler is unreachable. Not subscribing makes header mode's ZERO-WRITES
      // property structural rather than incidental (discovery §3.1).
      ...(headerMode
        ? []
        : [bus.on(`${eventName}_${scope}:selector:select` as any, () => doSelect())]),
      bus.on(`${eventName}_${scope}:selector:cancel` as any, () => abortAnyRate()),
      // `#reset` — coil.selector.js:4600. NOT in this effect's dep array (same as
      // FanSelector.tsx:3255): `doReset` depends on `graph`, and
      // useDependencyGraph returns a fresh handle object every render, so listing
      // it would tear down and re-add every subscription on every render. The
      // captured closure is safe because doReset only touches stable setters,
      // live refs and `graph.onParentChange` (itself a stable useCallback).
      bus.on(`${eventName}_${scope}:selector:reset` as any, () => doReset()),
      bus.on(`${eventName}_${scope}:selector:show_notes` as any, () => onShowNotes()),
      bus.on(`${eventName}_${scope}:selector:print` as any, () => doPrint()),
      bus.on('cancel:dialog:changes', () => abortAnyRate()),
      // update_rating_dialog:<id> — hot-swap fresh models after a syncdata coil-
      // master diff (CoilForm emits it). Re-seed from the latest server detail.
      // ★ HEADER MODE has no CoilForm and no component row to re-fetch (legacy
      // subscribes it under the `coil_header` namespace at js:647-652 but nothing
      // ever emits it), so it is omitted along with the re-seed.
      ...(headerMode
        ? []
        : [
            bus.on(`update_rating_dialog:${modelId}` as any, () => {
              seeded.current = null;
              cascadeFired.current = false;
              void dispatch(
                ahuComponentFormsApi.endpoints.getComponentDetail.initiate(
                  { plural: 'coils', id: modelId },
                  { forceRefetch: true }
                )
              );
              void dispatch(
                ahuComponentFormsApi.endpoints.getComponentDetail.initiate(
                  { plural: 'rating_mode_coils', id: modelId },
                  { forceRefetch: true }
                )
              );
              // ★ `seeded.current = null` alone does NOT re-open the seed: it is
              // a REF, and the seed effect only re-runs when one of its deps
              // changes identity. Both refetches above can legitimately land on
              // rows RTK Query structurally shares with what is already cached
              // — the sizer's APPLY refreshes them before it emits this event,
              // precisely so the re-seed reads fresh data — in which case
              // `detail`/`ratedCoilModel` keep their references, nothing in the
              // dep array moves, and the dialog sits on the preloader for ever.
              // This nonce IS in the dep array, so the re-run is guaranteed.
              setReseedNonce((n) => n + 1);
              bump();
            }),
          ]),
    ];
    return () => offs.forEach((off) => off());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventName, scope, bus, headerMode, modelId, doRate, doSelect, abortAnyRate, onShowNotes, doPrint, dispatch, bump]);

  // abort any in-flight rate on unmount (data-safety).
  useEffect(() => () => abortAnyRate(), [abortAnyRate]);

  // ---- render ----
  if (detailLoading || coilDocLoading || seeded.current !== modelId) {
    // `loadingRef` (not `rootRef`) so `chassisEl()` — and with it the `#rate`
    // gate — still resolves while this placeholder is what is mounted.
    return (
      <div ref={loadingRef} className="preloader dotted-preloader for-empty-list active" />
    );
  }

  const s = state.current as Record<string, any>;

  // ---- the two EITHER/OR input pairs (legacy `disablePair`, js:2903-2923,
  // plus the template classes that re-assert the same state on every render).
  // Each pair is "give me a leaving condition OR give me the duty you want",
  // never both, and whichever half the user fills locks the other.
  //
  //   Capacity Total ⇄ LADB | LAWB   (data-pair, tmpl:647/:654 ↔ :697/:704)
  //   Fluid Flow     ⇄ LFT           (data-pair, tmpl:932 ↔ :945/:948)
  //
  // NONE of it was ported, so both halves stayed live and a rate could carry a
  // desired capacity AND a desired LADB, which the vendors treat as
  // contradictory. Legacy flips the classes live on `input` and then re-asserts
  // them from the model on the `change` re-render; React commits on blur, so
  // the post-commit state below is the whole observable behaviour.
  const capacityDriven = hasLen(s.desired_ladb_temp) || hasLen(s.desired_lawb_temp);
  // tmpl:647/:654 — mfg 190 (Super Radiator) always keeps LADB/LAWB live.
  const leavingAirLocked = mfgId !== 190 && hasLen(s.desired_capacity_total);
  // tmpl:694-700 — the class is gated on `external_rating`; the `tabindex="-1"`
  // beside it is NOT. Reproduced as-is rather than tidied.
  const capacityLocked = isExternal && capacityDriven;
  const fluidFlowLocked = hasLen(s.desired_leaving_fluid_temp);
  // tmpl:932 — LFT locks only when it is EMPTY and a fluid flow total exists.
  const lftLocked = !fluidFlowLocked && hasLen(s.fluid_flow_total);

  // The Manufacturer select's value is the option `_id` (legacy
  // `params_coil.data_id`), falling back to the plain catalog id whenever
  // `data_id` does not resolve to a loaded option — which covers a coil whose
  // manufacturer is no longer in `rating_mfg` (the synthetic trailing option
  // below) and any state seeded before `data_id` was tracked.
  const mfgSelectValue =
    resolveMfgRow(s)?.dataId ?? (s.mfg_id == null ? '' : String(s.mfg_id));
  // ★ The three manufacturer-dependent lists legacy loads from the MASTER
  // catalog per mfg+type, all of which the dependency cascade already fetches.
  // They used to be read off the coil CMS doc / the per-coil endpoint, which
  // are not filtered by the manufacturer at all:
  //   • duty            — see the getCoilDuties note above (1 option vs dev's 2).
  //   • HT Fluid        — `collections.fluids`, /ahu/fluids/types/<t>/duties/<d>/
  //                       ?mfg_id=<m> (collection/fluids.js:8-12, rendered at
  //                       coil.selector.tmpl:110). The CMS doc's `coil.htfluid`
  //                       is the WHOLE fluid table (37 rows), and filtering it
  //                       by duty alone keeps every refrigerant (they all list
  //                       duty 1) — a fluid/cooling coil offered ~36 fluids
  //                       where dev offers 3 (Water / Ethylene / Propylene).
  //   • Evaporator Mode — `collections.evaporator_modes`,
  //                       /coils/evaporator_modes/mfgs/<m>/types/<t>/
  //                       (collection/evaporator_modes.js:8-12, tmpl:140). The
  //                       CMS doc has NO `coil.evaporator_mode` key on dev3, so
  //                       the select rendered EMPTY — dev shows DX / Liquid Feed,
  //                       and four SGS-specific modes for mfg 190.
  // The master rows are {item:{id,name,value}}-wrapped except fluids, which are
  // flat; normalise to the flat {id,name,value} shape the JSX below reads.
  const unwrap = (rows: unknown): OptionRow[] =>
    ((rows ?? []) as Array<Record<string, any>>).map(
      (r) => ({ ...((r.item ?? r) as Record<string, unknown>) })
    ) as OptionRow[];
  const htfluids = unwrap(masterLists.htfluid_id);
  const evapModes = unwrap(masterLists.evaporator_mode_id);
  const airStreams = coilAirStreams(ahuDoc as CoilCmsDoc);
  const dutyRows: OptionRow[] = unwrap(masterLists.duty_id);

  // legacy puts the red asterisk on these labels for ONE coil_type only — it is
  // `class="{{#ifCompare coil_type '==' 'steam'}}required{{/ifCompare}}"` on the
  // label span, not the `required_field` on the control (that one is
  // unconditional and drives the #rate gate, not the asterisk). React rendered
  // none of them. `.required:after` (common.css:29083) is the asterisk.
  const reqSteam = coilType === 'steam';
  const reqFluid = coilType === 'fluid';

  /** what the module-scope field controls render from — see `CoilFieldCtx`.
   *  Rebuilt every render, so they see the current state and handlers. */
  const fieldCtx: CoilFieldCtxValue = {
    s,
    masterLists,
    detail,
    coilType,
    dutyValue: resolveDutyValue(),
    onModelField,
    onSearchField,
    onSectionField,
    onCheckboxField,
  };

  const heating = String(dutyRows.find((d) => d.id == s.duty_id)?.name ?? '') === 'Heating';
  // the legacy per-pane class list (PANE_CLASS) rather than one shared string —
  // the panes genuinely differ (mt-26 vs mt-0, float-left or not).
  //
  // ★ `mt-26` is dropped while the "Available Coils" row is on screen. That row
  // is legacy's `position: absolute` label (coil.selector.tmpl:31-42) and the
  // 26px is the space reserved for it — `.ahu-rate-content-item
  // #rating_mode_coil_content .coil_new .tab-pane.mt-26 { margin-top: 26px
  // !important }` (common.css:13794). This port deliberately renders the label
  // in NORMAL FLOW instead (see the comment on the row itself), so the pane is
  // already pushed down by the label's own height and the 26px is a second,
  // duplicate reservation: measured on dev3, an external-rating mfg puts the
  // pane at content-top + 26 (legacy's number) while SGS Refrigeration (190,
  // the only non-external mfg in the coil list, so the only one that renders
  // the row) put it at content-top + 55.
  //
  // Keyed on the row's own render condition rather than on `mfg_id === 190`:
  // the two are the same set here, and any manufacturer that does not show the
  // row keeps the legacy 26px untouched by construction.
  const tabClass = (t: InputTab) => {
    const pane = isExternal
      ? PANE_CLASS[t]
      : PANE_CLASS[t].replace(/\bmt-26\b/, '').replace(/\s+/g, ' ').trim();
    return `tab-pane fade${activeTab === t ? ' in active' : ''} ${pane}`;
  };

  // Manifold connection-diameter enable gates (coil.selector.js:1745-1747).
  const enableInlet = mfgId === 143 || mfgId === 79;
  const enableOutlet = mfgId === 143 || (mfgId === 79 && coilType !== 'steam');
  const connRequred = mfgId === 143 && coilType === 'steam';
  const isEvaporator = coilType === 'evaporator';

  // ----- rated outputs -----
  // 5e-sync (KA-5259): min_aw_height recomputed from the CURRENT h_from_floor/
  // mounting_angle before every output render (legacy updateRequiredAwHeights
  // at the top of render_outputs, coil.selector.js:1313/:2337 — those fields
  // are `without_rate`, so they re-render outputs WITHOUT invalidating the
  // rating; the h_from_floor/mounting_angle/flange change → render_outputs
  // wiring collapses to this derived compute in React).
  const rows = applyCoilRequiredAwHeights(
    (ratedRows ?? []) as Array<Record<string, any>>,
    s,
    coilCmsAw
  );
  const rated = (rows[ratedIndex] ?? {}) as Record<string, any>;

  // ---- legacy calculateOutputs (coil.selector.js:3290-3339) ----
  // face_area = fh × fl / 144, then every "All Coils" twin = qty_stacked × per
  // (calculateTotalParameter, js:3350-3355). Legacy mutates each rated coil in
  // place before render_outputs; here it is derived for the row on screen.
  // ★ NOT useMemo — everything from here down sits BELOW the loading early
  // return at :1956, so a hook here changes the hook count between the
  // preloader render and the loaded render (React #310). Plain computes only.
  const perf = ((): Record<string, any> => {
    const p: Record<string, any> = { ...rated };
    const qty = Number(s.qty_stacked);
    const fa = (Number(s.fh) * Number(s.fl)) / 144;
    if (Number.isFinite(fa)) p.face_area = fa;
    if (qty) {
      const total = (v: unknown) =>
        v == null || v === '' || !Number.isFinite(Number(v)) ? null : qty * Number(v);
      p.face_area_total = total(p.face_area);
      p.capacity_sensible_total = total(p.capacity_sensible);
      p.capacity_total_total = total(p.capacity_total);
      p.fluid_flow_total = total(p.fluid_flow);
      p.fluid_velocity_total = total(p.fluid_velocity);
      // dataFormater:4475 — the external-rating "All Coils" sensible cell.
      p.capacity_sensible_total_int = p.capacity_sensible_total;
      // volume_total (render_outputs context, js:1590).
      p.volume_total = total(String(p.fluid_internal_volume ?? '').replace(',', ''));
    }
    return p;
  })();

  // ---- the Coil-tab summary rows (legacy rated_coils_summary, js:1543-1561) ----
  const sectionAwWidth = parseFloat(String((activate.section as Record<string, unknown> | null)?.aw_width));
  const sectionAwHeight = parseFloat(String((activate.section as Record<string, unknown> | null)?.aw_height));
  const summaryRows = (() => {
    const fmt = (v: unknown, digits: number) =>
      v === null || v === undefined || isNaN(parseFloat(String(v)))
        ? '--'
        : Number(parseFloat(String(v))).toFixed(digits);
    // js:1533-1541 getSummaryFaceVelocity / getSummaryLawbTemp — the rated row
    // first, then the coil form's own values.
    const fv = (c: Record<string, any>) =>
      c.face_velocity_actual ?? c.face_velocity_std ?? s.face_velocity_actual ?? s.face_velocity_std;
    const lawb = (c: Record<string, any>) =>
      c.lawb_temp ?? c.desired_lawb_temp ?? s.desired_lawb_temp;
    return rows.map((c, i) => ({
      idx: i,
      active: i === ratedIndex,
      model: c.model || '--',
      rpi: typeof c.rpi === 'number' && isFinite(c.rpi) ? c.rpi.toFixed(2) : '--',
      aw_width: fmt(c.min_aw_width, 2),
      aw_height: fmt(c.min_aw_height, 2),
      aw_width_warning:
        c.min_aw_width != null && !isNaN(sectionAwWidth) && parseFloat(String(c.min_aw_width)) > sectionAwWidth,
      aw_height_warning:
        c.min_aw_height != null && !isNaN(sectionAwHeight) && parseFloat(String(c.min_aw_height)) > sectionAwHeight,
      rows: c.rows !== null && c.rows !== undefined ? c.rows : '--',
      fpi: fmt(c.fpi, 2),
      capacity_sensible_mbh: fmt(Number(c.capacity_sensible) / 1000, 2),
      capacity_total_mbh: fmt(Number(c.capacity_total) / 1000, 2),
      ladb_temp: fmt(c.ladb_temp, 2),
      lawb_temp: fmt(lawb(c), 2),
      face_velocity: fmt(fv(c), 0),
      apd: fmt(c.apd, 2),
      fluid_pd: fmt(c.fluid_pd, 2),
    }));
  })();

  // legacy gates the whole sheet on the rated coil (tmpl:20-29): an `error` or
  // a 'None' model collapses it to the error message; before the first rate
  // (`ratedRows == null`) render_outputs has never run and `.output-part` is
  // empty.
  // `external_rating` in the render_outputs context is the MFG's CMS flag
  // (self.external_rating_state.external_rating), which is exactly the
  // `isExternal` already derived above — not the "which route did the last
  // rate take" ref.
  const duty = resolveDutyValue();
  // ★ The SHEET is not gated on having models. Dev's collapse condition is
  // `{{#if rated_coil.error}}` / `{{#ifCompare rated_coil.model '==' 'None'}}`
  // (coil.rated.outputs.tmpl:19-27) against a `rated_coil` that is
  // `{...self.rated_coils[i]}` — i.e. `{}` when the vendor returned no models
  // (coil.selector.js:1479-1482). `{}.error` is falsy and `undefined == 'None'`
  // is false, so a 0-model rate falls through to the FULL sheet: an empty
  // summary table, and the Notes pane holding `errors` / `warnings`. Both
  // 0-model response branches then call render_warnings (js:3243/:3252), which
  // marks every output tab `non-clickable` and selects Notes (js:1984-2015).
  //
  // React collapsed the whole nav + panes to one line of red text, so the
  // vendor's reason — already captured into `rateErrors`, already rendered by
  // the Notes pane below — had nowhere to appear. dev says "15127 - Water
  // velocity 47.01 exceeds 10 fps"; dev3 said "Unable to find a coil that
  // satisfies all inputted parameters." and nothing more, which is what "Rate
  // doesn't work" looks like from the outside.
  const ratedErrored =
    ratedRows != null && (Boolean(rated.error) || String(rated.model) === 'None');
  const showSheet = ratedRows != null && !ratedErrored;
  /** render_warnings fired ⇒ the nav is frozen on Notes. */
  const warningsOnly = showSheet && rows.length === 0;
  const effOutputTab: OutputTab = warningsOnly ? 'notes' : outputTab;
  // `.rating` drives the panel heights (`.coil-rating-mode.rating {height:340px
  // !important}`, common.css:14998, and the `.output-group.rating` pair at
  // :15020-15027), so it must NOT drop out while a rate is in flight — now that
  // the round trip clears `ratedRows`, tying it to `hasRated` alone would snap
  // both panes 340px→310px→340px on every re-rate. Legacy never flickers: it
  // ADDS `.rating` to `.coil-rating-mode` on the Rate click itself
  // (coil.selector.js:3031) and re-adds it to `.output-part` in render_outputs
  // (:1604), and nothing between the two removes it. (Legacy is in fact sticky
  // thereafter — only changeProjectField :2389 ever removes it — but widening
  // this to match that would reflow the unrated pane, which is not what was
  // reported; holding it across the round trip is the part the re-rate needs.)
  // ★ `ratedRows != null`, not `hasRated`: render_outputs adds `.rating` on
  // EVERY pass (:1604), the 0-model one included, so the sheet that now renders
  // for a 0-model rate gets dev's 340px instead of collapsing to the unrated
  // 310px mid-way through reading the Notes.
  const ratingClass = ratedRows != null || rateInFlight ? ' rating' : '';

  // ---- print-sheet inputs (the legacy print() context, js:4143-4173) ----
  // `hand` / `style` are the option VALUES, not the labels: getConnHand
  // (js:2125-2135) and getStyle (js:2137+) both read `collection[i].value`, and
  // the drawing branches compare against 'right'/'left' and 'std'.
  const optValue = (field: string, listKey?: string): string => {
    const raw = s[field];
    if (raw == null || raw === '') return '';
    const row = (masterLists[listKey ?? field] ?? []).find(
      (r: any) => String(coilOptionId(r, field)) === String(raw)
    );
    return row ? String(coilOptionValue(row, field) ?? '').toLowerCase() : '';
  };
  const printHand = optValue('conn_hand_id');
  const printStyle = optValue('style_id');
  // coil.selector.print.tmpl:74 prints `{{params_coil.coil_mfg}}` — the option
  // TEXT after a manufacturer pick (js:2794), otherwise the rated row's
  // backend-computed `coil_mfg`. Both already resolve a private-label coil to
  // the OWN company name; the mfg-list lookup never can, because a generic twin
  // shares its vendor's catalog id. Falls back to the lookup when neither is
  // present (header mode before any pick).
  const printMfgName = s.coil_mfg ?? coilMfgName(coilDoc as CoilCmsDoc, s.mfg_id);
  // js:4090-4093 — casing_length = header-side flange + return-side flange + FL.
  const flangeWidth = (field: string): number => {
    const row = (masterLists.casing_flange_widths ?? []).find(
      (r: any) => String(coilOptionId(r, field)) === String(s[field])
    );
    // coilOptionLabel dereferences `row.item` — it is NOT null-safe.
    return row ? Number(coilOptionLabel(row, field)) || 0 : 0;
  };
  const printCasingLength =
    flangeWidth('casing_flange_header_side_id') +
    flangeWidth('casing_flange_return_side_id') +
    (Number(s.fl) || 0);
  // js:4172-4174 — the rated row's circuiting, else the selected circuiting's
  // own name.
  const printCircuiting =
    (rated.circuiting_rating_output as string | undefined) ||
    (() => {
      const row = (masterLists.circuiting_id ?? []).find(
        (r: any) => String(coilOptionId(r, 'circuiting_id')) === String(s.circuiting_id)
      );
      return row ? coilOptionLabel(row, 'circuiting_id') : '';
    })();

  // the RAE feeds cell reads the feeds OPTION's `value`, not its id (tmpl:163-168).
  const feedsValue = (masterLists.feeds_id ?? []).find((o) => String(o.id) === String(s.feeds_id))?.value;

  // `self.array_of_notes` — a rated `notes` string splits on newlines (js:1508-1514).
  const ratedNotes = ((): string[] => {
    const n = rated.notes;
    if (typeof n === 'string') return n.split('\n').filter(Boolean);
    return Array.isArray(n) ? n.map(String).filter(Boolean) : [];
  })();

  // ★ The root carries the legacy `coil_new` id AND class (tmpl:2). This is the
  // dialog's main styling hook: common.css keys 231 rules on `.coil_new` and 5
  // more on `#coil_new` — select line-height, tab-pane spacing, content-data
  // sizing, the 30px column gutters — most scoped under
  // `#rating_mode_coil_content`, which the chassis does render. Every sibling
  // selector (fan, uvc, accessory, ewheel) already carries it; coil was the one
  // that did not, so it alone missed the whole cascade.
  return (
    <CoilFieldCtx.Provider value={fieldCtx}>
    {/* ★ the input root and the output panel are SIBLINGS, as in the template:
        `#coil_new` (the input part, height 43%) CLOSES at coil.selector.tmpl
        :1802 and `.output-part` opens at :1804. React nested the output INSIDE
        the input root, so it inherited that height cap. */}
    {/* height/min-height are applied with `!important` by the layout effect
        above — see the note there; they cannot be set from the style prop. */}
    <div ref={rootRef} id="coil_new"
      className={`coil_new coil input-part aaa coil-rating-mode${ratingClass}`}>
      {/* input tab nav — the full legacy nav (coil.selector.tmpl:4-29). */}
      <div className="col-xs-1 row nav nav-tabs input-menu">
        <ul className="col-xs-12">
          <li className="cursor-default">Input</li>
          {INPUT_TABS.map((t) => (
            <li key={t}
              className={`input_label ${idPrefix}${PANE_ID[t]}${activeTab === t ? ' selected active' : ''}`}>
              <a className={activeTab === t ? 'active' : undefined}
                data-toggle="tab" href={`#${idPrefix}${PANE_ID[t]}`}
                onClick={(e) => { e.preventDefault(); setActiveTab(t); }}>{TAB_LABEL[t]}</a>
            </li>
          ))}
        </ul>
      </div>

      <div className="col-xs-11 tab-content content-data full-height right">
        {/* tmpl:31-42 — shown only for a non-external mfg; count comes from the
            effect above (`/api/v1.0/cms/1/v1.0.0/ahu/coils/all/count/`), which
            also gates #rate (js:1844-1852). Visible on every sub-tab, same as
            legacy (it sits above the tab panes, a sibling inside THIS column).
            ★ NOT `position: absolute` like legacy's markup — legacy's own
            layout there depends on `.tab-pane.mt-26 { margin-top: 26px
            !important }` (django/static/css/.../rating_mode.scss) to clear the
            overlaid label, but the React SPA only links common.css/header.css
            (templates/common/react.html:15-16) — rating_mode.scss is never
            loaded here, so that margin rule does not exist for this page and
            an absolutely-positioned copy just sits on top of Manufacturer with
            no reserved space. Left in NORMAL FLOW instead so the tab panes are
            pushed down by its actual height — correct regardless of which
            legacy stylesheet is or isn't loaded. */}
        {!isExternal && (
          <div className="row" style={{ paddingTop: 15, paddingBottom: 0, paddingLeft: 45, fontSize: 14 }}>
            Available Coils: <span id="qty">
              {coilAvailableCount == null ? '' : formatIntegerDisplay(coilAvailableCount)}
            </span>
          </div>
        )}
        {/* ★ ALL seven tab panes live inside ONE `.row` (coil.selector.tmpl:43,
            closing at :1801) — it is not decoration. Every pane is a
            `col-xs-12`, i.e. `padding: 0 15px`, and `.coil_new .row
            { margin-left: -15px; margin-right: -15px }` (common.css:15771) is
            what cancels that gutter. Without the wrapper the panes keep their
            padding uncancelled, so every Coil sub-page drew 15px to the right
            of dev's and 30px narrower. The "Available Coils" row above is a
            SIBLING of this one (tmpl:32), not part of it. */}
        <div className="row">
        {/* ----- Properties ----- */}
        {/* coil.selector.tmpl:45 `id="{{prefix}}Coil_Properties_Performance"` —
            `prefix: self.header ? "" : "ahu-"` (coil.selector.js:1409), so the pane
            ids drop the prefix in header mode and cannot collide with a board
            dialog's panes. */}
        <div className={tabClass('Properties')} id={`${idPrefix}${PANE_ID.Properties}`}>
          {/* tmpl:46 */}
          <div className="col-xs-12 col-md-12 col-lg-6 extra-padding-right">
            {/* Manufacturer (drives the rate path: external vs DB).
                ★ The option VALUE is the rating_mfg row's `_id`, not its `id` —
                a private-label generic twin shares the vendor's `id` and would
                otherwise be unselectable. See the mfgOptions note above; the
                selected option is tracked as `state.data_id`, legacy's
                `params_coil.data_id` (coil.selector.tmpl:60-62). */}
            <SelectRow label="Manufacturer" dataName="mfg_id">
              <select name="mfg_id" data-name="mfg_id" className="params_coil search_field"
                value={mfgSelectValue}
                onChange={(e) => onMfgSelect(e.target.value)}>
                {mfgSelectValue === '' && <option value="" />}
                {mfgOptions.map((m) => (
                  <option key={m.dataId} value={m.dataId} data-id={m.dataId}
                    data-value={String(m.id)} data-external_rating={String(m.external_rating)}
                    data-generic={m.genericAttr}>
                    {m.name}
                  </option>
                ))}
                {mfgSelectValue !== '' && !mfgByDataId.has(mfgSelectValue) && (
                  <option value={mfgSelectValue}>{coilMfgName(coilDoc as CoilCmsDoc, s.mfg_id) || String(s.mfg_id)}</option>
                )}
              </select>
            </SelectRow>
            <CoilSelect name="type_id" label="Type" />
            <SelectRow label="Duty" dataName="duty_id">
              <select name="duty_id" data-name="duty" className="search_field coil_field"
                value={s.duty_id == null ? '' : String(s.duty_id)}
                onChange={(e) => {
                  // J6 — a user duty pick re-locks the staged season
                  // (coil.selector.js:2442-2444: changeDefaultDependsOnTypeAndDuty
                  // + setSeasonForDuty inside the duty_id branch).
                  setSeasonForDuty(e.target.value);
                  onModelField('duty_id', e.target.value);
                }}>
                {s.duty_id == null && <option value="" />}
                {dutyRows.map((d) => (
                  <option key={String(d.id)} value={String(d.id)}>{optionLabel(d, 'name')}</option>
                ))}
              </select>
            </SelectRow>
            {/* HT Fluid | Mode (coil.selector.tmpl:101-149). The ROW and the
                htfluid select are UNCONDITIONAL in legacy (tmpl:100-114 sits at
                the same level as Type and Duty, inside no ifCompare) — only the
                two trailing halves are gated: glycol % for `coil_type ==
                'fluid'` (tmpl:115-137) and Evaporator Mode for `coil_type ==
                'evaporator'` (tmpl:138-146), which share the row with the
                select. React gated the whole row on fluid, so an evaporator /
                steam / condenser coil lost the select outright — and it is the
                one control whose option list is duty-filtered
                (/ahu/fluids/types/{type}/duties/{duty}/), i.e. refrigerants for
                an evaporator, Steam for a steam coil. */}
            {(() => {
              const selHt = htfluids.find((f: any) => String(f.id) === String(s.htfluid_id));
              // glycol input is disabled when the fluid is water (tmpl:129-131).
              const glycolWater =
                String((selHt as any)?.value ?? (selHt as any)?.name ?? '').toLowerCase() === 'water';
              // INTEGER: the box carries `data-step="5"` (tmpl:123), and
              // validateNumberField formats decimal ONLY for step 0.01
              // (js:4265-4266). `ksdm_ahu_component_coil.glycol_percent` is an
              // integer column, so `0.00` was never a value dev could print.
              const glDisp = formatIntegerDisplay(s.glycol_percent);
              return (
                <div className="row">
                  <div className="col-xs-5 label-part">
                    {/* tmpl:103 — the label carries the Evaporator Mode half. */}
                    <span data-name="htfluid_id">
                      Heat Transfer Fluid{vis.refrigerant ? ' | Mode' : ''}
                    </span>
                  </div>
                  <div className="col-xs-7 input-part">
                    <div className="row">
                      {/* legacy grid (tmpl:106-135): select col-xs-6, then the `%`
                          unit in its OWN col-xs-1 col-xs-offset-1, then the glycol
                          box in a col-xs-3. React had the select and input in a
                          flex row with the `%` hung off the LABEL instead. */}
                      <div className="col-xs-6">
                        <select name="htfluid_id" data-name="htfluid" className="coil_field search_field"
                          value={s.htfluid_id == null ? '' : String(s.htfluid_id)}
                          onChange={(e) => onModelField('htfluid_id', e.target.value)}>
                          {s.htfluid_id == null && <option value="" />}
                          {htfluids.map((f: any) => (
                            <option key={String(f.id)} value={String(f.id)}>{String(f.name ?? '')}</option>
                          ))}
                        </select>
                      </div>
                      {vis.fluidFlow && (
                        <>
                          <div className="col-xs-1 col-xs-offset-1">
                            <span className="percent">%</span>
                          </div>
                          {/* glycol % — fluid coil `.search_field` (tmpl:124);
                              feeds the vendor rate. Blank + inert for water. */}
                          <div className="col-xs-3 right">
                            {/* `glycolWater` belongs in the key. The fluids list
                                is fetched, so the FIRST render has no row for
                                `htfluid_id` and computes `glycolWater === false`
                                — the box mounts with `defaultValue={glDisp}`.
                                When the list lands the flag flips, but the key
                                is unchanged, so React never remounts and the
                                uncontrolled `defaultValue` is never re-applied:
                                a water coil kept the `0` it should never have
                                shown. dev emits NO `value` attribute at all for
                                water (tmpl:129-130). */}
                            <input type="text" data-type="number" name="glycol_percent" data-name="glycol_percent"
                              data-formatter_type="integer" data-step="5" autoComplete="off"
                              key={`gl-${s.htfluid_id}-${glycolWater ? 'water' : glDisp}`}
                              className={`search_field coil_field short-input${glycolWater ? ' non-clickable' : ''}`}
                              readOnly={glycolWater} tabIndex={glycolWater ? -1 : undefined}
                              defaultValue={glycolWater ? '' : glDisp}
                              onBlur={(e) => { if (!glycolWater && e.target.value !== glDisp) onSearchField('glycol_percent', e.target.value); }}
                              onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
                          </div>
                        </>
                      )}
                      {/* Evaporator Mode is the row's OTHER col-xs-6, beside the
                          refrigerant select — not a row of its own (tmpl:138-146). */}
                      {vis.refrigerant && (
                        <div className="col-xs-6">
                          <select name="evaporator_mode_id" data-name="evaporator_mode"
                            className="search_field coil_field"
                            value={s.evaporator_mode_id == null ? '' : String(s.evaporator_mode_id)}
                            onChange={(e) => onModelField('evaporator_mode_id', e.target.value)}>
                            {s.evaporator_mode_id == null && <option value="" />}
                            {evapModes.map((m: any) => (
                              <option key={String(m.id)} value={String(m.id)}>{String(m.name ?? '')}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}
            {/* Qty High / FH x FL / Rows / Fin Spacing / Style / Circuiting |
                Feeds — legacy's left-column order (tmpl:151-295). FH x FL and
                Circuiting | Feeds are ONE row each, two controls wide. */}
            {/* Qty High is `<input type="number">` — a real spinner — with
                step 1 and the CMS min/max, in a col-xs-6 (tmpl:156-164). React
                rendered a plain full-width text box. dev3 has no coil
                cms_settings fetch, so the legacy `{{else}}` bounds (min 1,
                max 3) apply, which is what dev falls back to as well. */}
            <SelectRow label="Qty High" dataName="qty_stacked" width="col-xs-6">
              <input type="number" data-type="number" autoComplete="off" step={1}
                min={1} data-min={1} max={3} data-max={3}
                name="qty_stacked" data-name="qty_stacked" placeholder="1"
                className="custom-padding-width coil_field required_field search_field has_default"
                key={`qty-${s.qty_stacked ?? 1}`}
                defaultValue={String(s.qty_stacked || 1)}
                // ★ MUST be onModelField, as the previous <Num> was (model
                // defaults true). onModelField carries the qty_stacked-specific
                // logic: the >= 1 clamp AND the recompute of EVERY All-Coils
                // total from its per-coil value (coil.selector.js:2532-2560).
                // Routing it through onSearchField instead dropped that
                // cascade, so Capacity Total / Fluid Flow never recomputed and
                // the rate went out without them.
                onBlur={(e) => {
                  if (String(e.target.value) !== String(s.qty_stacked || 1)) {
                    onModelField('qty_stacked', e.target.value);
                  }
                }}
                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
            </SelectRow>
            {/* FH x FL are SELECTS off the fhs / fls collections (tmpl:177-191),
                not numeric inputs. FH additionally carries `feeds_collection`,
                which is legacy's hook for reloading the feeds list when the
                finned height changes. */}
            <PairRow label="FH x FL" dataName="fh" unit="in" leftCls="qty-fl-select"
              left={<CoilSelectControl name="fh"
                cls="feeds_collection search_field params_coil required_field has_default" />}
              right={<CoilSelectControl name="fl"
                cls="search_field params_coil required_field has_default" />} />
            <CoilSelect name="rows_id" label="Rows" withNone />
            <CoilSelect name="fpi_id" label="Fin Spacing" unit="fpi" withNone />
            {/* Style — NOT rendered for a fluid coil (tmpl:232 gates the whole
                row on `coil_type != 'fluid'`); React showed an empty row there.
                It is a 39px `.short-select` in a col-xs-6, and legacy flips
                data-name to "style" for mfg 27, else "coil_style". */}
            {coilType !== 'fluid' && (
              <SelectRow label="Style" dataName="style_id" width="col-xs-6">
                <CoilSelectControl name="style_id" cls="short-select params_coil search_field"
                  dataName={mfgId === 27 ? 'style' : 'coil_style'} />
              </SelectRow>
            )}
            <PairRow label="Circuiting | Feeds" dataName="circuiting_id"
              left={<CoilSelectControl name="circuiting_id" withNone />}
              right={<CoilSelectControl name="feeds_id" withNone noneValue={mfgId === 181 ? '--' : 'None'} />} />
          </div>
          {/* tmpl:298 */}
          <div className="col-xs-12 col-md-12 col-lg-6 performance-group">
            {/* Tube Dia and Tube Thickness get a full row each, both with the
                `in` unit (tmpl:299-345), as on dev. */}
            <CoilSelect name="tube_dia_id" label="Tube Dia" unit="in" />
            <CoilSelect name="tube_wall_thick_id" label="Tube Thickness" unit="in" />
            <CoilSelect name="tube_mtl_id" label="Tube Mtl" />
            <CoilSelect name="fin_mtl_id" label="Fin Material" />
            <CoilSelect name="fin_finish_id" label="Fin Finish" />
            <CoilSelect name="fin_thick_id" label="Fin Thickness" unit="in" />
            <CoilSelect name="fin_pattern_id" label="Fin Pattern" withNone />
            <CoilSelect name="casing_mtl_id" label="Casing Material" required={reqSteam} />
            {/* Hand — the LIVE conn_hand_id on the Coil tab (tmpl:466; the
                Manifold copy at tmpl:1189-1204 is legacy-commented). */}
            <CoilSelect name="conn_hand_id" label="Hand" required={reqSteam} />
          </div>
        </div>

        {/* ----- Performance (coil_type-dependent) ----- */}
        <div className={tabClass('Performance')} id={`${idPrefix}Coil_Performance`}>
          {/* tmpl:511 */}
          <div className="col-xs-12 col-md-12 col-lg-6 extra-padding-right">
            <PerAllHeadings />
            {/* Airflow heads the pane and is a Per Coil | All Coils PAIR
                (tmpl:512-532) — React rendered only the all-coils half. */}
            <PerAllRow label="Airflow" unit="CFM" per="airflow_actual" all="airflow_actual_total"
              formatter="integer" required={isExternal}
              perBounds={coilAirflowBounds(s, true)} allBounds={coilAirflowBounds(s, false)} />
            <SelectRow label="Air Stream" dataName="air_stream_id" required width="col-xs-6">
              <select name="air_stream_id" data-name="air_stream" className="model_field coil_field"
                value={s.air_stream_id == null ? 'None' : String(s.air_stream_id)}
                onChange={(e) => onModelField('air_stream_id', e.target.value)}>
                {/* legacy always renders the `- -` option (tmpl:541) */}
                <option value="None">- -</option>
                {airStreams.map((a: any) => (
                  <option key={String(a.id)} value={String(a.id)}>{String(a.name ?? '')}</option>
                ))}
                {s.air_stream_id != null && !airStreams.some((a: any) => String(a.id) === String(s.air_stream_id)) && (
                  <option value={String(s.air_stream_id)}>{String(s.air_stream_id)}</option>
                )}
              </select>
            </SelectRow>
            {/* Face Velocity | APD is ONE row for a DB-rated coil (tmpl:551-571);
                an external-rating mfg gets Face Velocity alone (tmpl:574-587). */}
            {isExternal ? (
              /* tmpl:578-586 nests the lone input in a `col-xs-6`, exactly like
                 the two-input branch below — without the width it fell back to
                 SelectRow's `col-xs-12` default and stretched across both the
                 Per Coil and All Coils columns. */
              /* ★ EDITABLE, both branches: legacy renders a plain `lg-input
                 coil_field search_field` here (tmpl:569 and :592). It was
                 read-only, which killed the whole airflow ⇄ face velocity
                 relationship — typing a face velocity is dev's way of asking
                 for an airflow (js:3457-3466), and the box otherwise sat on a
                 stale 500 while the airflow moved underneath it. */
              <Num name="face_velocity_actual" label="Face Velocity" unit="fpm" width="col-xs-6"
                formatter="integer" bounds={{ min: 0, step: 1 }} />
            ) : (
              <PairRow label="Face Velocity | APD" dataName="face_velocity_actual" unit="fpm | in WC"
                left={<NumControl name="face_velocity_actual" formatter="integer" bounds={{ min: 0, step: 1 }} />}
                right={<NumControl name="apd" />} />
            )}
            {/* Dev-sync J6 + sync K — the Season select (coil.selector.tmpl:
                589-605, unchanged by sync K): option list restricted by duty
                (dev render logic :1700-1716 on 9ab54f1b01) — Cooling → Summer
                only; Heating → Summer AND Winter, never "Summer + Winter"
                (a heating coil has one set of rated entering/leaving inputs
                and must map to exactly one season); other → full list. The
                wrapper div goes non-clickable only when the duty is NEITHER
                1 nor 2 (tmpl:595) — heating is genuinely interactive now,
                cooling stays a clickable single-option select, exactly like
                the legacy template. Displayed value = staged ?? the duty's
                default season (dev :1686-1699, resolved by VALUE; heating
                defaults to Winter unless in reheat position — heatingReheat
                — then Summer); the None option is the hidden `- -`. ★ NOT
                replicated (survey §B6): the legacy select carries BOTH
                `.section_field` and `.coil_field` → a double handler that
                also stamps a phantom model_data.model.season_id — here ONE
                handler stages seasonIdRef only. */}
            {(() => {
              const coilSeasons = cmsSeasonsByComponent(
                ahuDoc as ICmsAhuDoc | undefined,
                'Coil'
              );
              const dutyNum = Number(s.duty_id);
              const dutyKnown = dutyNum === 1 || dutyNum === 2;
              const options =
                dutyNum === 1
                  ? coilSeasons.filter((r) => r.value === 'summer')
                  : dutyNum === 2
                    ? coilSeasons.filter((r) => r.value === 'summer' || r.value === 'winter')
                    : coilSeasons;
              // ★ the NULLABLE duty resolution: dev's getDuty leaves
              // `self.duty` undefined when the coil's duty matches no row, so
              // no default season is derived and the select shows `- -`. The
              // 'cooling' fallback of resolveDutyValue() would have shown
              // "Summer" there instead.
              const dutyValue = resolveDutyValueOrNull();
              const dutyDefaultValue =
                dutyValue === 'heating'
                  ? heatingReheat
                    ? 'summer'
                    : 'winter'
                  : dutyValue === 'cooling'
                    ? 'summer'
                    : null;
              const dutyDefault = dutyDefaultValue
                ? coilSeasons.find((r) => r.value === dutyDefaultValue)
                : undefined;
              const rawSelectedId =
                seasonIdRef.current ?? (dutyDefault ? Number(dutyDefault.id) : null);
              // ★ A season the duty filter excludes (e.g. a section still
              // carrying "Summer + Winter" from the plenum-era create defaults
              // — season 3 is not in season_by_component.Coil at all) leaves
              // NO option carrying `selected` in the legacy template, so the
              // browser falls back to the first one: the `- -` placeholder. A
              // controlled React select given an unmatched value instead
              // renders selectedIndex -1 — a blank box. Snap to the
              // placeholder so the two agree.
              const selectedId =
                rawSelectedId != null &&
                options.some((r) => Number(r.id) === Number(rawSelectedId))
                  ? rawSelectedId
                  : null;
              return (
                /* tmpl:594-595 — `col-xs-6`, and `non-clickable` rides on THAT
                   div, not on an extra wrapper inside it. The missing width let
                   the select stretch the full input-part. */
                <SelectRow label="Season" dataName="season_id"
                  width={`col-xs-6${dutyKnown ? '' : ' non-clickable'}`}>
                  <select
                    name="season_id"
                    className="section_field"
                    value={selectedId == null ? 'None' : String(selectedId)}
                    onChange={(e) => {
                      // Sync K parity (dev 2242ac5697): a season change
                      // stages seasonIdRef + marks changes ONLY — ratedRows
                      // stays as-is and Select stays enabled (selectEnabled
                      // derives from ratedRows/detail), so switching
                      // seasons never invalidates a rating on screen.
                      seasonIdRef.current = parseInt(e.target.value, 10) || null;
                      markChanged();
                      bump();
                    }}
                  >
                    <option className="hide" value="None">- -</option>
                    {options.map((r) => (
                      <option key={String(r.id)} value={String(r.id)}>
                        {String(r.name ?? '')}
                      </option>
                    ))}
                  </select>
                </SelectRow>
              );
            })()}
            {/* EADB | EAWB (section fields) */}
            <TempPairRow label="EADB" rightLabel="EAWB" dataName="eadb_temp" required={isExternal}
              left={<NumControl name="eadb_temp" section />}
              right={<NumControl name="eawb_temp" section />} />
            {/* Selector-parity LADB/LAWB FIX: legacy binds `name="desired_ladb_temp"`
                / `desired_lawb_temp` to `model.desired_ladb_temp` (tmpl:646/:653) —
                the exact key all three rate builders read (buildVendorRateInput,
                COIL_DB_INPUT_FIELDS, buildHeaderVendorRateInput). The prior
                `ladb_temp`/`lawb_temp` names wrote a state key nothing read → the
                typed desired leaving temp never reached the vendor (cooling-coil
                data loss). */}
            <TempPairRow label="LADB" rightLabel="LAWB" dataName="desired_ladb_temp" required={isExternal}
              left={<NumControl name="desired_ladb_temp" readOnly={leavingAirLocked} bounds={AIR_TEMP_BOUNDS} />}
              right={<NumControl name="desired_lawb_temp" readOnly={leavingAirLocked} bounds={AIR_TEMP_BOUNDS} />} />
            {/* Capacity Sensible (per coil) | Capacity Total (all coils) — !external
                (tmpl:662-683). */}
            {!isExternal && (
              <PerAllRow label="Capacity Sensible" unit="btu/hr"
                per="capacity_sensible" all="capacity_total" formatter="integer"
                perBounds={CAPACITY_BOUNDS} allBounds={CAPACITY_BOUNDS} />
            )}
            {/* Capacity Total: desired per-coil | all-coils (tmpl:685-709). */}
            <PerAllRow label="Capacity Total" unit="btu/hr" required={isExternal}
              per="desired_capacity_total" all="desired_capacity_total_total" formatter="integer"
              locked={capacityLocked} tabSkip={capacityDriven}
              perBounds={CAPACITY_BOUNDS} allBounds={CAPACITY_BOUNDS} />
            {/* Entering Superheat + Steam Pressure close the LEFT column for a
                steam coil (tmpl:710-737) — they were rendered at the foot of the
                RIGHT one, alongside a `Condensate` box legacy does not have in
                the selector at all (condensate is a rated output). */}
            {vis.steam && (
              <>
                <Num name="entering_superheat" label="Entering Superheat" unit="F" required width="col-xs-6" />
                <Num name="steam_pressure" label="Steam Pressure" unit="PSI" required width="col-xs-6" />
              </>
            )}
          </div>
          {/* tmpl:740 */}
          <div className="col-xs-12 col-md-12 col-lg-6 performance-group">
            {/* the right column repeats the headings, but legacy hides them for
                a non-fluid coil (`style='opacity: 0'`, tmpl:495) — Fluid Flow is
                the column's only Per Coil | All Coils pair. */}
            <div style={vis.fluidFlow ? undefined : { opacity: 0 }}>
              <PerAllHeadings />
            </div>
            {/* Altitude — readonly project elevation, heads the column
                (tmpl:741-753). Was missing entirely. */}
            <Num name="elevation" label="Altitude" unit="ft" formatter="integer" readOnly width="col-xs-6" fallback={0} />
            {/* legacy's three type blocks, in template order and each gated on
                ONE type: condenser (tmpl:754-839), evaporator (tmpl:841-906),
                fluid (tmpl:908-969). */}
            {vis.condenser && (
              <>
                <Num name="vapor_temp" label="Vapor Temp" unit="F" required width="col-xs-6" />
                <Num name="ref_liquid_temp" label="Liquid Ref. Temp" unit="F" required width="col-xs-6" />
                <Num name="condensing_temp" label="Condensing Temp" unit="F" required width="col-xs-6" />
                <Num name="subcooling_temp" label="Subcooling Temp" unit="F" required width="col-xs-6" />
                <Num name="percent_condensing_load" label="Condensing Load" unit="%" required width="col-xs-6" />
              </>
            )}
            {vis.refrigerant && (
              <>
                {/* SST and Liquid Ref. Temp OPEN the evaporator block
                    (tmpl:842-868); they were being drawn after the superheat
                    rows, out of the `condenser` gate. `Refrigerant Charge` is
                    dropped — legacy has no such input in the selector. */}
                <Num name="suction_temp" label="Saturated Suction Temp" unit="F"
                  required={isExternal} width="col-xs-6" fallback={45} />
                <Num name="ref_liquid_temp" label="Liquid Ref. Temp" unit="F"
                  required={isExternal} width="col-xs-3" fallback={110} />
                {/* Vapor Velocity | FPD — the whole ROW is `{{#unless
                    external_rating}}` (tmpl:867-885), not just its right half. */}
                {!isExternal && (
                  <PairRow label="Vapor Velocity | FPD" dataName="tube_vapor_velocity" unit="fps | PSI" cell="col-xs-3"
                    left={<NumControl name="tube_vapor_velocity" fallback={8} />}
                    right={<NumControl name="fluid_pd" />} />
                )}
                {/* "Superheat Temp / Leaving" — legacy's `Ent` suffix sits under
                    `coil_type != 'evaporator'` INSIDE the evaporator block
                    (tmpl:889), so it never renders; the ` / Leaving` half is
                    DB-rated only. */}
                {isExternal ? (
                  <Num name="entering_superheat" label="Superheat Temp" unit="F" width="col-xs-3" fallback={8} />
                ) : (
                  <PairRow label="Superheat Temp / Leaving" dataName="entering_superheat" unit="F" cell="col-xs-3"
                    left={<NumControl name="entering_superheat" fallback={8} />}
                    right={<NumControl name="leaving_superheat" model={false} />} />
                )}
              </>
            )}
            {vis.fluidFlow && (
              <>
                {/* legacy fluid order: EFT, LFT, Fluid Flow, Fouling in / out
                    (tmpl:909-969). Fluid Velocity / Fluid Pressure Drop are NOT
                    Performance inputs in legacy — they are rated outputs — so
                    they are dropped here. */}
                <Num name="entering_fluid_temp" label="EFT" unit="F" required width="col-xs-6"
                  bounds={TEMP_BOUNDS} />
                <Num name="desired_leaving_fluid_temp" label="LFT" unit="F" required width="col-xs-6"
                  readOnly={lftLocked} bounds={TEMP_BOUNDS} />
                {/* tmpl:945/:948 — the per-coil box has no data-max; the
                    All-Coils box caps at 1000 gpm. */}
                <PerAllRow label="Fluid Flow" unit="gpm" per="fluid_flow" all="fluid_flow_total" required
                  locked={fluidFlowLocked} blank={fluidFlowLocked}
                  perBounds={{ min: 0, step: 0.01 }} allBounds={{ min: 0, step: 0.01, max: 1000 }} />
                {/* the degree ring sits on the F INSIDE this unit, not on the
                    whole span (tmpl:956). */}
                <PairRow label="Fouling in / out" dataName="fouling_factor_inside" cell="col-xs-3"
                  unit={<>hr.ft2.<span className="temp">F</span>/btu</>}
                  left={<NumControl name="fouling_factor_inside" fallback={0} />}
                  right={<NumControl name="fouling_factor_outside" fallback={0} />} />
              </>
            )}
            {/* hidden mounting-angle mirror the legacy reads at Select
                (readMountingAngle); the Properties tab renders one too. */}
            <input type="hidden" name="mounting_angle" value={String(s.mounting_angle ?? 0)} readOnly />
          </div>
        </div>

        {/* ----- Manifold (Headers & Connections — coil.selector.tmpl:975-1293) ----- */}
        <div className={tabClass('Manifold')} id={`${idPrefix}Coil_Headers_Connections`}>
          <div className="col-xs-12 sub-titles">
            <div className="col-xs-6 extra-padding-right">
              <div className="row text-uppercase sub-titles__text">Headers</div>
            </div>
            <div className="col-xs-6">
              <div className="col-xs-12 text-uppercase sub-titles__text">Connections</div>
            </div>
          </div>
          {/* tmpl:988 — Material heads the Headers group */}
          <div className="col-xs-12 col-md-12 col-lg-6 extra-padding-right">
            <CoilSelect name="header_mtl_id" label="Material" required={reqFluid} />
            {/* header diameters are VALUE-keyed off the connection_diameters list;
                reset_field/non-clickable unless enable_outlet/enable_inlet. */}
            <CoilSelect name="outlet_header_dia" label="Outlet" unit="in" listKey="connection_diameters"
              withNone hideNone={connRequred} disabled={!enableOutlet} width="col-xs-6" />
            <CoilSelect name="inlet_header_dia" label="Inlet" unit="in" listKey="connection_diameters"
              withNone hideNone={connRequred} disabled={!enableInlet} width="col-xs-6" />
            {/* Distributor 1/2 — mfg 79 evaporator (tmpl:1070-1109; legacy reuses
                the inlet_header_dia binding — a faithful quirk). */}
            {mfgId === 79 && isEvaporator && (
              <>
                <CoilSelect name="inlet_header_dia" label="Distributor - 1" unit="in" listKey="connection_diameters" withNone width="col-xs-3" />
                <CoilSelect name="inlet_header_dia" label="Distributor - 2" unit="in" listKey="connection_diameters" withNone width="col-xs-3" />
              </>
            )}
          </div>
          {/* tmpl:1112 — Material heads the Connections group */}
          <div className="col-xs-12 col-md-12 col-lg-6 connections-group">
            <CoilSelect name="conn_mtl_id" label="Material" required={reqFluid} />
            {/* half-width, as in the Headers group above — tmpl:1139/:1159 wrap
                both selects in a `col-xs-6`, not the default col-xs-12. */}
            <CoilSelect name="outlet_conn_dia_id" label="Outlet" unit="in" listKey="connection_diameters"
              withNone hideNone={connRequred} disabled={!enableOutlet} width="col-xs-6" />
            <CoilSelect name="inlet_conn_dia_id" label="Inlet" unit="in" listKey="connection_diameters"
              withNone hideNone={connRequred} disabled={!enableInlet} width="col-xs-6" />
            <CoilSelect name="conn_end_id" label="End" required={reqSteam} />
            <CoilSelect name="conn_pipe_end_id" label="Pipe End" withNone={mfgId === 21} required={reqSteam} />
            <Num name="conn_protrusion_from_header" label="Protrusion from Header" unit="in" model={false} required={reqSteam}
              style={{ textAlign: 'left' }} />
            {coilType !== 'steam' && (
              <SelectRow label="Connection per Header" dataName="connection_per_header" width="col-xs-6">
                <select name="connection_per_header" data-name="connection_per_header" className="params_coil search_field"
                  value={s.connection_per_header == null ? '1' : String(s.connection_per_header)}
                  onChange={(e) => onSearchField('connection_per_header', e.target.value)}>
                  <option value="1">1</option>
                  <option value="2">2</option>
                </select>
              </SelectRow>
            )}
            {/* Connection Rotation — fluid only (tmpl:1253-1271). */}
            {vis.fluidFlow && <CoilSelect name="conn_rotation_id" label="Connection Rotation" />}
            {/* Auxiliary Side Connections — mfg 21 evaporator (tmpl:1273-1289). */}
            {mfgId === 21 && isEvaporator && <Checkbox name="aux_side_conn" label="Auxiliary Side Connections" />}
          </div>
        </div>

        {/* ----- Casing (coil.selector.tmpl:1364-1515) ----- */}
        <div className={tabClass('Casing')} id={`${idPrefix}Coil_Casing_Options`}>
          {/* tmpl:1365 */}
          <div className="col-xs-12 col-md-12 col-lg-6 extra-padding-right">
            {/* Material + Type head the Casing pane (tmpl:1366-1390). NOTE
                casing_mtl_id is legitimately rendered TWICE — legacy also shows
                it on the Coil tab as "Casing Material" (tmpl:444). */}
            <CoilSelect name="casing_mtl_id" label="Material" required={reqSteam} />
            <CoilSelect name="casing_type_id" label="Type" withNone hideNone required={reqSteam} />
            <CoilSelect name="casing_flange_header_side_id" label="Flange Header" unit="in" listKey="casing_flange_widths" required={reqSteam} />
            <CoilSelect name="casing_flange_return_side_id" label="Flange Return" unit="in" listKey="casing_flange_widths" required={reqSteam} />
            {/* Flange Top/Bottom feed the vendor rate + the AW-height recompute
                (their width VALUE is synced to casing_flange_top/btm). */}
            <CoilSelect name="casing_flange_top_id" label="Flange Top" unit="in" listKey="casing_flange_widths" required={reqSteam} />
            <CoilSelect name="casing_flange_btm_id" label="Flange Bottom" unit="in" listKey="casing_flange_widths" required={reqSteam} />
            <CoilSelect name="fastening_method_id" label="Fastening method" cls="has_default" required={reqSteam} />
            <Checkbox name="mounting_holes" label="Mounting Holes" width="col-xs-6" />
            {/* Stacking Flanges — mfg 21 (tmpl:1499-1513). */}
            {mfgId === 21 && <Checkbox name="casing_stacking_flange" label="Stacking Flanges" />}
          </div>
        </div>

        {/* ----- Options (coil.selector.tmpl:1516-1751) ----- */}
        <div className={tabClass('Options')} id={`${idPrefix}Coil_Options`}>
          {/* tmpl:1517 */}
          <div className="col-xs-12 col-md-12 col-lg-6 extra-padding-right options-group">
            {/* Tube Spacing Vert./Hor. — feed the vendor rate; the value stays in
                lockstep with the id via the tube-spacing sync effect. */}
            <PairRow label="Tube Spacing Vert./Hor." dataName="tube_vert_spacing_id" unit="in"
              left={<CoilSelectControl name="tube_vert_spacing_id" />}
              right={<CoilSelectControl name="tube_hor_spacing_id" />} />
            {/* Fin Spacing Maximum — reads the shared fpi_id list (tmpl:1551). */}
            <CoilSelect name="fpi_max_id" label="Fin Spacing Maximum" unit="fpi"
              cls="coil_field search_field" listKey="fpi_id" width="col-xs-6" />
            {/* Air PD Maximum — search_field; feeds the vendor rate (default 3). */}
            <Num name="apd_max" label="Air PD Maximum" unit="in WC" model={false} width="col-xs-6" />
            {/* Fluid PD Maximum — fluid only; feeds the vendor rate (default 20). */}
            {vis.fluidFlow && <Num name="fluid_pd_max" label="Fluid PD Maximum" unit="ft WC" model={false} width="col-xs-6" fallback={20} />}
            {vis.steam && <Checkbox name="vacuum_breaker" label="Vacuum Breaker" />}
            {vis.steam && <Checkbox name="thermostatic_vent" label="Thermostatic Air Vent." />}
            {/* Vent & Drain, then Tube Interior (tmpl Options order). Both sit
                INSIDE the `coil_type == 'fluid'` block (tmpl:1624-1675) that
                also holds Tube Cleanable — they were the only two of the three
                left ungated, so they showed on evaporator/condenser/steam. */}
            {vis.fluidFlow && <CoilSelect name="vent_drain_id" label="Vent & Drain" />}
            {vis.fluidFlow && <CoilSelect name="tube_interior_id" label="Tube Interior" />}
            {vis.fluidFlow && <CoilSelect name="tube_cleanable_id" label="Tube Cleanable" withNone />}
            {isEvaporator && <Checkbox name="txv" label="TXV" />}
            {isEvaporator && <Checkbox name="txv_instl" label="TXV Installation" disabled={!parseTxv(s.txv)} />}
            {coilType !== 'steam' && <Checkbox name="moisture_eliminator" label="Moisture Eliminator" />}
            {mfgId === 79 && <Checkbox name="label_kit" label="Label Kit" />}
            <Checkbox name="nitrogen_charge" label="Nitrogen Charge" />
          </div>
        </div>

        {/* ----- Mounting (coil.selector.tmpl:1296-1362) ----- */}
        <div className={tabClass('Mounting')} id={`${idPrefix}${PANE_ID.Mounting}`}>
          {/* tmpl:1297 */}
          <div className="col-xs-12 col-md-12 col-lg-6 extra-padding-right float-left">
            <Num name="h_from_floor" label="Height from Floor" unit="in" />
            {/* Mounting Angle carries an info icon whose hover tooltip explains
                the sign convention (tmpl:1316-1341). Pure CSS — common.css
                :19559-19593 shows `.mounting_angle_tooltip_text` on hover of
                `.rating_mounting_angle_tooltip` — so the markup is the whole
                port. The `deg` unit was missing too. */}
            <SelectRow
              label={
                <>
                  Mounting Angle{' '}
                  <span className="rating_mounting_angle_tooltip">
                    <i className="icon-info" />
                    <span className="mounting_angle_tooltip_text hide">
                      <span>-90 degrees, layed down horizontally, opposite to airflow</span>
                      <span>0 degree, vertical</span>
                      <span>+90 degrees, layed down horizontally, in direction of airflow</span>
                    </span>
                  </span>
                </>
              }
              dataName="mounting_angle" unit="deg">
              {/* INTEGER, not decimal. Legacy picks the display format off
                  `data-step` alone — `const step = input.data("step"); var
                  format = step == 0.01 ? "decimal" : "integer"`
                  (coil.selector.js:4265-4266) — and this input carries a plain
                  `step="1"` with NO `data-step` (tmpl:1333-1336), so dev renders
                  `0`. The column is `integer` too (ksdm_ahu_component_coil
                  .mounting_angle). `bounds.step` makes coilValidateNumber round
                  on commit, so a typed `1.5` can no longer be stored. */}
              <NumControl name="mounting_angle" formatter="integer" bounds={{ step: 1 }} />
            </SelectRow>
            {/* 5e-sync (KA-5259): Available Airway W x H — readonly section AW,
                live-updated on cabinet-field changes via the section refetch
                listeners above. Third and last row of the Mounting tab, as on
                dev (coil.selector.tmpl:1326-1342). */}
            <SelectRow label="Available Airway W x H" dataName="aw_width" unit="in">
              <div className="row display-flex-row">
                <input name="aw_width" value={awInputVal(sectionAw?.aw_width)}
                  readOnly disabled tabIndex={-1} onChange={() => undefined} />
                <span className="unit">X</span>
                <input name="aw_height" value={awInputVal(sectionAw?.aw_height)}
                  readOnly disabled tabIndex={-1} onChange={() => undefined} />
              </div>
            </SelectRow>
          </div>
        </div>

        {/* ----- Pricing (coil.selector.tmpl:1752-1800) ----- */}
        <div className={tabClass('Pricing')} id={`${idPrefix}Coil_Pricing_Coil`}>
          {/* tmpl:1753 */}
          <div className="col-xs-12 col-md-12 col-lg-6 extra-padding-right float-left">
            {/* Method — iterates the real pricing_methods collection (not the old
                hardcoded List/User pair). */}
            <CoilSelect name="pricing_method_id" label="Method" cls="model_field coil_field pricing_method_field" />
            {/* pricing_method != 1 → the cost/net-price pair. == 2 → user COST;
                else (3, "by user price") → NET PRICE (tmpl:1774-1798).
                ONE row, two `col-xs-6` inputs under a single "Coil : Mtl |
                Freight" label (tmpl:1774-1796) — React had split it into two
                full-width rows. */}
            {s.pricing_method_id != null && Number(s.pricing_method_id) !== 1 && (
              <PairRow label="Coil : Mtl | Freight" unit="US$"
                dataName={Number(s.pricing_method_id) === 2 ? 'cost_product_user' : 'net_price_product_user'}
                left={<NumControl style={{ width: '100%' }}
                  name={Number(s.pricing_method_id) === 2 ? 'cost_product_user' : 'net_price_product_user'} />}
                right={<NumControl style={{ width: '100%' }}
                  name={Number(s.pricing_method_id) === 2 ? 'cost_freight_user' : 'net_price_freight_user'} />} />
            )}
          </div>
        </div>
        </div>{/* /.row — the tab-pane wrapper opened above (tmpl:43-1801) */}
      </div>
    </div>

      {/* ===== OUTPUT (coil.rated.outputs.tmpl — Coil / Performance / Notes) ===== */}
      {/* the legacy output container (coil.selector.tmpl:1804). The height
          rules are keyed on this exact class combination —
          `.output-group.output-content.coil_new` and its `.coil_coil_new`
          refinement (common.css:15020-15027) — so the React class list
          (`output-part coil col-xs-12 rating`) left the panel unsized.
          `rating` is conditional on a rated coil, as in the template. */}
      <div className={`coil_coil_new coil_new output-part rated-item output-group output-content${ratingClass}`}>
        {ratedErrored && (
          <div className="col-xs-11 with-border output-with-border tab-content full-height"
            style={{ float: 'right', minHeight: 255 }}>
            {/* tmpl:20-28 — an errored or 'None' model collapses the whole sheet. */}
            <div className="error-message">
              Unable to find a coil that satisfies all inputted parameters.
            </div>
          </div>
        )}

        {showSheet && (
          <>
            {/* output nav (tmpl:1-18) — rendered above the collapse branch in
                dev, so it is present for a 0-model result too. `warningsOnly`
                is render_warnings' state (js:1984-2015): every tab frozen,
                Notes selected. */}
            <div className="col-xs-1 nav nav-tabs">
              <ul className="ouptut_tubs coil-raiting">
                <li className="cursor-default">Output</li>
                {OUTPUT_TABS.map(({ tab, label, id, nav }) => (
                  <li key={tab} className={`${idPrefix}${nav}${
                    warningsOnly
                      ? ` non-clickable${tab === 'notes' ? ' selected active' : ''}`
                      : outputTab === tab ? ' selected' : ''}`}>
                    <a href={`#${idPrefix}${id}`}
                      onClick={(e) => { e.preventDefault(); if (!warningsOnly) setOutputTab(tab); }}>
                      {label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div className="col-xs-11 with-border output-with-border tab-content full-height"
              style={{ float: 'right', minHeight: 255 }}>
              <div className="with-bottom-distance row coil-summary-layout full-height">
                <div className="col-xs-12 content-data pb-0">
                  <div className="row coil-summary-content full-height">
                    {/* legacy renders this counts strip EMPTY (tmpl:33) */}
                    <div className="coil-counts coil_items_count coil-summary-counts col-xs-12 gap-5" />

                    {/* ---------- Coil ---------- */}
                    <div id={`${idPrefix}Coil_output`} name={`${idPrefix}Coil_output`}
                      className={`tab-pane fade scrollable-content col-xs-12 float-left coil-summary-pane full-height${effOutputTab === 'coil' ? ' in active' : ''}`}>
                      <div className="coil-summary-table-wrap">
                        <table className="coil-summary-table">
                          <thead>
                            <tr>
                              <th>MODEL</th>
                              <th>RPI</th>
                              <th className="coil-summary-right">
                                REQUIRED AW W<span style={{ textTransform: 'lowercase' }}> x </span>H{' '}
                                <span style={{ textTransform: 'lowercase' }}>(in)</span>
                              </th>
                              <th>ROWS</th>
                              <th>FIN SPACING</th>
                              <th>SENSIBLE/TOTAL (MBH)</th>
                              <th>{heating ? 'LADB' : 'LADB/LAWB'} (&deg;F)</th>
                              <th className="coil-summary-right">FV (FPM)</th>
                              <th className="coil-summary-right">APD (iWC)</th>
                              {coilType === 'fluid' && <th className="coil-summary-right">FPD (FWC)</th>}
                            </tr>
                          </thead>
                          <tbody>
                            {summaryRows.map((r) => (
                              <tr key={r.idx} data-idx={r.idx}
                                className={`coil-summary-row${r.active ? ' active' : ''}`}
                                onClick={() => changeCoil(r.idx)}>
                                <td>{r.model}</td>
                                <td>{r.rpi}</td>
                                <td className="coil-summary-right">
                                  <span className={`sizes_aw_width${r.aw_width_warning ? ' warning' : ''}`}
                                    style={r.aw_width_warning ? AW_WARNING : undefined}>{r.aw_width}</span>
                                  {' x '}
                                  <span className={`sizes_aw_height${r.aw_height_warning ? ' warning' : ''}`}
                                    style={r.aw_height_warning ? AW_WARNING : undefined}>{r.aw_height}</span>
                                </td>
                                <td>{String(r.rows)}</td>
                                <td>{r.fpi}</td>
                                <td>{r.capacity_sensible_mbh}/{r.capacity_total_mbh}</td>
                                <td>{r.ladb_temp}{!heating && `/${r.lawb_temp}`}</td>
                                <td className="coil-summary-right">{r.face_velocity}</td>
                                <td className="coil-summary-right">{r.apd}</td>
                                {coilType === 'fluid' && <td className="coil-summary-right">{r.fluid_pd}</td>}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* ---------- Performance ---------- */}
                    <div id={`${idPrefix}Properties-Performance_output`}
                      name={`${idPrefix}Properties-Performance_output`}
                      className={`tab-pane fade scrollable-content col-xs-12 float-left${effOutputTab === 'performance' ? ' in active' : ''}`}>
                      <div className="col-xs-12">
                        <div className="col-xs-3 col-xs-offset-9 sub-titles per-coil-text with-mb">
                          <div className="row">
                            <div className="col-xs-offset-5 col-xs-4 text-right negative-margin">
                              <div className="row">Per Coil</div>
                            </div>
                            <div className="col-xs-3 text-right">
                              <div className="row">All Coils</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* left column (tmpl:98-422) */}
                      <div className="col-xs-12 col-md-12 col-lg-6">
                        <OutRow label="Model" darker>{perf.model}</OutRow>
                        <OutRow label="Rows">{formatIntegerDisplay(perf.rows)}</OutRow>
                        <OutRow label="Fin Spacing" unit="fpi">{formatDecimalDisplay(perf.fpi)}</OutRow>
                        <OutRow label="Circuiting | Feeds">
                          <span className="long-input" style={{ textTransform: 'capitalize' }}
                            data-name="circuiting_rating_output">
                            {perf.circuiting ?? perf.circuiting_rating_output ?? ' --'}
                          </span>
                          {/* tmpl:157-182 — RAE (27) shows the params feeds, every
                              other mfg the rated `feeds` (or params for 181). */}
                          {mfgId === 27
                            ? (s.rae_feeds_id != null || s.feeds_id != null) && (
                                <span className="long-input number_output" data-name="feeds_rating_output">
                                  {formatIntegerDisplay(s.rae_feeds_id ?? feedsValue)}
                                </span>
                              )
                            : perf.feeds != null
                              ? <> | <span className="long-input number_output" data-name="feeds">
                                  {formatIntegerDisplay(perf.feeds)}</span></>
                              : mfgId === 181 && s.feeds != null
                                ? <> | <span className="long-input number_output" data-name="feeds">
                                    {formatIntegerDisplay(s.feeds)}</span></>
                                : null}
                        </OutRow>
                        {isEvaporator && (
                          <OutRow label="Distributor Connection" unit="in">
                            {String(perf.distributor_1_dia) !== 'None' && perf.distributor_1_dia != null && (
                              <span className="long-input">{perf.distributor_1_dia}</span>
                            )}
                            {perf.distributor_2_dia != null && String(perf.distributor_2_dia) !== '0.000' && (
                              <><span className="unit not-uom">|</span>
                                <span className="long-input">{perf.distributor_2_dia}</span></>
                            )}
                          </OutRow>
                        )}
                        {/* tmpl:214-263 — mfg 21 evaporator: both rows render their
                            values COMMENTED OUT in the legacy template, so the pair
                            is two labels with empty value cells. Reproduced. */}
                        {mfgId === 21 && isEvaporator && (
                          <>
                            <OutRow label="Suction Connection | Header" unit="in" />
                            <OutRow label="Discharge Connection | Header" unit="in" />
                          </>
                        )}
                        {!isEvaporator && (
                          <OutRow label="Outlet Connection | Header" unit="in">
                            {String(perf.outlet_conn_dia) !== '0.000' && (
                              <>
                                <span className="long-input" data-name="outlet_conn_dia">
                                  {formatDecimalDisplay(perf.outlet_conn_dia)}
                                </span>
                                <span> | </span>
                              </>
                            )}
                            {String(perf.outlet_header_dia) !== '0.000' && (
                              <span className="long-input" data-name="outlet_header_dia">
                                {formatDecimalDisplay(perf.outlet_header_dia)}
                              </span>
                            )}
                          </OutRow>
                        )}
                        <OutRow label="Inlet Connection | Header" unit="in">
                          <span data-name="inlet_conn_dia">{formatDecimalDisplay(perf.inlet_conn_dia)}</span>
                          {perf.inlet_conn_dia ? <span> | </span> : null}
                          <span data-name="inlet_header_dia">
                            {Number(perf.inlet_header_dia) > 0 ? formatDecimalDisplay(perf.inlet_header_dia) : '0.00'}
                          </span>
                        </OutRow>
                        <OutRow label="Weight Dry / Wet (per coil)" unit="lb">
                          <span className="number_output" data-name="shipping_weight">
                            {toInt(perf.shipping_weight)}
                          </span>
                          {perf.operating_weight ? ' / ' : ''}
                          <span className="number_output" data-name="operating_weight">
                            {toInt(perf.operating_weight)}
                          </span>
                        </OutRow>
                      </div>

                      {/* right column (tmpl:423-1048) */}
                      <div className="col-xs-12 col-md-12 col-lg-6 performance-group">
                        <PerfPair label="Face Area" unit={<>ft<sup>2</sup> </>}
                          per={<span className="number_output" data-name="face_area">
                            {formatDecimalDisplay(perf.face_area)}</span>}
                          all={<span className="number_output" data-name="face_area_total">
                            {formatDecimalDisplay(perf.face_area_total)}</span>} />

                        <PerfRow label="Face Velocity / APD" unit="fpm | in WC ">
                          {/* tmpl:471-475 — RAE(27)/181/190 all read the form's
                              actual FV, everyone else the rated std. mfg 190's
                              template branch is `{{#if model_coil.face_velocity_actual}}
                              …{{else}}{{faceVelocityActual}}{{/if}}`, but
                              model_coil IS self.model_data.model and
                              faceVelocityActual IS model_data.model.face_velocity_actual
                              (js:1568/:1586 — the SAME field both times), so the
                              `{{else}}` never differs from the `{{#if}}` branch and
                              there is no rated-coil fallback here at all — just s. */}
                          <span className="number_output" data-name="face_velocity_std">
                            {formatIntegerDisplay(
                              mfgId === 27 || mfgId === 181 || mfgId === 190
                                ? s.face_velocity_actual
                                : perf.face_velocity_std
                            )}
                          </span>
                          {perf.apd ? (
                            <> | <span className="number_output" data-name="apd">
                              {formatDecimalDisplay(perf.apd)}</span></>
                          ) : null}
                        </PerfRow>

                        <PerfRow
                          label={<>LADB{coilType !== 'steam' && duty === 'cooling' ? ' / LAWB' : ''}</>}
                          unit="F">
                          <span className="number_output" data-name="ladb">
                            {formatDecimalDisplay(
                              isExternal ? perf.ladb_temp : (s.desired_ladb_temp ?? perf.desired_ladb_temp)
                            )}
                          </span>
                          {coilType !== 'steam' && duty === 'cooling' && (
                            <> / <span className="number_output">
                              {formatDecimalDisplay(
                                isExternal ? perf.lawb_temp : (s.desired_lawb_temp ?? perf.desired_lawb_temp)
                              )}
                            </span></>
                          )}
                        </PerfRow>

                        {/* Capacity Sensible — the external branch is cooling-only
                            and non-steam (tmpl:609-648); note the non-external "All
                            Coils" cell reads capacity_total, the legacy per/all twin
                            of capacity_sensible (js:2486-2488). */}
                        {(!isExternal || (coilType !== 'steam' && duty === 'cooling')) && (
                          <PerfPair label="Capacity Sensible" unit="btu/hr"
                            per={<span className="medium-input number_format" data-name="capacity_sensible">
                              {formatIntegerDisplay(perf.capacity_sensible)}</span>}
                            all={<span className="long-input number_format" data-name="capacity_sensible_total">
                              {formatIntegerDisplay(isExternal ? perf.capacity_sensible_total_int : perf.capacity_total)}
                            </span>} />
                        )}

                        <PerfPair label="Capacity Total" unit="btu/hr"
                          per={<span className="medium-input number_format" data-name={isExternal ? 'capacity_total' : 'desired_capacity_total'}>
                            {formatIntegerDisplay(isExternal ? perf.capacity_total : (s.desired_capacity_total ?? perf.desired_capacity_total))}
                          </span>}
                          all={<span className="long-input number_format" data-name={isExternal ? 'capacity_total_total' : 'desired_capacity_total_total'}>
                            {formatIntegerDisplay(isExternal ? perf.capacity_total_total : (s.desired_capacity_total_total ?? perf.desired_capacity_total_total))}
                          </span>} />

                        {coilType === 'steam' && (
                          <PerfPair label="Condensate load/circuit" unit="lb/hr"
                            per={<span className="medium-input number_output" data-name="condensate_load_per_circuit">
                              {formatIntegerDisplay(perf.condensate_load_per_circuit)}</span>}
                            all={<span className="long-input number_output" data-name="condensate_load_per_circuit_all">
                              {formatIntegerDisplay(perf.condensate_load_per_circuit_all)}</span>} />
                        )}

                        {coilType === 'fluid' && (
                          <>
                            <PerfPair label="Fluid Flow" unit="gpm"
                              per={<span className="number_output" data-name="fluid_flow">
                                {formatDecimalDisplay(perf.fluid_flow)}</span>}
                              all={<span className="number_output" data-name="fluid_flow_total">
                                {formatDecimalDisplay(perf.fluid_flow_total)}</span>} />
                            <PerfRow label="Leaving Fluid Temp" unit="F">
                              <span className="number_output" data-name="leaving_fluid_temp">
                                {formatDecimalDisplay(perf.leaving_fluid_temp)}</span>
                            </PerfRow>
                            <PerfRow label="Fluid Velocity | FPD" unit="fps | ft WC">
                              <span>{perf.fluid_velocity}</span> |{' '}
                              <span>{perf.fluid_pd}</span>
                            </PerfRow>
                            {mfgId === 79 && (
                              <PerfPair label="Volume" unit={<>in<sup>3</sup></>}
                                per={<span className="number_output" data-name="fluid_internal_volume">
                                  {formatDecimalDisplay(perf.fluid_internal_volume)}</span>}
                                all={<span className="number_output" data-name="fluid_internal_volume">
                                  {formatDecimalDisplay(perf.volume_total)}</span>} />
                            )}
                            {/* tmpl:852-869 — Reynold's row exists but carries the
                                legacy `hide` class; kept hidden, not dropped. */}
                            {mfgId === 27 && (
                              <PerfRow label="Reynold's Number" rowClass="hide">
                                <span data-name="reynolds_number">{perf.reynolds_number}</span>
                              </PerfRow>
                            )}
                          </>
                        )}

                        {isEvaporator && (
                          <>
                            <PerfRow label="Tube Vapor Velocity" unit="fps ">
                              {[21, 190, 79, 143].includes(Number(mfgId)) && (
                                <span className="number_output" data-name="tube_vapor_velocity">
                                  {formatDecimalDisplay(perf.tube_vapor_velocity)}</span>
                              )}
                            </PerfRow>
                            <PerfRow label="Fluid PD" unit="PSI">
                              {perf.fluid_pd ? (
                                <span className="number_output" data-name="fluid_pd">
                                  {formatDecimalDisplay(perf.fluid_pd)}</span>
                              ) : null}
                            </PerfRow>
                            {mfgId === 21 && (
                              <PerfRow label="Leaving Superheat" unit="F">
                                <span className="number_output">{formatDecimalDisplay(perf.leaving_superheat)}</span>
                              </PerfRow>
                            )}
                            {mfgId === 79 && (
                              <>
                                <PerfRow label="Fluid Flow" unit="lbm/hr">
                                  <span data-name="fluid_flow">{formatDecimalDisplay(perf.fluid_flow)}</span>
                                </PerfRow>
                                <PerfPair label="Volume" unit={<>in<sup>3</sup></>}
                                  per={<span className="number_output" data-name="fluid_internal_volume">
                                    {formatDecimalDisplay(perf.fluid_internal_volume)}</span>}
                                  all={<span className="number_output" data-name="fluid_internal_volume">
                                    {formatDecimalDisplay(perf.volume_total)}</span>} />
                              </>
                            )}
                            {!isExternal && (
                              <PerfRow label="Leaving Superheat" unit="F">
                                <span className="medium-input" data-name="capacity_total">
                                  {formatIntegerDisplay(perf.leaving_superheat)}</span>
                              </PerfRow>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* ---------- Notes ---------- */}
                    <div id={`${idPrefix}Notes-output`} name={`${idPrefix}Notes-output`}
                      className={`tab-pane fade scrollable-content col-xs-12 float-left full-height${effOutputTab === 'notes' ? ' in active' : ''}`}>
                      <div className="col-xs-12 col-md-12 full-height">
                        <div className="row">
                          <div className="col-xs-12">
                            <div className="clearfix row nots_and_errors">
                              {/* a 0-model rate with no vendor reason (e.g. every model was
                                  filtered out by the max pressure-drop limits) would otherwise
                                  leave the pane blank. */}
                              {warningsOnly && !rateErrors.length && !rateWarnings.length && (
                                <>
                                  <div className="col-xs-1">
                                    <p className="warning-notes">Error :</p>
                                  </div>
                                  <div className="col-xs-11">
                                    <p className="warning-msg">
                                      Unable to find a coil that satisfies all inputted parameters.
                                    </p>
                                  </div>
                                </>
                              )}
                              <NotesGroup title="Error :" titleClass="warning-notes"
                                items={rateErrors} itemClass="warning-msg" block />
                              <NotesGroup title="Warning :" titleClass="warning-notes"
                                items={rateWarnings} itemClass="warning-msg" block />
                              <NotesGroup title="Mfg's Notes :" titleClass="label"
                                items={(coilNotes ?? []).map((n: any) => String(n.note ?? n.text ?? n.name ?? '')).filter(Boolean)}
                                itemClass="label" block />
                              <NotesGroup title="Rating Notes :" titleClass="label"
                                items={ratedNotes} itemClass="label" />
                              <NotesGroup title="Rating Notes :" titleClass="label"
                                items={apiNotes} itemClass="label" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* tmpl:3103-3107 — hidden WHILE A RATE IS IN FLIGHT. The line
                lives at the tail of coil.rated.outputs.tmpl, i.e. inside
                `.output-part.rated-item`, and legacy replaces that container's
                entire HTML with the preloader for the duration of the request
                (coil.selector.js:3196-3199) — so on dev the timestamp goes away
                on the Rate click and comes back with the new outputs. React
                renders the pane from state, so it survived the click and sat
                there showing a time while the results were still loading. */}
            {!rateInFlight && (
              <div className="col-xs-11 col-xs-offset-1">
                <div className="row rating-time">Rated on : {ratedDatetime.current ?? ''}</div>
              </div>
            )}
          </>
        )}

        {/* ★ NO Draw button. Neither coil template renders one — there is no
            `name="draw"`, `btn-add` or `coil-draw-trigger` anywhere in
            coil.selector.tmpl or coil.rated.outputs.tmpl. (The legacy
            `Drawing-output` PANE exists at outputs tmpl:1050 but the output nav
            only lists Coil / Performance / Notes, so nothing can reach it.)
            It was also adding its own height inside a fixed-height panel,
            pushing the `Rated on` line out of view. */}
        {/* legacy's output-pane spinner is the one `rate()` writes into
            `.rated-item` (coil.selector.js:3199) and removes on the response
            (:3207) — `preloader dotted-preloader active asd`. It is fired from
            `rate()`, which BOTH the vendor and the manual/DB routes funnel
            through, so gate it on `rateInFlight` (= `isRating || isDbRating`),
            not on the vendor mutation alone: a manual-manufacturer coil rated
            with no spinner at all. `asd` is carried so the legacy
            `.asd.preloader` selector keeps matching. */}
        {rateInFlight && (
          <div id="coil-selector-preloader" className="preloader dotted-preloader active asd" />
        )}
      </div>

      {/* ---- the PRINT SHEET (coil.selector.print.tmpl) ----
          Legacy: `$('body').append('<div class="print-content"></div>')`, fill
          it, print, `.remove()` (coil.selector.js:4094/:4196/:4206). The div
          MUST be a direct child of <body> and MUST keep a class containing
          "print": the app's print stylesheet hides `body > *:not([class*=print])`
          outright. `.print-content` is `display:none` on screen, so this costs
          nothing until the user prints. */}
      {printing &&
        createPortal(
          <div className="print-content">
            <CoilPrintSheet
              reportColor={reportColor}
              reportCo={reportCo}
              reportLogo={reportLogo}
              year={new Date().getFullYear()}
              projectName={projectName}
              projectNumber={projectNumber}
              elevation={projectElevation}
              ahuTag={ahuTag}
              sectionTag={(activate.section as { tag?: string } | undefined)?.tag}
              ratingDatetime={ratedDatetime.current}
              headerMode={headerMode}
              coilType={coilType}
              duty={duty}
              hand={printHand}
              style={printStyle}
              s={s}
              perf={perf}
              masterLists={masterLists}
              typeRows={types}
              dutyRows={dutyRows}
              htfluidRows={htfluids}
              mfgName={printMfgName}
              casingLength={printCasingLength}
              circuiting={printCircuiting}
              faceVelocityActual={s.face_velocity_actual}
              warnings={rateWarnings}
              cmsNotes={(coilNotes ?? [])
                .map((n: any) => String(n.note ?? n.text ?? n.name ?? ''))
                .filter(Boolean)}
              apiNotes={apiNotes}
              ratedNotes={ratedNotes}
            />
          </div>,
          document.body
        )}
    </CoilFieldCtx.Provider>
  );
};


// ---------------------------------------------------------------------------
// rated-output sheet helpers (coil.rated.outputs.tmpl)
// ---------------------------------------------------------------------------

/** the legacy AW-over-section warning colour. The template's `.warning` rule is
 * scoped to `.coil-summary-table` (rating_mode.scss:4924) and IS shipped in
 * dev3's common.css; the inline colour guarantees the cue either way. */
const AW_WARNING: React.CSSProperties = { color: '#dc362e' };

/** Handlebars `toInt` (coil.selector.js:364-368) — strip non-numeric, round,
 * NaN → 0. Used by the Weight Dry / Wet cells. */
const toInt = (value: unknown): number => {
  const n = Number(String(value).replace(/[^0-9.-]/g, ''));
  return isNaN(n) ? 0 : Math.round(n);
};

/** the unit suffix hung off a label. A °F unit additionally carries `unit
 * temp` in legacy — `.coil_new .temp:before { content: "o" }`
 * (common.css:15763) is what draws the little degree ring before the F, so a
 * plain `percent right` span renders a bare "F". Legacy tags EVERY °F unit
 * this way, input pane and rated outputs alike (coil.selector.tmpl:614/:641/
 * :714/:758/:775/:792/:809/:845/:858/:890/:912/:927, coil.rated.outputs
 * .tmpl:500/:543/:785/:948/:1028); only EADB/LADB had it here. Non-`F` units
 * keep the bare class list, as in the template. */
const UnitSpan = ({ unit }: { unit: React.ReactNode }) => (
  <span className={unit === 'F' ? 'percent right unit temp' : 'percent right'}>{unit}</span>
);

/** left-column output row (tmpl:99-110 — col-xs-6 label / col-xs-6 value). */
const OutRow = ({ label, unit, darker, children }: {
  label: React.ReactNode; unit?: string; darker?: boolean; children?: React.ReactNode;
}) => (
  <div className={`row${darker ? ' darker-row' : ''}`}>
    <div className="col-xs-6 label-part">
      {label}
      {unit && <UnitSpan unit={unit} />}
    </div>
    <div className="col-xs-6 input-part">
      <div className="row">
        <div className="col-xs-12 output-data">{children}</div>
      </div>
    </div>
  </div>
);

/** right-column single-value row (tmpl:456-489 — col-xs-5 label / col-xs-7). */
const PerfRow = ({ label, unit, rowClass, children }: {
  label: React.ReactNode; unit?: React.ReactNode; rowClass?: string; children?: React.ReactNode;
}) => (
  <div className={`row${rowClass ? ` ${rowClass}` : ''}`}>
    <div className="col-xs-5 label-part">
      <span>
        {label}
        {unit && <UnitSpan unit={unit} />}
      </span>
    </div>
    <div className="col-xs-7 input-part">
      <div className="row">
        <div className="output-data">
          <div className="col-xs-6 text-right">{children}</div>
        </div>
      </div>
    </div>
  </div>
);

/** right-column "Per Coil | All Coils" pair (tmpl:424-455). */
const PerfPair = ({ label, unit, per, all }: {
  label: React.ReactNode; unit?: React.ReactNode; per: React.ReactNode; all: React.ReactNode;
}) => (
  <div className="row">
    <div className="col-xs-5 label-part">
      <span>
        {label}
        {unit && <UnitSpan unit={unit} />}
      </span>
    </div>
    <div className="col-xs-7 input-part">
      <div className="row">
        <div className="output-data">
          <div className="col-xs-3" />
          <div className="col-xs-3" />
          <div className="col-xs-3 text-right">{per}</div>
          <div className="col-xs-3 right text-right">{all}</div>
        </div>
      </div>
    </div>
  </div>
);

/** a Notes-tab group (tmpl:3024-3074) — a col-xs-1 caption + col-xs-11 body.
 * `block` renders each item as <p> (errors / warnings / mfg notes); otherwise
 * the legacy inline <span> (the two Rating Notes groups). */
const NotesGroup = ({ title, titleClass, items, itemClass, block }: {
  title: string; titleClass: string; items: string[]; itemClass: string; block?: boolean;
}) => {
  if (!items.length) return null;
  return (
    <>
      <div className="col-xs-1">
        <p className={titleClass}>{title}</p>
      </div>
      <div className="col-xs-11">
        {items.map((t, i) =>
          block
            ? <p key={i} className={itemClass}>{t}</p>
            : <span key={i} className={itemClass} style={{ whiteSpace: 'normal' }}>{t}</span>
        )}
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// small presentational helpers (matches the PHX/EW SelectRow)
// ---------------------------------------------------------------------------

/** the Handlebars get_one_decimal semantics for the Available-AW inputs
 * (coil.selector.tmpl:1334/:1337 — truthy numeric → toFixed(2), else ''). */
const awInputVal = (v: unknown): string => {
  const n = Number(v);
  return n ? n.toFixed(2) : '';
};

const SelectRow = ({ label, dataName, unit, required, width = 'col-xs-12', labelClass, children }: {
  label: React.ReactNode; dataName: string; unit?: string; required?: boolean;
  /** the legacy wrapper inside `.input-part > .row` — most rows are col-xs-12
   * but many are col-xs-6 / col-xs-3, which is what sizes the control. */
  width?: string;
  /** extra class on the label span — legacy greys the LABEL of a non-editable
   * row as well as its input (`<span class="non-clickable">Altitude</span>`,
   * coil.selector.tmpl:743). `.non-clickable` is `opacity: .6`
   * (ahu/styles.css). */
  labelClass?: string;
  children: React.ReactNode;
}) => (
  <div className="row">
    <div className="col-xs-5 label-part">
      <span
        data-name={dataName}
        className={[required ? 'required' : null, labelClass].filter(Boolean).join(' ') || undefined}
      >
        {label}
      </span>
      {unit && <UnitSpan unit={unit} />}
    </div>
    {/* legacy nests the control in `.input-part > .row > .col-xs-12` (tmpl:50-52
        and every plain row after it). It is padding-neutral by design —
        `.coil_new .content-data .input-part > .row div { padding-left: 0 }`
        (common.css:16027) exists precisely to zero the grid gutter — so this
        only lands correctly now that the root carries `coil_new`. */}
    <div className="col-xs-7 input-part">
      <div className="row">
        <div className={width}>{children}</div>
      </div>
    </div>
  </div>
);

/** the "Per Coil | All Coils" column captions above the Performance pane's
 * paired rows (tmpl:480-493, repeated for the right column at :495-509). */
const PerAllHeadings = () => (
  <div className="col-xs-7 col-xs-offset-5 sub-titles per-coil-text">
    <div className="row">
      <div className="col-xs-3 col-xs-offset-6 text-right negative-margin" style={{ paddingRight: 10 }}>
        <div className="row">Per Coil</div>
      </div>
      <div className="col-xs-3 text-right">
        <div className="row">All Coils</div>
      </div>
    </div>
  </div>
);

/** the legacy TWO-control row — `.input-part > .row > .col-xs-6 + .col-xs-6`
 * (FH x FL at tmpl:171-193, Circuiting | Feeds at tmpl:251-295). React had
 * split both into two separate single-control rows. */
const PairRow = ({ label, dataName, unit, leftCls, required, cell = 'col-xs-6', left, right }: {
  label: string; dataName: string; unit?: React.ReactNode; leftCls?: string; required?: boolean;
  /** legacy cell width for BOTH halves — col-xs-6 or col-xs-3. */
  cell?: string;
  left: React.ReactNode; right: React.ReactNode;
}) => (
  <div className="row">
    <div className="col-xs-5 label-part">
      <span data-name={dataName} className={required ? 'required' : undefined}>{label}</span>
      {unit && <UnitSpan unit={unit} />}
    </div>
    <div className="col-xs-7 input-part">
      <div className="row">
        <div className={`${cell}${leftCls ? ` ${leftCls}` : ''}`}>{left}</div>
        <div className={cell}>{right}</div>
      </div>
    </div>
  </div>
);

export default CoilSelector;
